// src/money/money.ts
var toCents = (value) => {
  return Math.round(value * 100);
};
var fromCents = (cents) => {
  return cents / 100;
};
var roundDiv = (numerator, denominator) => {
  if (denominator === 0) {
    throw new Error("Division by zero");
  }
  const quotient = Math.trunc(numerator / denominator);
  const remainder = numerator % denominator;
  const absRemainder = Math.abs(remainder);
  const absDenominator = Math.abs(denominator);
  if (absRemainder * 2 > absDenominator) {
    return quotient + Math.sign(numerator) * Math.sign(denominator);
  }
  if (absRemainder * 2 === absDenominator && numerator * denominator > 0) {
    return quotient + 1;
  }
  return quotient;
};
var mulBps = (amountCents, bps) => {
  return roundDiv(amountCents * bps, 1e4);
};
var clampMin0 = (value) => {
  return Math.max(0, value);
};
var allocateCents = (totalCents, qty) => {
  const qtyInt = Math.trunc(qty);
  if (!Number.isFinite(qtyInt) || qtyInt <= 0) {
    return [];
  }
  const base = Math.floor(totalCents / qtyInt);
  const remainder = totalCents - base * qtyInt;
  const allocations = new Array(qtyInt).fill(base);
  for (let i = 0; i < remainder; i += 1) {
    allocations[i] += 1;
  }
  return allocations;
};
var percentToBps = (percent) => {
  return Math.round(percent * 100);
};
var percentToBpsOrZero = (percent) => {
  if (percent === null || percent === void 0) {
    return 0;
  }
  return percentToBps(percent);
};
var bpsToPercent = (bps) => {
  return bps / 100;
};
var grossToNetCents = (grossCents, taxRateBps) => {
  return roundDiv(grossCents * 1e4, 1e4 + taxRateBps);
};

// src/money/display.ts
function formatMoneyString(amount2, decimalCount = 2, decimal = ".", thousands = ",", currencySymbol = "$") {
  const parsedAmount = amount2 === "" || amount2 == null ? 0 : Number(amount2);
  const numericAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
  const safeDecimalCount = Number.isFinite(decimalCount) ? Math.max(0, Math.trunc(decimalCount)) : 2;
  const negativeSign = numericAmount < 0 ? "-" : "";
  const absoluteAmount = Math.abs(numericAmount);
  const fixedAmount = absoluteAmount.toFixed(safeDecimalCount);
  const [integerPart, decimalPart] = fixedAmount.split(".");
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  const decimalSuffix = safeDecimalCount > 0 ? `${decimal}${decimalPart || ""}` : "";
  return `${negativeSign}${currencySymbol}${groupedInteger}${decimalSuffix}`;
}

// src/money/order.ts
var isApprovedPaymentLine = (paymentLine) => {
  if (!paymentLine) return false;
  return String(paymentLine.status || "approved").toLowerCase() === "approved";
};
var getOrderAmountDue = (order) => {
  const totalCents = toCents(order?.total || 0);
  const paidCents = (order?.paymentLines ?? []).reduce((prev, next) => {
    if (!next) return prev;
    return prev + toCents(next.amountPaid || 0);
  }, 0) || 0;
  const amountDueCents = clampMin0(totalCents - paidCents);
  return fromCents(amountDueCents);
};

// src/pricing/billableQuantity.ts
var normalizeMinimumBillableQuantity = (value, type) => {
  if (type !== "weight" && type !== "length") {
    return 0;
  }
  if (value == null || value === "") {
    return 0;
  }
  const minimum = Number(value);
  if (!Number.isFinite(minimum) || minimum < 0) {
    throw new RangeError(
      "minimumBillableQuantity must be a finite non-negative number"
    );
  }
  return minimum;
};
var resolveBillableQuantity = (line) => {
  const actualQuantity = Number(line.qty ?? line.quantity ?? 0);
  if (!Number.isFinite(actualQuantity) || actualQuantity <= 0) {
    return 0;
  }
  const isMeasured = line.type === "weight" || line.type === "length";
  const isPromotional = Boolean(line.isBuyAndGetProduct || line.isFreeItem);
  if (!isMeasured || isPromotional) {
    return actualQuantity;
  }
  const minimum = Number(line.minimumBillableQuantity ?? 0);
  if (!Number.isFinite(minimum) || minimum <= 0) {
    return actualQuantity;
  }
  return Math.max(actualQuantity, minimum);
};

// src/pricing/buyXGetYVariants.ts
var asRecord = (value) => value !== null && typeof value === "object" ? value : null;
var normalizeId = (value) => {
  if (value === null || value === void 0) return "";
  return String(value).trim();
};
var getProductIdentity = (value) => {
  const product = asRecord(value);
  if (!product) return "";
  const directId = normalizeId(product._id ?? product.productId);
  if (directId) return directId;
  const storeProductId = product.storeProductId;
  if (typeof storeProductId === "string") return normalizeId(storeProductId);
  return normalizeId(asRecord(storeProductId)?._id);
};
var getDiscountTargetIdentity = (value) => {
  if (typeof value === "string") return normalizeId(value);
  const target = asRecord(value);
  return normalizeId(target?._id ?? target?.productId ?? target?.storeProductId);
};
var getLineQuantity = (value) => {
  const line = asRecord(value);
  const quantity = Number(line?.qty ?? line?.quantity ?? 0);
  return Number.isFinite(quantity) ? quantity : 0;
};
var normalizeBuyXGetYSelectedOptionId = (value) => {
  const lineOrCondition = asRecord(value);
  const selectedOption = asRecord(lineOrCondition?.selectedOption);
  return normalizeId(lineOrCondition?.selectedOptionId ?? selectedOption?._id);
};
var normalizeBuyXGetYVariantScope = (value) => asRecord(value)?.variantScope === "specific" ? "specific" : "all";
var matchesBuyXGetYCondition = (line, value) => {
  const condition = asRecord(value);
  const productId = getProductIdentity(line);
  const targetProductId = getDiscountTargetIdentity(condition?.buyProduct);
  if (!productId || !targetProductId || productId !== targetProductId) return false;
  if (normalizeBuyXGetYVariantScope(condition) === "all") return true;
  const conditionOptionId = normalizeBuyXGetYSelectedOptionId(condition);
  return Boolean(
    conditionOptionId && normalizeBuyXGetYSelectedOptionId(line) === conditionOptionId
  );
};
var getBuyXGetYQuantityForCondition = (lines = [], condition) => lines.reduce(
  (quantity, line) => matchesBuyXGetYCondition(line, condition) ? quantity + getLineQuantity(line) : quantity,
  0
);
var getBuyXGetYFulfillmentCount = (buyConditions = [], lines = []) => {
  if (!Array.isArray(buyConditions) || buyConditions.length === 0) return 0;
  const fulfillments = buyConditions.map((value) => {
    const condition = asRecord(value);
    const requiredQuantity = Number(condition?.qty ?? 0);
    if (!Number.isFinite(requiredQuantity) || requiredQuantity <= 0) return 0;
    return Math.floor(
      getBuyXGetYQuantityForCondition(lines, condition) / requiredQuantity
    );
  });
  return fulfillments.length > 0 ? Math.min(...fulfillments) : 0;
};
var resolveBuyXGetYRewardSnapshot = (value, isExpress) => {
  const condition = asRecord(value);
  const getProduct = asRecord(condition?.getProduct);
  if (!getProduct) return null;
  const selectedOptionId = normalizeBuyXGetYSelectedOptionId(condition);
  if (!selectedOptionId) {
    const price3 = Number(isExpress ? getProduct.expressPrice : getProduct.price);
    return Number.isFinite(price3) ? { productSnapshot: { ...getProduct }, price: price3 } : null;
  }
  const priceOptions = Array.isArray(getProduct.priceOptions) ? getProduct.priceOptions : [];
  const option = priceOptions.map(asRecord).find(
    (candidate) => normalizeId(candidate?._id) === selectedOptionId && candidate?.isActive !== false
  );
  if (!option) return null;
  const productSnapshot = {
    ...getProduct,
    name: option.label,
    price: option.price,
    expressPrice: option.expressPrice,
    selectedOptionId,
    selectedOption: {
      _id: selectedOptionId,
      label: option.label
    }
  };
  const price2 = Number(
    isExpress ? productSnapshot.expressPrice : productSnapshot.price
  );
  return Number.isFinite(price2) ? { productSnapshot, price: price2 } : null;
};

// src/pricing/manualUnitPrice.ts
var parseManualUnitPrice = (input) => {
  if (typeof input !== "string" && typeof input !== "number") return null;
  const text3 = String(input).trim();
  if (!/^\d+(?:[.,]\d{1,2})?$/.test(text3)) return null;
  const value = Number(text3.replace(",", "."));
  return Number.isFinite(value) && value >= 0 && Number.isSafeInteger(Math.round(value * 100)) ? value : null;
};
var parseLinePriceAdjustment = (input) => {
  if (typeof input !== "string" && typeof input !== "number") return null;
  const text3 = String(input).trim();
  const magnitude = parseManualUnitPrice(text3.replace(/^[+-]/, ""));
  if (magnitude === null) return null;
  return text3.startsWith("-") && magnitude !== 0 ? -magnitude : magnitude;
};
var canAdjustOrderLinePrice = (line) => !line.returnSourceOrderLineId && !line.isBuyAndGetProduct && !line.isFreeItem;
var hasManualUnitPrice = (line) => line.manualUnitPrice != null && !line.isBuyAndGetProduct && !line.isFreeItem;
var applyManualUnitPrice = (line, isExpress = false) => {
  if (line.manualUnitPrice == null || !canAdjustOrderLinePrice(line)) return line;
  const manualUnitPrice = parseManualUnitPrice(line.manualUnitPrice);
  const catalogPrice = Number(isExpress ? line.expressPrice ?? line.price : line.price);
  if (manualUnitPrice === null || !Number.isFinite(catalogPrice) || catalogPrice < 0) {
    throw new RangeError("manualUnitPrice must be a finite non-negative monetary amount");
  }
  const billableQuantity = resolveBillableQuantity(line);
  const targetCents = Math.round(Math.round(manualUnitPrice * 100) * billableQuantity);
  const catalogCents = Math.round(Math.round(catalogPrice * 100) * billableQuantity);
  const extraCents = targetCents - catalogCents;
  if (![targetCents, catalogCents, extraCents].every(Number.isSafeInteger)) {
    throw new RangeError("manualUnitPrice line amount is too large");
  }
  const extraAmount = Number((targetCents / 100 - catalogPrice * billableQuantity).toFixed(6));
  return { ...line, manualUnitPrice, extraAmount };
};
var resolvePercentageDiscountUnitPrice = (line, isExpress = false) => hasManualUnitPrice(line) ? Number(line.manualUnitPrice) : Number(isExpress ? line.expressPrice ?? line.price : line.price);

// src/pricing/returnPricing.ts
var isNoChargeReturnLine = (order, line) => order.orderType === "return" && order.returnItemsChargeMode === "no_charge" && Boolean(line.returnSourceOrderLineId);
var isHistoricalPromotionProvenanceLine = (line) => Boolean(line.returnSourceOrderLineId);
var clearHistoricalPromotionRoleForPricing = (line) => {
  if (!isHistoricalPromotionProvenanceLine(line)) return line;
  return {
    ...line,
    isBuyAndGetProduct: false,
    isFreeItem: false,
    discountAmount: 0,
    discountAmountMode: "per_unit"
  };
};
var normalizeHistoricalPromotionProvenanceForPricing = (line, bucket) => bucket === "buyAndGetProducts" ? clearHistoricalPromotionRoleForPricing(line) : line;

// src/pricing/orderLineCharge.ts
var finiteNumber = (value) => {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};
var resolveLineDiscountTotal = (line, billableQuantity) => {
  const discountAmount = finiteNumber(line.discountAmount);
  return line.discountAmountMode === "line_total" ? discountAmount : discountAmount * billableQuantity;
};
var resolveOrderLineCharge = (line, isExpress = false) => {
  line = applyManualUnitPrice(line, isExpress);
  const rawActualQuantity = finiteNumber(line.qty ?? line.quantity);
  const actualQuantity = rawActualQuantity > 0 ? rawActualQuantity : 0;
  const billableQuantity = resolveBillableQuantity(line);
  const selectedUnitPrice = finiteNumber(
    isExpress ? line.expressPrice ?? line.price : line.price
  );
  const extraAmount = finiteNumber(line.extraAmount);
  const discountTotal = resolveLineDiscountTotal(line, billableQuantity);
  const subtotalBeforeDiscount = hasManualUnitPrice(line) ? Math.round(Math.round(Number(line.manualUnitPrice) * 100) * billableQuantity) / 100 : selectedUnitPrice * billableQuantity + extraAmount;
  const total = Math.max(subtotalBeforeDiscount - discountTotal, 0);
  return {
    actualQuantity,
    billableQuantity,
    minimumApplied: billableQuantity > actualQuantity,
    selectedUnitPrice,
    extraAmount,
    discountTotal,
    subtotalBeforeDiscount,
    total,
    effectiveUnitPrice: actualQuantity > 0 ? total / actualQuantity : 0,
    billableUnitPrice: billableQuantity > 0 ? total / billableQuantity : 0
  };
};
var resolveEffectiveOrderLineCharge = (order, line, isExpress = false) => {
  const charge = resolveOrderLineCharge(line, isExpress);
  if (!isNoChargeReturnLine(order, line)) return charge;
  return {
    ...charge,
    selectedUnitPrice: 0,
    extraAmount: 0,
    discountTotal: 0,
    subtotalBeforeDiscount: 0,
    total: 0,
    effectiveUnitPrice: 0,
    billableUnitPrice: 0
  };
};

// src/pricing/helpers.ts
var TAX_SLOTS = [
  { key: "taxOne", exemptField: "taxExemptOne" },
  { key: "taxTwo", exemptField: "taxExemptTwo" },
  { key: "taxThree", exemptField: "taxExemptThree" }
];
var isValidRate = (rate) => {
  const n = Number(rate);
  return Number.isFinite(n) && n > 0;
};
var isStoreRetentionTax = (tax) => Boolean(tax?.isRetention) || String(tax?.name ?? "").trim().toUpperCase() === "ISR";
var toTaxDefinition = (key, tax) => {
  if (!isValidRate(tax?.value)) {
    return null;
  }
  const rate = Number(tax?.value);
  const name = String(tax?.name ?? key).trim() || key;
  return {
    key,
    name,
    rate,
    bps: percentToBpsOrZero(rate),
    isRetention: Boolean(tax?.isRetention)
  };
};
var getApplicableTaxesForLine = ({
  line,
  taxOne,
  taxTwo,
  taxThree
}) => {
  const taxByKey = {
    taxOne,
    taxTwo,
    taxThree
  };
  return TAX_SLOTS.reduce((acc, slot) => {
    if (line[slot.exemptField]) {
      return acc;
    }
    const definition = toTaxDefinition(slot.key, taxByKey[slot.key]);
    if (definition && definition.bps > 0) {
      acc.push(definition);
    }
    return acc;
  }, []);
};
var getConfiguredStoreTaxes = (store) => getApplicableTaxesForLine({
  line: {},
  taxOne: store.taxOne,
  taxTwo: store.taxTwo,
  taxThree: store.taxThree
});
var getConfiguredStoreTransferTaxes = (store) => getConfiguredStoreTaxes(store).filter((tax) => !isStoreRetentionTax(tax));
var aggregateTaxBreakdown = (items) => {
  const byKey = /* @__PURE__ */ new Map();
  items.forEach((item) => {
    if (!item || item.amount <= 0) {
      return;
    }
    const existing = byKey.get(item.key);
    if (existing) {
      existing.amount += item.amount;
      return;
    }
    byKey.set(item.key, { ...item });
  });
  return TAX_SLOTS.map((slot) => byKey.get(slot.key)).filter(
    (item) => Boolean(item && item.amount > 0)
  );
};
var normalizeTaxBreakdownToTotal = (items, total) => {
  const normalizedTotal = Math.trunc(Number(total) || 0);
  if (normalizedTotal <= 0) {
    return [];
  }
  const aggregated = aggregateTaxBreakdown(items);
  if (aggregated.length === 0) {
    return [];
  }
  const sum = aggregated.reduce((acc, item) => acc + item.amount, 0);
  const diff = normalizedTotal - sum;
  if (diff !== 0) {
    aggregated[aggregated.length - 1] = {
      ...aggregated[aggregated.length - 1],
      amount: aggregated[aggregated.length - 1].amount + diff
    };
  }
  return aggregated.filter((item) => item.amount > 0);
};
var calculateLineTaxBreakdown = ({
  lineTax,
  applicableTaxes
}) => {
  if (lineTax <= 0 || applicableTaxes.length === 0) {
    return [];
  }
  const rawAmounts = allocateByWeights(lineTax, applicableTaxes.map((tax) => tax.bps));
  const items = applicableTaxes.map((tax, index) => ({
    key: tax.key,
    name: tax.name,
    rate: tax.rate,
    amount: rawAmounts[index] ?? 0
  }));
  return normalizeTaxBreakdownToTotal(items, lineTax);
};
var matchesProduct = (products, productId) => {
  if (!products || products.length === 0) {
    return false;
  }
  const target = String(productId);
  return products.some((p) => {
    if (p === null || p === void 0) {
      return false;
    }
    const id = typeof p === "string" ? p : p._id;
    return id !== void 0 && id !== null && String(id) === target;
  });
};
var getTaxRateBpsForLine = (store, line) => {
  return getApplicableTaxesForLine({
    line,
    taxOne: store.taxOne,
    taxTwo: store.taxTwo,
    taxThree: store.taxThree
  }).reduce((acc, tax) => acc + tax.bps, 0);
};
var getStoreTaxBps = (store) => {
  return getConfiguredStoreTaxes(store).reduce((acc, tax) => acc + tax.bps, 0);
};
var getStoreTransferTaxBps = (store) => {
  return getConfiguredStoreTransferTaxes(store).reduce(
    (acc, tax) => acc + tax.bps,
    0
  );
};
var allocateByWeights = (total, weights) => {
  const len = weights.length;
  if (len === 0) return [];
  if (total <= 0) return new Array(len).fill(0);
  const safeWeights = weights.map((w) => Math.max(0, Math.trunc(w)));
  const sum = safeWeights.reduce((a, b) => a + b, 0);
  if (sum <= 0) return new Array(len).fill(0);
  const raw = safeWeights.map((w) => total * w / sum);
  const floors = raw.map((x) => Math.floor(x));
  let remaining = total - floors.reduce((a, b) => a + b, 0);
  const order = raw.map((x, i) => ({ i, frac: x - Math.floor(x) })).sort((a, b) => b.frac - a.frac);
  const out = floors.slice();
  for (let k = 0; k < order.length && remaining > 0; k += 1) {
    out[order[k].i] += 1;
    remaining -= 1;
  }
  return out;
};
var allocateFixedDiscountAcrossLines = (totalCents, baseCents) => {
  const len = baseCents.length;
  if (len === 0) return [];
  const safeBases = baseCents.map((b) => Math.max(0, Math.trunc(b)));
  const sumBases = safeBases.reduce((a, b) => a + b, 0);
  if (sumBases <= 0 || totalCents <= 0) {
    return new Array(len).fill(0);
  }
  const cappedTotal = Math.min(Math.trunc(totalCents), sumBases);
  const slices = allocateByWeights(cappedTotal, safeBases);
  let overflow = 0;
  for (let i = 0; i < len; i += 1) {
    if (slices[i] > safeBases[i]) {
      overflow += slices[i] - safeBases[i];
      slices[i] = safeBases[i];
    }
  }
  if (overflow === 0) return slices;
  const headroomOrder = safeBases.map((b, i) => ({ i, headroom: b - slices[i] })).filter((entry) => entry.headroom > 0).sort((a, b) => b.headroom - a.headroom);
  for (const entry of headroomOrder) {
    if (overflow <= 0) break;
    const take = Math.min(entry.headroom, overflow);
    slices[entry.i] += take;
    overflow -= take;
  }
  return slices;
};

// src/pricing/shipping.ts
var normalizeShippingTaxPolicy = (value) => value === "included" || value === "over_price" || value === "non_taxable" ? value : "non_taxable";
var calculateShippingPricing = ({
  store,
  hasShippingCost,
  isFreeDelivery
}) => {
  if (!hasShippingCost || isFreeDelivery) {
    return {
      shippingServiceTotal: 0,
      shippingTaxesTotal: 0,
      taxBreakdown: []
    };
  }
  const shippingBase = clampMin0(
    toCents(store.orderPageConfig?.shippingServiceCost ?? 0)
  );
  const shippingPolicy = normalizeShippingTaxPolicy(
    store.orderPageConfig?.shippingTaxPolicy
  );
  if (shippingPolicy === "non_taxable") {
    return {
      shippingServiceTotal: shippingBase,
      shippingTaxesTotal: 0,
      taxBreakdown: []
    };
  }
  const taxRateBps = getStoreTransferTaxBps(store);
  const applicableTaxes = getConfiguredStoreTransferTaxes(store);
  const shippingNet = shippingPolicy === "included" ? grossToNetCents(shippingBase, taxRateBps) : shippingBase;
  const shippingTaxesTotal = shippingPolicy === "included" ? clampMin0(shippingBase - shippingNet) : mulBps(shippingNet, taxRateBps);
  const shippingServiceTotal = shippingPolicy === "included" ? shippingBase : shippingNet + shippingTaxesTotal;
  return {
    shippingServiceTotal,
    shippingTaxesTotal,
    taxBreakdown: calculateLineTaxBreakdown({
      lineTax: shippingTaxesTotal,
      applicableTaxes
    })
  };
};

// src/pricing/calculateOrderTotal.ts
var clampBps = (bps) => {
  const n = Number(bps);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.min(1e4, n));
};
var asFinite = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
var resolveLineId = (line) => {
  const explicitLineId = String(line.lineId ?? "").trim();
  if (explicitLineId.length > 0) {
    return explicitLineId;
  }
  const legacyLineId = String(line.orderProductLineID ?? "").trim();
  if (legacyLineId.length > 0) {
    return legacyLineId;
  }
  return line._id;
};
var MAX_QTY = 999999999;
var isNumberDiscountCode = (discount) => {
  return discount.type === "NUMBER" || discount.type === "number";
};
var isPercentageDiscountCode = (discount) => {
  return discount.type === "PERCENTAGE" || discount.type === "percentage";
};
var isBuyXGetYDiscountCode = (discount) => {
  return discount.type === "BUY_X_GET_Y" || discount.type === "buyXGetY";
};
var isFreeItemDiscountCode = (discount) => {
  return discount.type === "FREE_ITEM" || discount.type === "freeItem";
};
var isFreeDeliveryDiscountCode = (discount) => {
  return discount.type === "FREE_DELIVERY" || discount.type === "freeDelivery";
};
var getDiscountEligibility = (discountCode, line, taxRateBps) => {
  if (isNumberDiscountCode(discountCode)) {
    const eligible = discountCode.applyToAllProducts || matchesProduct(discountCode.products ?? [], line._id);
    const netPerUnit = grossToNetCents(toCents(discountCode.value), taxRateBps);
    return {
      eligible,
      isNumberDiscount: true,
      percentBps: 0,
      numberDiscountNetPerUnit: netPerUnit
    };
  }
  if (isPercentageDiscountCode(discountCode)) {
    const eligible = discountCode.applyToAllProducts || matchesProduct(discountCode.products ?? [], line._id);
    const percentBps = clampBps(percentToBps(discountCode.value));
    return {
      eligible,
      isNumberDiscount: false,
      percentBps,
      numberDiscountNetPerUnit: 0
    };
  }
  if (isBuyXGetYDiscountCode(discountCode)) {
    const eligible = Boolean(line.isBuyAndGetProduct);
    const condition = discountCode.buyAndGetConditions?.[0];
    const percentFromCondition = condition?.getDiscountType === "free" ? 1e4 : condition?.getDiscountType === "percentage" ? clampBps(percentToBps(condition.discountValue)) : 0;
    return {
      eligible,
      isNumberDiscount: false,
      percentBps: percentFromCondition,
      numberDiscountNetPerUnit: 0
    };
  }
  if (isFreeItemDiscountCode(discountCode)) {
    const eligible = Boolean(line.isFreeItem);
    return {
      eligible,
      isNumberDiscount: false,
      percentBps: eligible ? 1e4 : 0,
      numberDiscountNetPerUnit: 0
    };
  }
  return {
    eligible: false,
    isNumberDiscount: false,
    percentBps: 0,
    numberDiscountNetPerUnit: 0
  };
};
var getStorePercentEntriesForLine = (storeDiscounts, line) => {
  const entries = [];
  storeDiscounts.forEach((discount) => {
    if (!discount.isActive || discount.type !== "percentage") {
      return;
    }
    if (!matchesProduct(discount.products, line._id)) {
      return;
    }
    const bps = clampBps(percentToBps(discount.value));
    if (bps > 0) {
      entries.push({ id: discount._id, bps });
    }
  });
  return entries;
};
var computeGrossBeforeDiscount = (netBefore, taxRateBps) => {
  const taxBefore = mulBps(netBefore, taxRateBps);
  return netBefore + taxBefore;
};
var calculateOrderTotal = (input, customer, store, hasShippingCost, storeDiscounts, discountCodeObj, redeemPointsAsPaymentAmount) => {
  const taxIncluded = store.taxesType === "included";
  const allLines = [
    ...input.products ?? [],
    ...(input.buyAndGetProducts ?? []).map(clearHistoricalPromotionRoleForPricing)
  ].map((line) => applyManualUnitPrice(line, Boolean(input.express)));
  const lines = [];
  const appliedOrderDiscounts = {};
  const automaticDiscountAmounts = /* @__PURE__ */ new Map();
  if (!discountCodeObj) {
    storeDiscounts.forEach((discount) => {
      const discountId = String(discount._id || "").trim();
      if (discountId) {
        automaticDiscountAmounts.set(discountId, { net: 0, taxReduction: 0 });
      }
    });
  }
  const orderTaxBreakdownItems = [];
  let totalQuantity = 0;
  let subtotalBeforeDiscountNet = 0;
  let discountTotalNet = 0;
  let subtotalNetAfterDiscount = 0;
  let taxesTotal = 0;
  let productGrossTotal = 0;
  let productTotalWithoutDiscount = 0;
  let discountTotalGrossLines = 0;
  let orderLevelDiscountGross = 0;
  const onceOnOrderSlicePerLine = new Array(allLines.length).fill(0);
  if (discountCodeObj && isNumberDiscountCode(discountCodeObj) && discountCodeObj.applyOnceOnOrder) {
    const couponProducts = discountCodeObj.products ?? [];
    const eligibleEntries = [];
    allLines.forEach((line, idx) => {
      if (isNoChargeReturnLine(input, line)) return;
      const actualQuantityRaw = asFinite(line.qty, 0);
      if (!Number.isFinite(actualQuantityRaw) || actualQuantityRaw <= 0) return;
      const actualQuantity = Math.min(actualQuantityRaw, MAX_QTY);
      const billableQuantity = resolveBillableQuantity({
        ...line,
        qty: actualQuantity
      });
      const hasManualPrice = hasManualUnitPrice(line);
      const priceToUse = hasManualPrice ? line.manualUnitPrice : input.express ? line.expressPrice ?? line.price : line.price;
      const priceNum = asFinite(priceToUse, NaN);
      if (!Number.isFinite(priceNum)) return;
      const isEligible = discountCodeObj.applyToAllProducts || matchesProduct(couponProducts, line._id);
      if (!isEligible) return;
      const extraNum = hasManualPrice ? 0 : asFinite(line.extraAmount, 0);
      let baseCents = 0;
      if (taxIncluded) {
        const unitGrossCents = toCents(priceNum);
        const extraGrossTotalCents = toCents(extraNum);
        baseCents = Math.round(unitGrossCents * billableQuantity) + extraGrossTotalCents;
      } else {
        const unitNetBase = toCents(priceNum);
        const extraNetTotal = toCents(extraNum);
        baseCents = Math.round(unitNetBase * billableQuantity) + extraNetTotal;
      }
      if (baseCents > 0) {
        eligibleEntries.push({ idx, baseCents });
      }
    });
    if (eligibleEntries.length > 0) {
      const couponTotalCents = clampMin0(toCents(discountCodeObj.value));
      const slices = allocateFixedDiscountAcrossLines(
        couponTotalCents,
        eligibleEntries.map((e) => e.baseCents)
      );
      eligibleEntries.forEach((e, i) => {
        onceOnOrderSlicePerLine[e.idx] = slices[i] ?? 0;
      });
    }
  }
  allLines.forEach((line, lineIdx) => {
    let actualQuantity = asFinite(line.qty, 0);
    if (!Number.isFinite(actualQuantity) || actualQuantity <= 0) {
      return;
    }
    actualQuantity = Math.min(actualQuantity, MAX_QTY);
    if (isNoChargeReturnLine(input, line)) {
      totalQuantity += actualQuantity;
      lines.push({
        lineId: resolveLineId(line),
        quantity: actualQuantity,
        netBeforeDiscount: 0,
        netDiscount: 0,
        netAfterDiscount: 0,
        tax: 0,
        taxBreakdown: [],
        grossBeforeDiscount: 0,
        gross: 0
      });
      return;
    }
    const billableQuantity = resolveBillableQuantity({
      ...line,
      qty: actualQuantity
    });
    const taxRateBps = clampBps(getTaxRateBpsForLine(store, line));
    const applicableTaxes = getApplicableTaxesForLine({
      line,
      taxOne: store.taxOne,
      taxTwo: store.taxTwo,
      taxThree: store.taxThree
    });
    const hasManualPrice = hasManualUnitPrice(line);
    const priceToUse = hasManualPrice ? line.manualUnitPrice : input.express ? line.expressPrice ?? line.price : line.price;
    const priceNum = asFinite(priceToUse, NaN);
    if (!Number.isFinite(priceNum)) {
      return;
    }
    const extraNum = hasManualPrice ? 0 : asFinite(line.extraAmount, 0);
    let lineNetBeforeDiscount = 0;
    let grossBeforeDiscount = 0;
    if (taxIncluded) {
      const unitGrossCents = toCents(priceNum);
      const extraGrossTotalCents = toCents(extraNum);
      grossBeforeDiscount = Math.round(unitGrossCents * billableQuantity) + extraGrossTotalCents;
      lineNetBeforeDiscount = grossToNetCents(grossBeforeDiscount, taxRateBps);
    } else {
      const unitNetBase = toCents(priceNum);
      const extraNetTotal = toCents(extraNum);
      lineNetBeforeDiscount = Math.round(unitNetBase * billableQuantity) + extraNetTotal;
      grossBeforeDiscount = computeGrossBeforeDiscount(lineNetBeforeDiscount, taxRateBps);
    }
    productTotalWithoutDiscount += grossBeforeDiscount;
    let percentBps = 0;
    let discountGross = 0;
    let discountNet = 0;
    const storeEntries = !discountCodeObj ? getStorePercentEntriesForLine(storeDiscounts, line) : [];
    const storeBpsSum = storeEntries.reduce((acc, e) => acc + e.bps, 0);
    const customerPercentBps = !discountCodeObj ? percentToBpsOrZero(customer?.discountPercent) : 0;
    const isOnceOnOrderNumber = discountCodeObj !== null && isNumberDiscountCode(discountCodeObj) && Boolean(discountCodeObj.applyOnceOnOrder);
    if (isOnceOnOrderNumber) {
      const slice = onceOnOrderSlicePerLine[lineIdx] ?? 0;
      if (slice > 0) {
        if (taxIncluded) {
          discountGross = clampMin0(Math.min(grossBeforeDiscount, slice));
        } else {
          discountNet = clampMin0(Math.min(lineNetBeforeDiscount, slice));
        }
      }
    } else if (discountCodeObj) {
      const eligibility = getDiscountEligibility(discountCodeObj, line, taxRateBps);
      if (eligibility.eligible) {
        if (eligibility.isNumberDiscount && isNumberDiscountCode(discountCodeObj)) {
          if (taxIncluded) {
            const perUnitGross = toCents(discountCodeObj.value);
            discountGross = clampMin0(
              Math.min(grossBeforeDiscount, Math.round(perUnitGross * billableQuantity))
            );
          } else {
            const perUnitNet = toCents(discountCodeObj.value);
            discountNet = clampMin0(
              Math.min(lineNetBeforeDiscount, Math.round(perUnitNet * billableQuantity))
            );
          }
        } else if (eligibility.percentBps > 0) {
          percentBps = eligibility.percentBps;
        }
      }
    } else {
      percentBps = clampBps(storeBpsSum + customerPercentBps);
    }
    let lineNetAfterDiscount = lineNetBeforeDiscount;
    let lineGrossAfterDiscount = grossBeforeDiscount;
    let lineTax = 0;
    let lineDiscountNet = 0;
    let lineDiscountGross = 0;
    if (taxIncluded) {
      const percentDiscountGross = percentBps > 0 ? mulBps(grossBeforeDiscount, percentBps) : 0;
      discountGross += percentDiscountGross;
      discountGross = clampMin0(Math.min(discountGross, grossBeforeDiscount));
      lineGrossAfterDiscount = clampMin0(grossBeforeDiscount - discountGross);
      lineNetAfterDiscount = grossToNetCents(lineGrossAfterDiscount, taxRateBps);
      lineTax = clampMin0(lineGrossAfterDiscount - lineNetAfterDiscount);
      lineDiscountNet = clampMin0(lineNetBeforeDiscount - lineNetAfterDiscount);
      lineDiscountGross = clampMin0(grossBeforeDiscount - lineGrossAfterDiscount);
    } else {
      const percentDiscountNet = percentBps > 0 ? mulBps(lineNetBeforeDiscount, percentBps) : 0;
      discountNet += percentDiscountNet;
      discountNet = clampMin0(Math.min(discountNet, lineNetBeforeDiscount));
      lineNetAfterDiscount = clampMin0(lineNetBeforeDiscount - discountNet);
      lineTax = mulBps(lineNetAfterDiscount, taxRateBps);
      lineGrossAfterDiscount = lineNetAfterDiscount + lineTax;
      lineDiscountNet = clampMin0(lineNetBeforeDiscount - lineNetAfterDiscount);
      lineDiscountGross = clampMin0(grossBeforeDiscount - lineGrossAfterDiscount);
    }
    const lineTaxBreakdown = calculateLineTaxBreakdown({
      lineTax,
      taxesType: store.taxesType,
      applicableTaxes
    });
    const totalPercentBps = percentBps;
    const totalNetDiscount = lineDiscountNet;
    const totalTaxReduction = clampMin0(lineDiscountGross - lineDiscountNet);
    if (!discountCodeObj && storeEntries.length > 0 && totalPercentBps > 0 && (totalNetDiscount > 0 || totalTaxReduction > 0)) {
      const storeNetShare = clampMin0(
        Math.min(
          totalNetDiscount,
          Math.round(totalNetDiscount * storeBpsSum / totalPercentBps)
        )
      );
      const weights = storeEntries.map((e) => e.bps);
      const allocations = allocateByWeights(storeNetShare, weights);
      const storeTaxReductionShare = clampMin0(
        Math.min(
          totalTaxReduction,
          Math.round(totalTaxReduction * storeBpsSum / totalPercentBps)
        )
      );
      const taxReductionAllocations = allocateByWeights(storeTaxReductionShare, weights);
      allocations.forEach((share, idx) => {
        const id = storeEntries[idx].id;
        appliedOrderDiscounts[id] = (appliedOrderDiscounts[id] ?? 0) + share;
        const currentAmount = automaticDiscountAmounts.get(id) ?? { net: 0, taxReduction: 0 };
        automaticDiscountAmounts.set(id, {
          net: currentAmount.net + share,
          taxReduction: currentAmount.taxReduction + taxReductionAllocations[idx]
        });
      });
    }
    totalQuantity += actualQuantity;
    subtotalBeforeDiscountNet += lineNetBeforeDiscount;
    discountTotalNet += lineDiscountNet;
    subtotalNetAfterDiscount += lineNetAfterDiscount;
    taxesTotal += lineTax;
    orderTaxBreakdownItems.push(...lineTaxBreakdown);
    productGrossTotal += lineGrossAfterDiscount;
    discountTotalGrossLines += lineDiscountGross;
    if (isOnceOnOrderNumber) {
      orderLevelDiscountGross += taxIncluded ? clampMin0(discountGross) : clampMin0(discountNet);
    }
    lines.push({
      lineId: resolveLineId(line),
      quantity: actualQuantity,
      netBeforeDiscount: lineNetBeforeDiscount,
      netDiscount: lineDiscountNet,
      netAfterDiscount: lineNetAfterDiscount,
      tax: lineTax,
      taxBreakdown: lineTaxBreakdown,
      grossBeforeDiscount,
      gross: lineGrossAfterDiscount
    });
  });
  const shippingPricing = calculateShippingPricing({
    store,
    hasShippingCost,
    isFreeDelivery: Boolean(
      discountCodeObj && isFreeDeliveryDiscountCode(discountCodeObj)
    )
  });
  const { shippingServiceTotal } = shippingPricing;
  taxesTotal += shippingPricing.shippingTaxesTotal;
  orderTaxBreakdownItems.push(...shippingPricing.taxBreakdown);
  const productTotalAfterOrderLevel = productGrossTotal;
  const totalGrossBeforePayments = productTotalAfterOrderLevel + shippingServiceTotal;
  const totalDiscountGross = discountTotalGrossLines;
  const creditAvailable = toCents(customer?.credit ?? 0);
  const creditApplied = clampMin0(Math.min(creditAvailable, totalGrossBeforePayments));
  const redeemPointsPesos = Number.isFinite(Number(redeemPointsAsPaymentAmount)) ? Number(redeemPointsAsPaymentAmount) : 0;
  const pointsAvailable = toCents(Math.max(0, redeemPointsPesos));
  const redeemPointsApplied = clampMin0(
    Math.min(pointsAvailable, totalGrossBeforePayments - creditApplied)
  );
  const total = totalGrossBeforePayments - creditApplied - redeemPointsApplied;
  const taxBreakdown = normalizeTaxBreakdownToTotal(orderTaxBreakdownItems, taxesTotal);
  const discountApplications = Array.from(
    automaticDiscountAmounts.entries(),
    ([discountId, amount2]) => ({
      source: "automatic",
      discountId,
      netAmountCents: amount2.net,
      taxReductionCents: amount2.taxReduction,
      grossAmountCents: amount2.net + amount2.taxReduction
    })
  );
  const discountCodeId = String(
    discountCodeObj?._id ?? discountCodeObj?.discountId ?? ""
  ).trim();
  if (discountCodeObj && discountCodeId) {
    let codeNetAmount = discountTotalNet;
    let codeGrossAmount = totalDiscountGross;
    if (isFreeDeliveryDiscountCode(discountCodeObj) && hasShippingCost) {
      const avoidedShipping = calculateShippingPricing({
        store,
        hasShippingCost,
        isFreeDelivery: false
      });
      codeGrossAmount += avoidedShipping.shippingServiceTotal;
      codeNetAmount += clampMin0(
        avoidedShipping.shippingServiceTotal - avoidedShipping.shippingTaxesTotal
      );
    }
    discountApplications.push({
      source: "code",
      discountId: discountCodeId,
      netAmountCents: codeNetAmount,
      taxReductionCents: clampMin0(codeGrossAmount - codeNetAmount),
      grossAmountCents: codeGrossAmount
    });
  }
  return {
    totals: {
      totalQuantity,
      productTotal: productTotalAfterOrderLevel,
      productTotalWithoutDiscount,
      taxesTotal,
      taxBreakdown,
      shippingServiceTotal,
      orderLevelDiscountGross,
      discountTotalGrossLines,
      totalDiscountGross,
      subtotalBeforeDiscountNet,
      subtotalNetAfterDiscount,
      discountTotalNet,
      subtotalBeforeDiscountGross: productTotalWithoutDiscount,
      subtotalAfterDiscountGross: productTotalAfterOrderLevel,
      totalBeforePaymentsGross: totalGrossBeforePayments,
      creditApplied,
      redeemPointsApplied,
      total
    },
    lines,
    appliedOrderDiscounts,
    discountApplications
  };
};

// src/pricing/manualPriceLineTotals.ts
var calculateManualPriceLineTotals = ({
  line,
  express,
  store,
  storeDiscounts,
  customerDiscount,
  hasDiscountCode,
  discountCode
}) => {
  const fixed = hasDiscountCode && discountCode?.type === "number";
  const effectiveCode = !hasDiscountCode ? null : fixed ? { type: "number", value: line.discountAmount ?? 0, applyOnceOnOrder: true, applyToAllProducts: true } : discountCode ?? { type: "percentage", value: 0, applyToAllProducts: true };
  const productId = String(line._id ?? line.productId ?? (typeof line.storeProductId === "string" ? line.storeProductId : line.storeProductId?._id) ?? "").trim();
  const result = calculateOrderTotal(
    { express, products: [{ ...line, _id: productId, qty: line.qty ?? line.quantity ?? 0 }] },
    { discountPercent: customerDiscount },
    store,
    false,
    storeDiscounts,
    effectiveCode,
    0
  );
  const totals = result.totals;
  const included = store.taxesType === "included";
  return {
    product: line,
    qty: line.qty ?? line.quantity ?? 0,
    productLineImportTotal: (included ? totals.subtotalBeforeDiscountGross : totals.subtotalBeforeDiscountNet) / 100,
    productLineTotalWithDiscount: totals.productTotal / 100,
    productLineTaxesTotal: totals.taxesTotal / 100,
    lineDiscountAmount: (included && fixed ? totals.totalDiscountGross : totals.discountTotalNet) / 100,
    productLineSubtotal: (included ? totals.subtotalBeforeDiscountNet : totals.subtotalNetAfterDiscount) / 100,
    lineNetAfterDiscount: totals.subtotalNetAfterDiscount / 100,
    lineGrossBeforeDiscount: totals.subtotalBeforeDiscountGross / 100,
    taxBreakdown: (totals.taxBreakdown ?? []).map((item) => ({ ...item, amount: item.amount / 100 })),
    appliedDiscountIds: Object.keys(result.appliedOrderDiscounts)
  };
};

// src/order/display.ts
var DEFAULT_FOLIO_PREFIX = "WD";
var PRODUCT_TYPE_WEIGHT = "weight";
var PRODUCT_TYPE_LENGTH = "length";
function isOrderProductLike(item) {
  return typeof item === "object" && item !== null && !Array.isArray(item);
}
function buildOrderFolio(prefix = DEFAULT_FOLIO_PREFIX, sequence) {
  const effectivePrefix = (prefix || DEFAULT_FOLIO_PREFIX).toUpperCase();
  return `${effectivePrefix}-${sequence}`;
}
function formatProductAttributesLines(attributes) {
  if (!attributes?.length) return [];
  return attributes.map((attribute) => {
    const values = Object.entries(attribute).filter(([key]) => key !== "_id").map(([, value]) => {
      if (value == null) return null;
      const trimmedValue = String(value).trim();
      return trimmedValue ? trimmedValue : null;
    }).filter((value) => value !== null);
    return values.join(" / ");
  }).filter((line) => line.length > 0);
}
function getPiecesBreakdown(products, buyAndGetProducts) {
  const allProducts = [...products || [], ...buyAndGetProducts || []];
  return allProducts.reduce(
    (accumulator, item) => {
      if (!isOrderProductLike(item)) return accumulator;
      const type = item.type;
      const pieces = +(item.pieces ?? 0);
      const quantity = +(item.quantity ?? 0);
      const pieceCount = item.pieceCount == null ? null : +item.pieceCount;
      if (type === PRODUCT_TYPE_WEIGHT) {
        accumulator.kg += quantity;
        if (pieceCount != null) accumulator.pieces += pieceCount;
        return accumulator;
      }
      if (type === PRODUCT_TYPE_LENGTH) {
        accumulator.m += quantity;
        if (pieceCount != null) accumulator.pieces += pieceCount;
        return accumulator;
      }
      accumulator.pieces += pieceCount ?? pieces * quantity;
      return accumulator;
    },
    { pieces: 0, kg: 0, m: 0 }
  );
}
function formatMeasuredQuantity(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  }).format(numericValue);
}
function getMeasuredUnitLabel(productType, measurementUnits) {
  if (productType === PRODUCT_TYPE_LENGTH) {
    return measurementUnits?.length ?? "m";
  }
  if (productType === PRODUCT_TYPE_WEIGHT) {
    return measurementUnits?.weight ?? "kg";
  }
  return "";
}
function formatPiecesBreakdown(breakdown, options) {
  const decimals = options?.decimals ?? 2;
  const units = options?.measurementUnits;
  const format = (value) => Number.isInteger(value) ? String(value) : value.toFixed(decimals);
  const weightLabel = units?.weight === "lb" ? "lbs" : "kgs";
  const lengthLabel = units?.length === "ft" ? "ft" : "m";
  const parts = [];
  if (breakdown.pieces > 0) parts.push(`${format(breakdown.pieces)} piezas`);
  if (breakdown.kg > 0) parts.push(`${format(breakdown.kg)} ${weightLabel}`);
  if (breakdown.m > 0) parts.push(`${format(breakdown.m)} ${lengthLabel}`);
  return parts.join(" | ") || "0 piezas";
}

// src/order/displayTotals.ts
function pickOrderDisplayTotals(totals) {
  if (!totals) return void 0;
  return { subtotal: totals.subtotal, discount: totals.discount, taxesLabel: totals.taxesLabel };
}
function orderDisplayTotalsFromPricingResult(result, taxesType) {
  const included = taxesType === "included";
  return {
    subtotal: fromCents(included ? result.totals.subtotalBeforeDiscountGross : result.totals.subtotalBeforeDiscountNet),
    discount: fromCents(included ? result.totals.totalDiscountGross : result.totals.discountTotalNet),
    taxesLabel: included ? "taxesIncluded" : "taxesTotal"
  };
}
var isAmount = (value) => typeof value === "number" && Number.isFinite(value);
function resolveOrderDisplayTotals(order) {
  const before = order.productTotalWithoutDiscount;
  let discount = order.totalDiscountAmount ?? 0;
  if (isAmount(before) && isAmount(order.productTotal)) {
    const productTotalReconciles = !isAmount(order.total) || toCents(order.productTotal) + toCents(order.shippingServiceTotal ?? 0) - toCents(order.creditApplied ?? 0) - toCents(order.loyaltyDiscountAmount ?? 0) === toCents(order.total);
    if (order.taxesType === "included" && productTotalReconciles) {
      discount = fromCents(toCents(before) - toCents(order.productTotal));
    }
  }
  if (order.taxesType === "over_price" && isAmount(before) && isAmount(order.subtotal)) {
    const netDiscountCents = toCents(before) - toCents(order.subtotal);
    if (isAmount(order.totalDiscountAmount) && netDiscountCents === toCents(order.totalDiscountAmount)) {
      discount = fromCents(netDiscountCents);
    }
  }
  return {
    subtotal: order.displayTotals?.subtotal ?? before ?? order.subtotal ?? 0,
    discount: order.displayTotals?.discount ?? discount,
    taxesLabel: order.displayTotals?.taxesLabel ?? (order.taxesType === "included" ? "taxesIncluded" : "taxesTotal")
  };
}

// src/order/section.ts
var resolveSectionReferenceName = (section) => {
  if (section && typeof section === "object" && typeof section.name === "string") {
    return section.name.trim();
  }
  return "";
};

// src/order/lineAssignments.ts
function canAssignOrderLineEmployees(permission) {
  return permission?.canEditOrders?.canMutate === true || permission?.assignOrderLineEmployees?.canMutate === true;
}
var record = (value) => value !== null && typeof value === "object";
var stringId = (value) => typeof value === "string" && value.trim() ? value.trim() : null;
function getAssignedEmployeeId(value) {
  return stringId(record(value) ? value._id ?? value.id : value);
}
function getOrderLineAssignmentId(line) {
  return record(line) ? stringId(line.lineId) || stringId(line._id) : null;
}
function isOrderLineAssignable(line) {
  return record(line) && !line.returnSourceOrderLineId && getOrderLineAssignmentId(line) !== null;
}
function getOrderLineAssignmentKey(line, collection, index) {
  return `${collection}:${getOrderLineAssignmentId(line) || `index-${index}`}`;
}
function buildInitialAssignedEmployeeMap(order) {
  const assignments = {};
  for (const collection of ["products", "buyAndGetProducts"]) {
    (order?.[collection] || []).forEach((line, index) => {
      if (!record(line)) return;
      assignments[getOrderLineAssignmentKey(line, collection, index)] = getAssignedEmployeeId(line.assignedEmployee);
    });
  }
  return assignments;
}
function buildOrderLineAssignmentPayload(order, assignments) {
  const updates = [];
  for (const collection of ["products", "buyAndGetProducts"]) {
    (order?.[collection] || []).forEach((line, index) => {
      if (!record(line)) return;
      const lineId = getOrderLineAssignmentId(line);
      const key = getOrderLineAssignmentKey(line, collection, index);
      if (!lineId || !isOrderLineAssignable(line) || !Object.prototype.hasOwnProperty.call(assignments, key) || assignments[key] === getAssignedEmployeeId(line.assignedEmployee)) return;
      updates.push({ collection, lineId, assignedEmployee: assignments[key] });
    });
  }
  return { updates };
}
function hasOrderLineAssignmentChanges(order, assignments) {
  return buildOrderLineAssignmentPayload(order, assignments).updates.length > 0;
}
function getEmployeeDisplayName(employee) {
  return employee.name || [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim() || employee.email || employee.username || employee._id || employee.id || "";
}
function getAssignedEmployeeLabel(value, employees, unassignedLabel) {
  const id = getAssignedEmployeeId(value);
  if (!id) return unassignedLabel;
  const employee = employees.find((candidate) => getAssignedEmployeeId(candidate) === id);
  return employee ? getEmployeeDisplayName(employee) || id : record(value) ? getEmployeeDisplayName(value) || id : id;
}
function normalizeAssignedEmployeeFilter(value) {
  return value && value.trim() ? value : void 0;
}

// src/qr/qrPayload.ts
var PREFIX = "wd";
var VERSION = "1";
var PERCENT_ENCODED_REGEX = /%[0-9a-fA-F]{2}/;
var PERCENT_ENCODED_GLOBAL_REGEX = /%[0-9a-fA-F]{2}/g;
function normalizeScanInput(raw) {
  const input = (raw || "").trim();
  if (!input) return "";
  if (!PERCENT_ENCODED_REGEX.test(input)) return input;
  try {
    return decodeURIComponent(input);
  } catch {
    const stripped = input.replace(PERCENT_ENCODED_GLOBAL_REGEX, "");
    return stripped || input;
  }
}
function buildQrPayload({ orderId, customerId }) {
  const cleanOrderId = (orderId || "").trim();
  const cleanCustomerId = (customerId || "").trim();
  if (!cleanOrderId) {
    throw new Error("orderId is required to build QR payload");
  }
  const segments = [`order:[${cleanOrderId}]`];
  if (cleanCustomerId) {
    segments.push(`customer:[${cleanCustomerId}]`);
  }
  return [PREFIX, VERSION, ...segments].join("|");
}
function parseScannedCode(raw) {
  const input = normalizeScanInput(raw || "");
  if (!input) {
    return { error: "invalid_payload" };
  }
  if (!input.includes("|")) {
    const orderMatch = /order[^a-fA-F0-9]*([a-fA-F0-9]{24})/i.exec(input);
    if (orderMatch) {
      const customerMatch = /customer[^a-fA-F0-9]*([a-fA-F0-9]{24})/i.exec(input);
      const orderId2 = orderMatch[1];
      if (customerMatch) {
        return { orderId: orderId2, customerId: customerMatch[1] };
      }
      return { orderId: orderId2 };
    }
    if (/(order|customer)/i.test(input)) {
      return { error: "invalid_payload" };
    }
    return { legacyOrderId: input };
  }
  const parts = input.split("|").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return { error: "invalid_payload" };
  const [prefix, version, ...payloadParts] = parts;
  if (prefix.toLowerCase() !== PREFIX || version !== VERSION) {
    return { error: "invalid_payload" };
  }
  let orderId;
  let customerId;
  for (const part of payloadParts) {
    const match = /^(order|customer):\[(.+)\]$/i.exec(part);
    if (!match) return { error: "invalid_payload" };
    const [, key, value] = match;
    if (!value.trim()) return { error: "invalid_payload" };
    const keyLower = key.toLowerCase();
    if (keyLower === "order") orderId = value.trim();
    if (keyLower === "customer") customerId = value.trim();
  }
  if (!orderId) return { error: "invalid_payload" };
  return customerId ? { orderId, customerId } : { orderId };
}

// src/quantity/quantity.ts
var MEASURED_QUANTITY_MAX = 99999.99;
var parseDecimalInput = (input) => {
  if (typeof input !== "string") return null;
  const normalized = input.trim();
  if (normalized.includes(",")) return null;
  if (!normalized) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
};
var normalizeMeasuredQuantity = (value, precision = 3) => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};
var validateMeasuredQuantity = (value, precisionOrOptions = 3, maxValue = MEASURED_QUANTITY_MAX, allowNegative = false, allowZero = false) => {
  const options = typeof precisionOrOptions === "number" ? {
    precision: precisionOrOptions,
    maxValue,
    allowNegative,
    allowZero
  } : precisionOrOptions;
  const precision = options.precision ?? 3;
  const max = options.maxValue ?? maxValue;
  const allowNeg = options.allowNegative ?? false;
  const allowZ = options.allowZero ?? false;
  if (!Number.isFinite(value)) {
    return { ok: false, error: "invalid_quantity" };
  }
  const normalized = normalizeMeasuredQuantity(value, precision);
  if (!allowNeg && normalized < 0) {
    return { ok: false, error: "invalid_quantity" };
  }
  if (!allowZ && normalized === 0) {
    return { ok: false, error: "invalid_quantity" };
  }
  if (Math.abs(normalized) > max) {
    return { ok: false, error: "invalid_quantity" };
  }
  return { ok: true, value: normalized };
};

// src/location/mapLocation.ts
var isFiniteNumber = (value) => {
  return typeof value === "number" && Number.isFinite(value);
};
var isValidLatLng = (lat, lng) => {
  return isFiniteNumber(lat) && isFiniteNumber(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};
function buildMapLocationFromLatLng(args) {
  return {
    type: "Point",
    coordinates: [args.lng, args.lat],
    source: args.source || "manual",
    ...args.formattedAddress ? { formattedAddress: args.formattedAddress } : {},
    ...args.placeId ? { placeId: args.placeId } : {},
    ...args.updatedAt ? { updatedAt: args.updatedAt } : {}
  };
}
function getLatFromMapLocation(mapLocation) {
  const lat = mapLocation?.coordinates?.[1];
  return isFiniteNumber(lat) ? lat : null;
}
function getLngFromMapLocation(mapLocation) {
  const lng = mapLocation?.coordinates?.[0];
  return isFiniteNumber(lng) ? lng : null;
}
function getLatLngFromMapLocation(mapLocation) {
  const lat = getLatFromMapLocation(mapLocation);
  const lng = getLngFromMapLocation(mapLocation);
  if (lat === null || lng === null || !isValidLatLng(lat, lng)) {
    return null;
  }
  return { lat, lng };
}
function isValidMapLocationCoordinates(mapLocation) {
  if (mapLocation?.type !== "Point") {
    return false;
  }
  const lat = getLatFromMapLocation(mapLocation);
  const lng = getLngFromMapLocation(mapLocation);
  return isValidLatLng(lat, lng);
}
function buildGoogleMapsSearchUrlFromLatLng(latLng) {
  if (!latLng || !isValidLatLng(latLng.lat, latLng.lng)) {
    return null;
  }
  return `https://www.google.com/maps/search/?api=1&query=${latLng.lat},${latLng.lng}`;
}
function getGoogleMapsUrlFromMapLocation(mapLocation) {
  return buildGoogleMapsSearchUrlFromLatLng(getLatLngFromMapLocation(mapLocation));
}

// src/location/shippingDecision.ts
var isValidLatLng2 = (value) => Boolean(
  value && Number.isFinite(value.lat) && value.lat >= -90 && value.lat <= 90 && Number.isFinite(value.lng) && value.lng >= -180 && value.lng <= 180
);
function resolveAddressLatLng(address) {
  const mapLatLng = isValidMapLocationCoordinates(address?.mapLocation) ? getLatLngFromMapLocation(address?.mapLocation) : null;
  if (mapLatLng) {
    return mapLatLng;
  }
  const legacyLatLng = address?.coordinates ? { lat: address.coordinates.lat, lng: address.coordinates.lng } : null;
  return isValidLatLng2(legacyLatLng) ? legacyLatLng : null;
}
function calculateHaversineDistanceKm(origin, destination) {
  if (!isValidLatLng2(origin) || !isValidLatLng2(destination)) {
    throw new RangeError("Invalid latitude or longitude");
  }
  if (origin.lat === destination.lat && origin.lng === destination.lng) {
    return 0;
  }
  const toRadians = (degrees) => degrees * Math.PI / 180;
  const earthRadiusKm = 6371.0088;
  const latitudeDelta = toRadians(destination.lat - origin.lat);
  const longitudeDelta = toRadians(destination.lng - origin.lng);
  const originLatitude = toRadians(origin.lat);
  const destinationLatitude = toRadians(destination.lat);
  const haversine = Math.sin(latitudeDelta / 2) ** 2 + Math.cos(originLatitude) * Math.cos(destinationLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  const normalizedHaversine = Math.min(1, Math.max(0, haversine));
  return earthRadiusKm * 2 * Math.atan2(
    Math.sqrt(normalizedHaversine),
    Math.sqrt(1 - normalizedHaversine)
  );
}
function resolveShippingDecision(input) {
  const radiusKm = Number.isFinite(input.freeShippingRadiusKm) && Number(input.freeShippingRadiusKm) > 0 ? Number(input.freeShippingRadiusKm) : 0;
  if (!input.pickup && !input.delivery) {
    return { shouldCharge: false, reason: "not_requested", radiusKm };
  }
  if (Number.isFinite(input.shippingServiceCost) && Number(input.shippingServiceCost) <= 0) {
    return { shouldCharge: false, reason: "shipping_cost_disabled", radiusKm };
  }
  if (radiusKm <= 0) {
    return { shouldCharge: true, reason: "radius_disabled", radiusKm };
  }
  const storeLatLng = isValidMapLocationCoordinates(input.storeMapLocation) ? getLatLngFromMapLocation(input.storeMapLocation) : null;
  if (!storeLatLng) {
    return { shouldCharge: true, reason: "missing_store_coordinates", radiusKm };
  }
  const addressLatLng = resolveAddressLatLng(input.address);
  if (!addressLatLng) {
    return { shouldCharge: true, reason: "missing_address_coordinates", radiusKm };
  }
  const distanceKm = calculateHaversineDistanceKm(storeLatLng, addressLatLng);
  const insideFreeRadius = distanceKm <= radiusKm;
  return {
    shouldCharge: !insideFreeRadius,
    reason: insideFreeRadius ? "inside_free_radius" : "outside_free_radius",
    distanceKm,
    radiusKm
  };
}

// src/location/isoCountryCode.ts
var ISO_3166_ALPHA_2_COUNTRY_CODES = new Set(
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ")
);
function isIso3166Alpha2CountryCode(value) {
  return typeof value === "string" && ISO_3166_ALPHA_2_COUNTRY_CODES.has(value.trim().toUpperCase());
}

// src/location/storeCountryDefaults.ts
var DEFAULT_STORE_COUNTRY_DEFAULTS = { currencySymbol: "$" };
var STORE_DEFAULTS_BY_COUNTRY = {
  MX: { currencySymbol: "$", currencyCode: "MXN" },
  CO: { currencySymbol: "$", currencyCode: "COP" },
  PE: { currencySymbol: "S/", currencyCode: "PEN" }
};
function resolveStoreCountryDefaultsByIso(iso) {
  return STORE_DEFAULTS_BY_COUNTRY[iso?.trim().toUpperCase() || ""] || DEFAULT_STORE_COUNTRY_DEFAULTS;
}
function resolveCustomerDisplayCurrency(currency, country, companyCountry) {
  if (currency && /^[A-Z]{3}$/.test(currency)) return currency;
  const iso = (value) => {
    const candidate = typeof value === "string" ? value : value && typeof value === "object" ? value.iso : null;
    return typeof candidate === "string" && /^[A-Za-z]{2}$/.test(candidate.trim()) ? candidate.trim().toUpperCase() : "";
  };
  const countryIso = iso(country) || iso(companyCountry);
  return resolveStoreCountryDefaultsByIso(countryIso).currencyCode || { CL: "CLP", US: "USD" }[countryIso] || "MXN";
}

// src/expenses/form.ts
var DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
var padDatePart = (value) => String(value).padStart(2, "0");
var formatDatePartsInTimeZone = (date, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(date);
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    if (!year || !month || !day) return null;
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
};
var getExpenseStoreTimeZone = (expense) => {
  const store = expense?.storeId;
  if (!store || typeof store === "string") return void 0;
  return store.timezone;
};
var parseExpenseDateForCalendar = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const dateOnlyMatch = value.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
var formatExpenseDateForPayload = (value, timeZone) => {
  if (typeof value === "string") {
    const dateOnlyMatch = value.trim().match(DATE_ONLY_PATTERN);
    if (dateOnlyMatch) return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (timeZone) {
    const zonedDate = formatDatePartsInTimeZone(date, timeZone);
    if (zonedDate) return zonedDate;
  }
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};
var isValueInExpenseOptionList = (value, options) => options.includes(value);
var cleanOptionalText = (value, preserveEmpty = false) => {
  const trimmed2 = value.trim();
  if (trimmed2) return trimmed2;
  return preserveEmpty ? "" : void 0;
};
var isExpenseMovementLocked = (expense) => {
  const movement = expense?.cashierBoxMovementId;
  if (!movement || typeof movement === "string") return false;
  return movement.isOnACashUpReport === true;
};
var getExpenseCashierBoxId = (expense) => {
  const cashierBox = expense?.cashierBoxId;
  if (!cashierBox) return "";
  if (typeof cashierBox === "string") return cashierBox;
  return cashierBox._id || "";
};
var getExpenseCashierBoxLabel = (expense) => {
  const cashierBox = expense?.cashierBoxId;
  if (!cashierBox || typeof cashierBox === "string") return "";
  return cashierBox.name || "";
};
function buildExpensePayload(values, isUpdate = false) {
  const payload = {
    category: values.category,
    description: values.description.trim(),
    amount: Number(values.amount),
    expenseDate: formatExpenseDateForPayload(values.expenseDate || /* @__PURE__ */ new Date()),
    paymentMethod: values.paymentMethod,
    reference: cleanOptionalText(values.reference, isUpdate),
    notes: cleanOptionalText(values.notes, isUpdate),
    registerAsCashierBoxMovement: values.registerAsCashierBoxMovement
  };
  if (!isUpdate) {
    payload.storeId = values.storeId;
  }
  if (values.registerAsCashierBoxMovement && values.cashierBoxId) {
    payload.cashierBoxId = values.cashierBoxId;
  }
  return payload;
}

// src/cashup/calculationDetail.ts
var movementGroupKeys = ["cash", "card", "transfer", "mercadoPago"];
function isCashPaymentMethod(paymentMethod) {
  return paymentMethod === "cash";
}
function mapPendingPayments(preview) {
  return (preview?.ordersWithLines || []).flatMap(
    (order) => (order.paymentLines || []).filter((paymentLine) => !paymentLine.isOnACashUpReport).map((paymentLine, index) => ({
      key: `${order._id || order.sequence || "order"}-${paymentLine._id || index}`,
      folio: buildOrderFolio(order.folioPrefix, order.sequence),
      amount: paymentLine.amountPaid,
      paymentMethod: paymentLine.paymentMethod,
      date: paymentLine.paymentDate
    }))
  );
}
function mapMovements(preview) {
  return ["inMovements", "outMovements"].flatMap(
    (movementType) => movementGroupKeys.flatMap(
      (paymentMethod) => (preview?.[movementType]?.[paymentMethod] || []).map(
        (movement, index) => ({
          key: movement._id || `${movementType}-${paymentMethod}-${index}`,
          type: movement.type,
          amount: movement.amount,
          paymentMethod: movement.paymentMethod,
          date: movement.date,
          notes: movement.notes,
          isExpense: movement.sourceType === "expense"
        })
      )
    )
  );
}
function mapCashupCalculationDetail(preview, isCashOnly) {
  const payments = mapPendingPayments(preview);
  const movements = mapMovements(preview);
  if (!isCashOnly) {
    return [{ key: "all", payments, movements }];
  }
  return [
    {
      key: "cash",
      payments: payments.filter(
        (payment) => isCashPaymentMethod(payment.paymentMethod)
      ),
      movements: movements.filter(
        (movement) => isCashPaymentMethod(movement.paymentMethod)
      )
    },
    {
      key: "nonCash",
      payments: payments.filter(
        (payment) => !isCashPaymentMethod(payment.paymentMethod)
      ),
      movements: movements.filter(
        (movement) => !isCashPaymentMethod(movement.paymentMethod)
      )
    }
  ];
}

// src/cashup/reportSummary.ts
var CASH_ONLY_PLACEHOLDER = "--";
var asNumber = (value) => Number(value ?? 0);
var buildLegacyCashupReportSummary = (reportData) => {
  const totalMercadoPagoCalculated = asNumber(
    reportData.totalMercadoPagoTotalCalculated
  );
  const declaredMercadoPago = asNumber(reportData.declaredMercadoPago);
  const totalCashCalculated = asNumber(reportData.totalCashCalculated);
  const totalCardCalculated = asNumber(reportData.totalCardCalculated);
  const totalTransferCalculated = asNumber(
    reportData.totalTransferCalculated
  );
  const declaredCash = asNumber(reportData.declaredCash);
  const declaredCard = asNumber(reportData.declaredCard);
  const declaredTransfer = asNumber(reportData.declaredTransfer);
  const calculatedTotal = totalCardCalculated + totalCashCalculated + totalTransferCalculated + totalMercadoPagoCalculated;
  const declaredTotal = declaredCard + declaredTransfer + declaredCash + declaredMercadoPago;
  return {
    declaredTotal,
    calculatedTotal,
    difference: declaredTotal - calculatedTotal,
    paymentTypeRows: [
      {
        key: "cash",
        calculated: totalCashCalculated,
        counted: declaredCash,
        difference: declaredCash - totalCashCalculated
      },
      {
        key: "card",
        calculated: totalCardCalculated,
        counted: declaredCard,
        difference: declaredCard - totalCardCalculated
      },
      {
        key: "transfer",
        calculated: totalTransferCalculated,
        counted: declaredTransfer,
        difference: declaredTransfer - totalTransferCalculated
      },
      {
        key: "mercadopago",
        calculated: totalMercadoPagoCalculated,
        counted: declaredMercadoPago,
        difference: declaredMercadoPago - totalMercadoPagoCalculated
      }
    ]
  };
};
var buildCashUpReportDisplaySummary = (reportData) => {
  const legacySummary = buildLegacyCashupReportSummary(reportData);
  if (!reportData.reconciliationMode) {
    return legacySummary;
  }
  const nonCashSummary = {
    card: reportData.nonCashSummary?.card ?? asNumber(reportData.totalCardCalculated),
    transfer: reportData.nonCashSummary?.transfer ?? asNumber(reportData.totalTransferCalculated),
    mercadoPago: reportData.nonCashSummary?.mercadoPago ?? asNumber(reportData.totalMercadoPagoTotalCalculated)
  };
  const declaredTotal = reportData.declaredTotal ?? legacySummary.declaredTotal;
  const calculatedTotal = reportData.calculatedTotal ?? legacySummary.calculatedTotal;
  const difference = reportData.variance ?? declaredTotal - calculatedTotal;
  const declaredCash = asNumber(reportData.declaredCash);
  const declaredCard = asNumber(reportData.declaredCard);
  const declaredTransfer = asNumber(reportData.declaredTransfer);
  const declaredMercadoPago = asNumber(reportData.declaredMercadoPago);
  const totalCashCalculated = asNumber(reportData.totalCashCalculated);
  if (reportData.reconciliationMode === "cash_only") {
    const cashOnlyDeclaredTotal = reportData.declaredTotal ?? declaredCash;
    const cashOnlyCalculatedTotal = reportData.calculatedTotal ?? totalCashCalculated;
    const cashOnlyDifference = reportData.variance ?? cashOnlyDeclaredTotal - cashOnlyCalculatedTotal;
    return {
      declaredTotal: cashOnlyDeclaredTotal,
      calculatedTotal: cashOnlyCalculatedTotal,
      difference: cashOnlyDifference,
      paymentTypeRows: [
        {
          key: "cash",
          calculated: totalCashCalculated,
          counted: declaredCash,
          difference: declaredCash - totalCashCalculated
        },
        {
          key: "card",
          calculated: nonCashSummary.card,
          counted: CASH_ONLY_PLACEHOLDER,
          difference: CASH_ONLY_PLACEHOLDER
        },
        {
          key: "transfer",
          calculated: nonCashSummary.transfer,
          counted: CASH_ONLY_PLACEHOLDER,
          difference: CASH_ONLY_PLACEHOLDER
        },
        {
          key: "mercadopago",
          calculated: nonCashSummary.mercadoPago,
          counted: CASH_ONLY_PLACEHOLDER,
          difference: CASH_ONLY_PLACEHOLDER
        }
      ]
    };
  }
  return {
    declaredTotal,
    calculatedTotal,
    difference,
    paymentTypeRows: [
      {
        key: "cash",
        calculated: totalCashCalculated,
        counted: declaredCash,
        difference: declaredCash - totalCashCalculated
      },
      {
        key: "card",
        calculated: nonCashSummary.card,
        counted: declaredCard,
        difference: declaredCard - nonCashSummary.card
      },
      {
        key: "transfer",
        calculated: nonCashSummary.transfer,
        counted: declaredTransfer,
        difference: declaredTransfer - nonCashSummary.transfer
      },
      {
        key: "mercadopago",
        calculated: nonCashSummary.mercadoPago,
        counted: declaredMercadoPago,
        difference: declaredMercadoPago - nonCashSummary.mercadoPago
      }
    ]
  };
};

// src/cfdi/receiverFiscalProfile.ts
var CFDI_CUSTOMER_FISCAL_PROFILE_FIELDS = [
  "rfc",
  "legalName",
  "taxSystem",
  "zipCode"
];
var SAT_TAX_SYSTEM_CODES = /* @__PURE__ */ new Set([
  "601",
  "603",
  "605",
  "606",
  "607",
  "608",
  "609",
  "610",
  "611",
  "612",
  "614",
  "615",
  "616",
  "620",
  "621",
  "622",
  "623",
  "624",
  "625",
  "626",
  "628",
  "629",
  "630"
]);
var RFC_PATTERN = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;
var MEXICAN_POSTAL_CODE_PATTERN = /^\d{5}$/;
var trimmed = (value) => typeof value === "string" ? value.trim() : "";
function normalizeCFDICustomerFiscalProfile(profile) {
  return {
    rfc: trimmed(profile?.rfc).toUpperCase(),
    legalName: trimmed(profile?.legalName),
    taxSystem: trimmed(profile?.taxSystem),
    zipCode: trimmed(profile?.zipCode)
  };
}
function getCFDICustomerFiscalProfileFromPreview(customer) {
  return normalizeCFDICustomerFiscalProfile({
    rfc: customer?.tax_id || "",
    legalName: customer?.legal_name || "",
    taxSystem: customer?.tax_system || "",
    zipCode: customer?.address?.zip || ""
  });
}
function getMissingCFDICustomerFiscalProfileFields(profile) {
  const normalized = normalizeCFDICustomerFiscalProfile(profile);
  return CFDI_CUSTOMER_FISCAL_PROFILE_FIELDS.filter((field) => !normalized[field]);
}
function validateCFDICustomerFiscalProfile(profile) {
  const normalized = normalizeCFDICustomerFiscalProfile(profile);
  const errors = {};
  for (const field of CFDI_CUSTOMER_FISCAL_PROFILE_FIELDS) {
    if (!normalized[field]) {
      errors[field] = "required";
    }
  }
  if (normalized.rfc && !RFC_PATTERN.test(normalized.rfc)) {
    errors.rfc = "invalid_rfc";
  }
  if (normalized.taxSystem && !SAT_TAX_SYSTEM_CODES.has(normalized.taxSystem)) {
    errors.taxSystem = "invalid_tax_system";
  }
  if (normalized.zipCode && !MEXICAN_POSTAL_CODE_PATTERN.test(normalized.zipCode)) {
    errors.zipCode = "invalid_zip_code";
  }
  return errors;
}
function areCFDICustomerFiscalProfilesEqual(left, right) {
  const normalizedLeft = normalizeCFDICustomerFiscalProfile(left);
  const normalizedRight = normalizeCFDICustomerFiscalProfile(right);
  return CFDI_CUSTOMER_FISCAL_PROFILE_FIELDS.every(
    (field) => normalizedLeft[field] === normalizedRight[field]
  );
}

// src/entityReference.ts
var getReferenceId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value._id === "string") return value._id;
  if (typeof value.id === "string") return value.id;
  return null;
};

// src/files/reportFiles.ts
var MIME_TYPES = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
};
function identifyReportFile(contentType, bytes) {
  const mime = contentType?.split(";")[0].trim().toLowerCase();
  const format = bytes[0] === 37 && bytes[1] === 80 && bytes[2] === 68 && bytes[3] === 70 && bytes[4] === 45 ? "pdf" : bytes[0] === 80 && bytes[1] === 75 && bytes[2] === 3 && bytes[3] === 4 ? "xlsx" : null;
  if (!format || mime && mime !== "application/octet-stream" && mime !== MIME_TYPES[format]) return null;
  return { format, mimeType: MIME_TYPES[format] };
}
function buildReportFileName(input) {
  const safePart = (value) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^[-_]+|[-_]+$/g, "").slice(0, 50);
  const parts = [safePart(input.reportName) || "Reporte", safePart(input.storeName || ""), input.date.toISOString().slice(0, 10)];
  return `${parts.filter(Boolean).join("_")}.${input.format}`;
}

// src/ticket/mapping.ts
var CUSTOMER_NAME_FONT_SIZE_TO_ESC_POS_SCALE = {
  small: { widthMultiplier: 1, heightMultiplier: 1 },
  medium: { widthMultiplier: 1, heightMultiplier: 2 },
  large: { widthMultiplier: 2, heightMultiplier: 2 }
};
var ORDER_FOLIO_FONT_PRESET_TO_ESC_POS_SCALE = {
  small: { widthMultiplier: 1, heightMultiplier: 1 },
  medium: { widthMultiplier: 1, heightMultiplier: 2 },
  large: { widthMultiplier: 2, heightMultiplier: 2 }
};
var WASHDAY_LINE_MARGIN_TO_ESC_POS_SPACING = [
  {
    maxPx: 4,
    preset: {
      blankLinesBefore: 0,
      blankLinesAfter: 0,
      blankLineFeedDots: 24
    }
  },
  {
    maxPx: 8,
    preset: {
      blankLinesBefore: 0,
      blankLinesAfter: 1,
      blankLineFeedDots: 18
    }
  },
  {
    maxPx: 12,
    preset: {
      blankLinesBefore: 0,
      blankLinesAfter: 1,
      blankLineFeedDots: 24
    }
  },
  {
    maxPx: Number.POSITIVE_INFINITY,
    preset: {
      blankLinesBefore: 0,
      blankLinesAfter: 1,
      blankLineFeedDots: 30
    }
  }
];
var TICKET_LINE_SPACING_PRESET_TO_ESC_POS_SPACING = {
  // Shared presets capture visual intent only. ESC/POS still renders them as a
  // few discrete feed steps instead of pretending thermal printers understand px.
  small: {
    blankLinesBefore: 0,
    blankLinesAfter: 0,
    blankLineFeedDots: 24
  },
  medium: {
    blankLinesBefore: 0,
    blankLinesAfter: 1,
    blankLineFeedDots: 24
  },
  large: {
    blankLinesBefore: 0,
    blankLinesAfter: 1,
    blankLineFeedDots: 30
  }
};
function resolveOrderFolioFontPresetFromLegacyPx(fontSizePx = 25) {
  if (fontSizePx <= 15) {
    return "small";
  }
  if (fontSizePx <= 23) {
    return "medium";
  }
  return "large";
}
function resolveTicketLineSpacingPresetFromLegacyPx(spacingPx = 12) {
  const normalizedSpacingPx = Number.isFinite(spacingPx) ? spacingPx : 12;
  if (normalizedSpacingPx <= 4) {
    return "small";
  }
  if (normalizedSpacingPx <= 12) {
    return "medium";
  }
  return "large";
}
function resolveOrderFolioFontPresetFromLegacyEmphasis(emphasis = "hero") {
  switch (emphasis) {
    case "normal":
      return "small";
    case "prominent":
      return "medium";
    case "hero":
    default:
      return "large";
  }
}
function resolveTicketLineSpacingPresetFromLegacyDensity(density = "regular") {
  switch (density) {
    case "compact":
      return "small";
    case "spacious":
      return "large";
    case "regular":
    default:
      return "medium";
  }
}
function mapOrderFolioFontPreset(fontSize = "large") {
  return ORDER_FOLIO_FONT_PRESET_TO_ESC_POS_SCALE[fontSize];
}
function mapOrderFolioFontSize(fontSizePx = 25) {
  return mapOrderFolioFontPreset(
    resolveOrderFolioFontPresetFromLegacyPx(fontSizePx)
  );
}
function mapCustomerNameFontSize(fontSize = "medium") {
  return CUSTOMER_NAME_FONT_SIZE_TO_ESC_POS_SCALE[fontSize];
}
function mapTicketLineSpacingPreset(spacingPreset = "medium") {
  return TICKET_LINE_SPACING_PRESET_TO_ESC_POS_SPACING[spacingPreset];
}
function mapTicketLineMarginToEscPosSpacing(spacingPx = 12) {
  const normalizedSpacingPx = Number.isFinite(spacingPx) ? spacingPx : 12;
  const match = WASHDAY_LINE_MARGIN_TO_ESC_POS_SPACING.find(
    ({ maxPx }) => normalizedSpacingPx <= maxPx
  );
  return match?.preset ?? WASHDAY_LINE_MARGIN_TO_ESC_POS_SPACING[2].preset;
}
function mapSpacing(spacingPx = 12) {
  return mapTicketLineMarginToEscPosSpacing(spacingPx);
}
function mapSpacingDensity(density = "regular") {
  return mapTicketLineSpacingPreset(
    resolveTicketLineSpacingPresetFromLegacyDensity(density)
  );
}
function resolveOrderFolioScale(presentation) {
  const preset = presentation?.orderFolioSize ?? resolveOrderFolioFontPresetFromLegacyEmphasis(
    presentation?.orderFolioEmphasis
  );
  return mapOrderFolioFontPreset(preset);
}
function resolveEscPosSpacing(presentation) {
  if (presentation?.blockSpacingPreset) {
    return mapTicketLineSpacingPreset(presentation.blockSpacingPreset);
  }
  if (typeof presentation?.blockSpacingPxHint === "number") {
    return mapTicketLineMarginToEscPosSpacing(presentation.blockSpacingPxHint);
  }
  return mapSpacingDensity(presentation?.blockDensity);
}
function getEscPosPaperLayout(paperWidth = 58) {
  if (paperWidth === 80) {
    return {
      paperWidth,
      charactersPerLine: 48,
      imageMaxWidth: 576,
      qrModuleSize: 8,
      barcodeHeight: 96
    };
  }
  return {
    paperWidth: 58,
    charactersPerLine: 32,
    imageMaxWidth: 384,
    qrModuleSize: 6,
    barcodeHeight: 72
  };
}

// src/ticket/viewModel.ts
var DEFAULT_TICKET_STRUCTURE = {
  showSubtotal: true,
  showEmisionDate: true,
  showEmisionTime: true,
  showLegend: true,
  showStorePhone: true,
  showCustomerPoints: false,
  showDeliveryDate: true,
  showDeliveryTime: true,
  showPickupInfo: false,
  showServiceType: false,
  showIssuerTaxInfo: false,
  ticketLegendText: "\xA1GRACIAS POR SU COMPRA!",
  showStoreName: true,
  showCustomerName: false,
  showCustomerPhone: false,
  showCreatedByName: true,
  showOrderNotes: false,
  showTotalPieces: false,
  showTaxes: false,
  showDiscounts: true,
  showCredit: true,
  printLogoOnTicket: false,
  showStoreAddress: false,
  showPaymentLines: false,
  groupProductsBySection: false,
  showOrderCodeInTicket: false,
  orderCodeFormat: "qr",
  showCustomerPrivateNotes: false,
  orderFolioFontSizePx: 25,
  showOrderFolioAsNormalTicketLine: false,
  customerNameFontSize: "medium",
  ticketLineMarginBlockPx: 12,
  showProductAttributes: false
};
var DEFAULT_LABELS = {
  quantityLabel: "Cantidad",
  orderLabel: "Pedido",
  customerPhoneLabel: "Tel Cliente",
  frequentCustomerCardLabel: "Tarjeta frecuente No.",
  customerPointsLabel: "Puntos estimados",
  emissionDateLabel: "Fecha emisi\xF3n",
  emissionTimeLabel: "Hora emisi\xF3n",
  attendedByLabel: "Atendi\xF3",
  serviceTypeLabel: "Tipo de servicio",
  deliveryLabel: "Entrega",
  pickupLabel: "Recolecci\xF3n",
  totalPiecesLabel: "Piezas",
  subtotalLabel: "Subtotal",
  discountLabel: "Descuento",
  creditLabel: "Cr\xE9dito",
  loyaltyDiscountLabel: "Descuento por puntos",
  taxesLabel: "Impuestos",
  taxesIncludedLabel: "Impuestos incluidos",
  shippingLabel: "Servicio a domicilio",
  totalLabel: "Total",
  paidLabel: "Pago",
  amountDueLabel: "Debe",
  minimumChargeLabel: "Cobro m\xEDnimo",
  legalNameLabel: "Raz\xF3n social",
  taxSystemLabel: "R\xE9gimen fiscal",
  taxIdLabel: "RFC",
  phoneLabel: "Tel",
  whatsappLabel: "WhatsApp",
  serviceType: {
    express: "Express",
    regular: "Regular"
  },
  paymentMethodLabels: {
    cash: "Efectivo",
    card: "Tarjeta",
    transfer: "Transferencia",
    mercadopago: "Mercado Pago"
  }
};
function joinNonEmpty(parts) {
  return parts.map((part) => part == null ? "" : String(part).trim()).filter(Boolean).join(", ");
}
function formatDateValue(value, timeZone) {
  if (!value) return "";
  if (typeof value === "string") {
    const literalIsoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (literalIsoDate) {
      const [, year, month, day] = literalIsoDate;
      return `${day}/${month}/${year}`;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...timeZone ? { timeZone } : {}
  }).format(date);
}
function formatTimeValue(value, timeZone) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    ...timeZone ? { timeZone } : {}
  }).format(date).replace(/\u202f/g, " ");
}
function normalizeOrderScheduleTimeValue(value) {
  if (!value) return "";
  const plainTimeMatch = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (plainTimeMatch) {
    return `${plainTimeMatch[1]}:${plainTimeMatch[2]}`;
  }
  const isoTimeMatch = /T(\d{2}):(\d{2})/.exec(value);
  if (isoTimeMatch) {
    return `${isoTimeMatch[1]}:${isoTimeMatch[2]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
}
function buildPickupInfoLabel(pickupInfo, timeZone) {
  if (!pickupInfo?.date) return "";
  const pickupDate = formatDateValue(pickupInfo.date, timeZone);
  if (!pickupDate) return "";
  const fromTime = normalizeOrderScheduleTimeValue(pickupInfo.fromTime);
  const toTime = normalizeOrderScheduleTimeValue(pickupInfo.toTime);
  const schedule = fromTime && toTime ? `${fromTime} - ${toTime}` : fromTime;
  return [pickupDate, schedule].filter(Boolean).join(" ");
}
function getCustomerFullName(name, lastName) {
  return [name, lastName].filter(Boolean).join(" ").trim();
}
function resolveLabels(labels) {
  return {
    ...DEFAULT_LABELS,
    ...labels,
    serviceType: {
      ...DEFAULT_LABELS.serviceType,
      ...labels?.serviceType
    },
    paymentMethodLabels: {
      ...DEFAULT_LABELS.paymentMethodLabels,
      ...labels?.paymentMethodLabels
    }
  };
}
function resolveStructure(structure) {
  return {
    ...DEFAULT_TICKET_STRUCTURE,
    ...structure
  };
}
function resolveFinancialDataMode(input) {
  if (input.financialDataMode) {
    return input.financialDataMode;
  }
  return input.ticketStructure?.showFinancialData === false ? "hidden" : "full";
}
function resolveMeasurementUnits(store) {
  return store?.measurementUnits;
}
function resolveAddress(store) {
  const country = store?.country && typeof store.country === "object" ? store.country.name : void 0;
  return joinNonEmpty([
    store?.streetAddress,
    store?.zipCode,
    store?.city,
    store?.state,
    country
  ]);
}
function resolveLogoPreference(request) {
  if (typeof request.ticketStructure?.printLogoOnTicket === "boolean") {
    return request.ticketStructure.printLogoOnTicket;
  }
  return Boolean(request.store?.printingConfig?.printLogoOnTicket);
}
function paymentMethodLabel(paymentMethod, labels) {
  const normalizedMethod = (paymentMethod || "").trim().toLowerCase();
  return labels.paymentMethodLabels[normalizedMethod] || paymentMethod || "";
}
function textRow(text3, options = {}) {
  return {
    kind: "text",
    text: text3,
    ...options
  };
}
function keyValueRow(label, value, role) {
  return {
    kind: "keyValue",
    label,
    value,
    ...role ? { role } : {}
  };
}
function buildHeaderRows(params) {
  const { store, structure, labels, companyTaxInfo } = params;
  const rows = [];
  const address = resolveAddress(store);
  const hasIssuerTaxInfo = Boolean(companyTaxInfo?.legal_name) || Boolean(companyTaxInfo?.tax_system) || Boolean(structure.tax_id);
  if (structure.showStoreName && store?.name) {
    rows.push(textRow(store.name, { align: "center", role: "sectionTitle" }));
  }
  if (structure.showStoreAddress && address) {
    rows.push(textRow(address, { align: "center" }));
  }
  if (structure.showStorePhone && store?.phone) {
    rows.push(textRow(`${labels.phoneLabel}: ${store.phone}`, { align: "center" }));
  }
  if (structure.whatsappPhone) {
    rows.push(
      textRow(`${labels.whatsappLabel}: ${structure.whatsappPhone}`, {
        align: "center"
      })
    );
  }
  if (structure.showIssuerTaxInfo && hasIssuerTaxInfo) {
    if (companyTaxInfo?.legal_name) {
      rows.push(
        textRow(`${labels.legalNameLabel}: ${companyTaxInfo.legal_name}`, {
          align: "center"
        })
      );
    }
    if (companyTaxInfo?.tax_system) {
      rows.push(
        textRow(
          `${labels.taxSystemLabel}: ${companyTaxInfo.tax_system && companyTaxInfo.tax_system_label ? `${companyTaxInfo.tax_system} - ${companyTaxInfo.tax_system_label}` : companyTaxInfo.tax_system_label || companyTaxInfo.tax_system}`,
          { align: "center" }
        )
      );
    }
    if (structure.tax_id) {
      rows.push(textRow(`${labels.taxIdLabel}: ${structure.tax_id}`, { align: "center" }));
    }
  }
  return rows;
}
function buildOrderInfoRows(params) {
  const { request, structure, labels } = params;
  const { order, store, timeZone } = request;
  const rows = [];
  const customerName = getCustomerFullName(order.customer?.name, order.customer?.lastName);
  const pickupInfoLabel = buildPickupInfoLabel(order.pickupInfo, timeZone);
  const orderProducts = order.products || [];
  const orderBuyAndGetProducts = order.buyAndGetProducts || [];
  if (structure.showOrderFolioAsNormalTicketLine) {
    rows.push(keyValueRow(labels.orderLabel, buildOrderFolio(order.folioPrefix, order.sequence)));
  } else {
    rows.push(
      textRow(buildOrderFolio(order.folioPrefix, order.sequence), {
        align: "center",
        role: "folio"
      })
    );
  }
  if (structure.showCustomerName && customerName) {
    rows.push(
      textRow(customerName, {
        role: "customerName"
      })
    );
  }
  if (structure.showCustomerPhone && order.customer?.phone) {
    rows.push(keyValueRow(labels.customerPhoneLabel, order.customer.phone));
  }
  const cardNumber = order.customer?.frequentCustomerCard?.number?.trim();
  if (cardNumber) {
    rows.push(textRow(`${labels.frequentCustomerCardLabel} ${cardNumber}`));
  }
  if (request.financialDataMode !== "hidden" && structure.showCustomerPoints && Number(order.loyaltyPointsExpectedToEarn || 0) > 0) {
    rows.push(
      keyValueRow(
        labels.customerPointsLabel,
        String(order.loyaltyPointsExpectedToEarn || 0)
      )
    );
  }
  if (structure.showEmisionDate && order.createdDate) {
    rows.push(keyValueRow(labels.emissionDateLabel, formatDateValue(order.createdDate, timeZone)));
  }
  if (structure.showEmisionTime && order.createdDate) {
    rows.push(keyValueRow(labels.emissionTimeLabel, formatTimeValue(order.createdDate, timeZone)));
  }
  if (structure.showCreatedByName && order.createdBy?.name) {
    rows.push(keyValueRow(labels.attendedByLabel, order.createdBy.name));
  }
  if (structure.showServiceType) {
    rows.push(
      keyValueRow(
        labels.serviceTypeLabel,
        order.express ? labels.serviceType.express || "Express" : labels.serviceType.regular || "Regular"
      )
    );
  }
  if (structure.showDeliveryDate && order.deliveryInfo?.date) {
    const deliveryDate = formatDateValue(order.deliveryInfo.date, timeZone);
    const deliveryTime = structure.showDeliveryTime ? normalizeOrderScheduleTimeValue(order.deliveryInfo.fromTime) : "";
    rows.push(
      keyValueRow(
        labels.deliveryLabel,
        [deliveryDate, deliveryTime].filter(Boolean).join(" ")
      )
    );
  }
  if (structure.showPickupInfo && pickupInfoLabel) {
    rows.push(keyValueRow(labels.pickupLabel, pickupInfoLabel));
  }
  if (structure.showTotalPieces) {
    rows.push(
      keyValueRow(
        labels.totalPiecesLabel,
        formatPiecesBreakdown(
          getPiecesBreakdown(orderProducts, orderBuyAndGetProducts),
          { measurementUnits: store?.measurementUnits }
        )
      )
    );
  }
  if (structure.showOrderNotes && order.notes) {
    rows.push(textRow(order.notes));
  }
  if (structure.showCustomerPrivateNotes && order.customer?.privateNotes) {
    rows.push(textRow(order.customer.privateNotes));
  }
  return rows;
}
function buildProductRow(params) {
  const {
    product,
    order,
    isExpress,
    currencySymbol,
    showProductAttributes,
    measurementUnits,
    labels,
    financialDataMode
  } = params;
  const isMeasuredProduct = product.type === "weight" || product.type === "length";
  const measuredUnitLabel = getMeasuredUnitLabel(product.type, measurementUnits);
  const measuredQuantityLabel = isMeasuredProduct ? `${formatMeasuredQuantity(product.quantity)} ${measuredUnitLabel}`.trim() : "";
  const displayName = isMeasuredProduct ? `${product.name} (${measuredQuantityLabel})` : product.name;
  const displayQuantity = isMeasuredProduct ? measuredQuantityLabel : String(product.quantity);
  const attributeLines = showProductAttributes ? product.preformattedAttributeLines ?? formatProductAttributesLines(product.attributes) : void 0;
  const detailLines = product.detailLines?.filter((line) => Boolean(line?.trim()));
  if (financialDataMode === "hidden") {
    return {
      name: displayName,
      detailLines: detailLines?.length ? detailLines : void 0,
      attributeLines,
      quantityLabel: displayQuantity,
      quantityOnlyLabel: `${labels.quantityLabel}: ${displayQuantity}`
    };
  }
  const productPrice = isExpress ? Number(product.expressPrice ?? product.price ?? 0) : Number(product.price ?? 0);
  const charge = resolveEffectiveOrderLineCharge(
    order,
    { ...product, price: productPrice },
    false
  );
  const productQuantity = charge.actualQuantity;
  const billableQuantity = charge.billableQuantity;
  const totalProductLine = charge.total;
  const minimumApplied = charge.total > 0 && billableQuantity > productQuantity;
  const pricingQuantity = minimumApplied ? billableQuantity : productQuantity;
  const effectiveUnitPrice = minimumApplied ? charge.billableUnitPrice : charge.effectiveUnitPrice;
  const pricingQuantityLabel = isMeasuredProduct ? `${formatMeasuredQuantity(pricingQuantity)} ${measuredUnitLabel}`.trim() : String(pricingQuantity);
  const minimumChargeLine = minimumApplied ? `${labels.minimumChargeLabel}: ${formatMeasuredQuantity(
    product.minimumBillableQuantity || 0
  )} ${measuredUnitLabel}`.trim() : void 0;
  const resolvedDetailLines = [
    ...detailLines || [],
    minimumChargeLine
  ].filter((line) => Boolean(line));
  return {
    name: displayName,
    detailLines: resolvedDetailLines.length ? resolvedDetailLines : void 0,
    attributeLines,
    quantityLabel: pricingQuantityLabel,
    unitPriceLabel: `${formatMoneyString(effectiveUnitPrice, 2, ".", ",", currencySymbol)}${isMeasuredProduct ? `/${measuredUnitLabel}` : ""}`,
    totalLabel: formatMoneyString(totalProductLine, 2, ".", ",", currencySymbol)
  };
}
function buildProductSections(params) {
  const { request, structure, labels } = params;
  const { order, store } = request;
  const allProducts = [
    ...order.products || [],
    ...(order.buyAndGetProducts || []).map((product) => ({
      ...product,
      isBuyAndGetProduct: true
    }))
  ];
  const currencySymbol = store?.currencySymbol || "$";
  const measurementUnits = resolveMeasurementUnits(store);
  const financialDataMode = request.financialDataMode ?? "full";
  if (!structure.groupProductsBySection) {
    return [
      {
        items: allProducts.map(
          (product) => buildProductRow({
            product,
            order,
            isExpress: order.express,
            currencySymbol,
            showProductAttributes: structure.showProductAttributes,
            measurementUnits,
            labels,
            financialDataMode
          })
        )
      }
    ];
  }
  const groupedProducts = allProducts.reduce((accumulator, product, index) => {
    const sectionId = typeof product.section === "string" || !product.section ? product.section || "__default__" : product.section._id || product.section.name || "__default__";
    const sectionTitle = typeof product.section === "string" || !product.section ? product.section || void 0 : product.section.name;
    if (!accumulator[sectionId]) {
      accumulator[sectionId] = {
        title: sectionTitle,
        items: [],
        sectionOrder: index
      };
    }
    accumulator[sectionId].items.push(
      buildProductRow({
        product,
        order,
        isExpress: order.express,
        currencySymbol,
        showProductAttributes: structure.showProductAttributes,
        measurementUnits,
        labels,
        financialDataMode
      })
    );
    return accumulator;
  }, {});
  return Object.values(groupedProducts).sort((left, right) => left.sectionOrder - right.sectionOrder).map(({ sectionOrder: _sectionOrder, ...section }) => section);
}
function sumPaymentLines(paymentLines) {
  return paymentLines?.reduce(
    (total, line) => total + Number(line?.amountPaid || 0),
    0
  ) || 0;
}
function buildTotalsRows(params) {
  const { request, structure, labels } = params;
  const { order, store } = request;
  if (request.financialDataMode === "hidden") {
    return { totals: [] };
  }
  const currencySymbol = store?.currencySymbol || "$";
  const totals = [];
  const displayTotals = resolveOrderDisplayTotals(order);
  const paymentLinesTotal = sumPaymentLines(order.paymentLines);
  const amountDue = getOrderAmountDue({
    total: order.total,
    paymentLines: order.paymentLines
  });
  if (structure.showSubtotal !== false) {
    totals.push(
      keyValueRow(
        labels.subtotalLabel,
        formatMoneyString(
          Number(
            displayTotals.subtotal
          ),
          2,
          ".",
          ",",
          currencySymbol
        )
      )
    );
  }
  if (structure.showDiscounts) {
    totals.push(
      keyValueRow(
        labels.discountLabel,
        formatMoneyString(displayTotals.discount, 2, ".", ",", currencySymbol)
      )
    );
  }
  if (structure.showCredit) {
    totals.push(
      keyValueRow(
        labels.creditLabel,
        formatMoneyString(order.creditApplied, 2, ".", ",", currencySymbol)
      )
    );
  }
  if (store?.loyaltySettings?.enabled && Number(order.loyaltyDiscountAmount || 0) > 0) {
    totals.push(
      keyValueRow(
        labels.loyaltyDiscountLabel,
        formatMoneyString(order.loyaltyDiscountAmount, 2, ".", ",", currencySymbol)
      )
    );
  }
  if (structure.showTaxes) {
    const resolvedTaxesLabel = displayTotals.taxesLabel === "taxesIncluded" ? labels.taxesIncludedLabel : labels.taxesLabel;
    totals.push(
      keyValueRow(
        resolvedTaxesLabel,
        formatMoneyString(order.taxesTotal, 2, ".", ",", currencySymbol)
      )
    );
  }
  if (order.delivery || order.pickup) {
    totals.push(
      keyValueRow(
        labels.shippingLabel,
        formatMoneyString(order.shippingServiceTotal, 2, ".", ",", currencySymbol)
      )
    );
  }
  totals.push(
    keyValueRow(
      labels.totalLabel,
      formatMoneyString(order.total, 2, ".", ",", currencySymbol),
      "total"
    ),
    keyValueRow(
      labels.paidLabel,
      formatMoneyString(paymentLinesTotal, 2, ".", ",", currencySymbol),
      "total"
    ),
    keyValueRow(
      labels.amountDueLabel,
      formatMoneyString(amountDue, 2, ".", ",", currencySymbol),
      "total"
    )
  );
  if (!structure.showPaymentLines || !order.paymentLines?.length) {
    return { totals };
  }
  const payments = order.paymentLines.map(
    (paymentLine) => keyValueRow(
      paymentMethodLabel(paymentLine.paymentMethod, labels),
      formatMoneyString(paymentLine.amountPaid, 2, ".", ",", currencySymbol)
    )
  );
  return { totals, payments };
}
function buildTicketViewModel(request) {
  const resolvedRequest = {
    ...request,
    financialDataMode: resolveFinancialDataMode(request)
  };
  const structure = resolveStructure(request.ticketStructure);
  const labels = resolveLabels(request.labels);
  const legacyBlockSpacingPxHint = typeof request.ticketStructure?.ticketLineMarginBlockPx === "number" ? request.ticketStructure.ticketLineMarginBlockPx : void 0;
  const logo = resolveLogoPreference(request) ? request.logo : void 0;
  const header = buildHeaderRows({
    store: request.store,
    structure,
    labels,
    companyTaxInfo: request.companyTaxInfo
  });
  const orderInfo = buildOrderInfoRows({
    request: resolvedRequest,
    structure,
    labels
  });
  const products = buildProductSections({
    request: resolvedRequest,
    structure,
    labels
  });
  const { totals, payments } = buildTotalsRows({
    request: resolvedRequest,
    structure,
    labels
  });
  const customerId = request.order.customer?._id || request.order.customer?.id;
  const code = structure.showOrderCodeInTicket && request.order._id ? {
    type: structure.orderCodeFormat || "qr",
    value: buildQrPayload({
      orderId: request.order._id,
      customerId
    }),
    ...request.codeFallbackImage ? { fallbackAsset: request.codeFallbackImage } : {}
  } : void 0;
  const footer = structure.showLegend && structure.ticketLegendText ? [
    textRow(structure.ticketLegendText, {
      align: "center",
      role: "legend"
    })
  ] : void 0;
  return {
    logo,
    header,
    orderInfo,
    products,
    totals,
    payments,
    code,
    footer,
    presentation: {
      orderFolioSize: structure.orderFolioFontSizePreset ?? resolveOrderFolioFontPresetFromLegacyPx(structure.orderFolioFontSizePx),
      customerNameSize: structure.customerNameFontSize,
      blockSpacingPreset: structure.ticketLineSpacingPreset ?? resolveTicketLineSpacingPresetFromLegacyPx(
        structure.ticketLineMarginBlockPx
      ),
      blockSpacingPxHint: legacyBlockSpacingPxHint
    }
  };
}

// src/ticket/text.ts
var ESC_POS_CHAR_FALLBACKS = {
  "\xA1": "!",
  "\xBF": "?",
  "\u201C": '"',
  "\u201D": '"',
  "\u2018": "'",
  "\u2019": "'",
  "\u2013": "-",
  "\u2014": "-",
  "\u2022": "*",
  "\xBA": "o",
  "\xAA": "a",
  "\xDF": "ss",
  "\xC6": "AE",
  "\xE6": "ae",
  "\xD8": "O",
  "\xF8": "o",
  "\u0152": "OE",
  "\u0153": "oe"
};
function sanitizeEscPosText(value) {
  const mapped = Array.from(value || "", (char) => ESC_POS_CHAR_FALLBACKS[char] ?? char).join("");
  return mapped.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\x0a\x0d\x20-\x7e]/g, "?");
}
function wrapText(text3, width) {
  const paragraphs = sanitizeEscPosText(text3 || "").split("\n");
  const lines = [];
  for (const paragraph of paragraphs) {
    const trimmedParagraph = paragraph.trim();
    if (!trimmedParagraph) {
      lines.push("");
      continue;
    }
    const words = trimmedParagraph.split(/\s+/);
    let currentLine = "";
    for (const word of words) {
      if (!currentLine) {
        if (word.length <= width) {
          currentLine = word;
          continue;
        }
        for (let index = 0; index < word.length; index += width) {
          lines.push(word.slice(index, index + width));
        }
        currentLine = "";
        continue;
      }
      const nextCandidate = `${currentLine} ${word}`;
      if (nextCandidate.length <= width) {
        currentLine = nextCandidate;
        continue;
      }
      lines.push(currentLine);
      if (word.length <= width) {
        currentLine = word;
        continue;
      }
      for (let index = 0; index < word.length; index += width) {
        const chunk = word.slice(index, index + width);
        if (chunk.length === width) {
          lines.push(chunk);
        } else {
          currentLine = chunk;
        }
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }
  }
  return lines.length ? lines : [""];
}
function alignValue(left, right, width) {
  const cleanLeft = sanitizeEscPosText(left).trim();
  const cleanRight = sanitizeEscPosText(right).trim();
  const minimumGap = 1;
  if (cleanLeft.length + cleanRight.length + minimumGap <= width) {
    return [
      `${cleanLeft}${" ".repeat(width - cleanLeft.length - cleanRight.length)}${cleanRight}`
    ];
  }
  const rightWidth = Math.min(
    Math.max(cleanRight.length, 8),
    Math.max(8, Math.floor(width * 0.35))
  );
  const leftWidth = Math.max(8, width - rightWidth - minimumGap);
  const wrappedLeft = wrapText(cleanLeft, leftWidth);
  const lines = wrappedLeft.slice(0, -1);
  const lastLeft = wrappedLeft[wrappedLeft.length - 1] || "";
  if (lastLeft.length + cleanRight.length + minimumGap <= width) {
    lines.push(
      `${lastLeft}${" ".repeat(width - lastLeft.length - cleanRight.length)}${cleanRight}`
    );
    return lines;
  }
  lines.push(lastLeft);
  lines.push(cleanRight.padStart(width));
  return lines;
}

// src/ticket/escpos.ts
var ESC = 27;
var GS = 29;
var LF = 10;
var textEncoder = new TextEncoder();
var DEFAULT_ESC_POS_FEED_LINES_BEFORE_CUT = 4;
function encodeText(value) {
  return textEncoder.encode(sanitizeEscPosText(value));
}
function monoRowBytes(width) {
  return Math.ceil(width / 8);
}
function packBits(bits, width, height) {
  const bytesPerRow = monoRowBytes(width);
  const output = new Uint8Array(bytesPerRow * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const bit = bits[y * width + x];
      if (!bit) continue;
      const byteIndex = y * bytesPerRow + Math.floor(x / 8);
      const bitIndex = 7 - x % 8;
      output[byteIndex] |= 1 << bitIndex;
    }
  }
  return output;
}
function normalizeImage(image) {
  if (image.kind === "mono") {
    return {
      width: image.width,
      height: image.height,
      data: image.data
    };
  }
  const totalPixels = image.width * image.height;
  const monoBits = new Uint8Array(totalPixels);
  if (image.kind === "grayscale") {
    for (let index = 0; index < totalPixels; index += 1) {
      monoBits[index] = image.data[index] < 128 ? 1 : 0;
    }
  }
  if (image.kind === "rgba") {
    for (let index = 0; index < totalPixels; index += 1) {
      const offset = index * 4;
      const red = image.data[offset] ?? 255;
      const green = image.data[offset + 1] ?? 255;
      const blue = image.data[offset + 2] ?? 255;
      const alpha = image.data[offset + 3] ?? 255;
      const grayscale = (0.299 * red + 0.587 * green + 0.114 * blue) * (alpha / 255) + 255 * (1 - alpha / 255);
      monoBits[index] = grayscale < 160 ? 1 : 0;
    }
  }
  return {
    width: image.width,
    height: image.height,
    data: packBits(monoBits, image.width, image.height)
  };
}
function qrNativeCommands(content, moduleSize) {
  const data = Array.from(encodeText(content));
  const storeLength = data.length + 3;
  const pL = storeLength % 256;
  const pH = Math.floor(storeLength / 256);
  return [
    GS,
    40,
    107,
    4,
    0,
    49,
    65,
    50,
    0,
    GS,
    40,
    107,
    3,
    0,
    49,
    67,
    moduleSize,
    GS,
    40,
    107,
    3,
    0,
    49,
    69,
    49,
    GS,
    40,
    107,
    pL,
    pH,
    49,
    80,
    48,
    ...data,
    GS,
    40,
    107,
    3,
    0,
    49,
    81,
    48
  ];
}
function barcodeCode128Commands(content, height) {
  const sanitizedContent = sanitizeEscPosText(content);
  const payload = Array.from(encodeText(`{B${sanitizedContent}`));
  if (!sanitizedContent.length || payload.length > 255) {
    throw new Error("Barcode content must be 1-255 ASCII-safe characters");
  }
  return [
    GS,
    104,
    height,
    GS,
    119,
    2,
    GS,
    72,
    0,
    GS,
    107,
    73,
    payload.length,
    ...payload
  ];
}
var EscPosBuffer = class {
  constructor() {
    this.bytes = [];
  }
  pushBytes(...values) {
    this.bytes.push(...values);
  }
  pushText(value) {
    this.bytes.push(...encodeText(value));
  }
  line(value = "") {
    if (value) {
      this.pushText(value);
    }
    this.pushBytes(LF);
  }
  blankLines(count) {
    this.blankLinesWithFeedDots(count);
  }
  blankLinesWithFeedDots(count, lineFeedDots = 24) {
    if (count <= 0) return;
    this.lineSpacing(lineFeedDots);
    for (let index = 0; index < count; index += 1) {
      this.pushBytes(LF);
    }
    this.defaultLineSpacing();
  }
  align(alignment = "left") {
    const code = alignment === "center" ? 1 : alignment === "right" ? 2 : 0;
    this.pushBytes(ESC, 97, code);
  }
  bold(enabled) {
    this.pushBytes(ESC, 69, enabled ? 1 : 0);
  }
  scale(scale) {
    const value = scale.widthMultiplier - 1 << 4 | scale.heightMultiplier - 1;
    this.pushBytes(GS, 33, value);
  }
  lineSpacing(dots) {
    const safeDots = Math.max(0, Math.min(255, Math.round(dots)));
    this.pushBytes(ESC, 51, safeDots);
  }
  defaultLineSpacing() {
    this.pushBytes(ESC, 50);
  }
  resetStyle() {
    this.bold(false);
    this.scale({ widthMultiplier: 1, heightMultiplier: 1 });
    this.align("left");
    this.defaultLineSpacing();
  }
  separator(width) {
    this.align("left");
    this.line("-".repeat(width));
  }
  image(image) {
    const normalized = normalizeImage(image);
    const rowBytes = monoRowBytes(normalized.width);
    const xL = rowBytes % 256;
    const xH = Math.floor(rowBytes / 256);
    const yL = normalized.height % 256;
    const yH = Math.floor(normalized.height / 256);
    this.pushBytes(GS, 118, 48, 0, xL, xH, yL, yH);
    this.pushBytes(...normalized.data);
    this.pushBytes(LF);
  }
  nativeQr(content, moduleSize) {
    this.pushBytes(...qrNativeCommands(content, moduleSize));
    this.pushBytes(LF);
  }
  barcode(content, height) {
    this.pushBytes(...barcodeCode128Commands(content, height));
    this.pushBytes(LF);
  }
  cut() {
    this.pushBytes(GS, 86, 0);
  }
  cashDrawerPulse() {
    this.pushBytes(ESC, 112, 0, 25, 250);
  }
  toUint8Array() {
    return new Uint8Array(this.bytes);
  }
};
function rowScale(row, presentation) {
  if (row.role === "folio") {
    return resolveOrderFolioScale(presentation);
  }
  if (row.role === "customerName") {
    return mapCustomerNameFontSize(presentation.customerNameSize);
  }
  if (row.role === "total") {
    return { widthMultiplier: 1, heightMultiplier: 2 };
  }
  return { widthMultiplier: 1, heightMultiplier: 1 };
}
function rowBold(row) {
  if (row.kind === "divider") {
    return false;
  }
  return row.role === "sectionTitle" || row.role === "folio" || row.role === "total";
}
function rowAlignment(row) {
  if (row.kind === "text" && row.align) {
    return row.align;
  }
  if (row.kind === "text" && row.role === "folio") {
    return "center";
  }
  return "left";
}
function renderTextRow(buffer, row, width, presentation) {
  const spacing = resolveEscPosSpacing(presentation);
  const lines = wrapText(row.text, width);
  buffer.blankLinesWithFeedDots(
    spacing.blankLinesBefore,
    spacing.blankLineFeedDots
  );
  buffer.align(rowAlignment(row));
  buffer.bold(rowBold(row));
  buffer.scale(rowScale(row, presentation));
  for (const line of lines) {
    buffer.line(line);
  }
  buffer.resetStyle();
  buffer.blankLinesWithFeedDots(
    spacing.blankLinesAfter,
    spacing.blankLineFeedDots
  );
}
function renderKeyValueRow(buffer, row, width, presentation) {
  const spacing = resolveEscPosSpacing(presentation);
  const lines = alignValue(`${row.label}:`, row.value, width);
  buffer.blankLinesWithFeedDots(
    spacing.blankLinesBefore,
    spacing.blankLineFeedDots
  );
  buffer.align("left");
  buffer.bold(rowBold(row));
  if (row.role === "total") {
    buffer.scale({ widthMultiplier: 1, heightMultiplier: 2 });
  }
  for (const line of lines) {
    buffer.line(line);
  }
  buffer.resetStyle();
  buffer.blankLinesWithFeedDots(
    spacing.blankLinesAfter,
    spacing.blankLineFeedDots
  );
}
function renderRow(buffer, row, width, presentation) {
  if (row.kind === "divider") {
    buffer.separator(width);
    return;
  }
  if (row.kind === "text") {
    renderTextRow(buffer, row, width, presentation);
    return;
  }
  renderKeyValueRow(buffer, row, width, presentation);
}
function renderProductRow(buffer, row, width, presentation) {
  renderTextRow(
    buffer,
    {
      kind: "text",
      text: row.name,
      role: "sectionTitle"
    },
    width,
    presentation
  );
  for (const detailLine of row.detailLines || []) {
    renderTextRow(
      buffer,
      {
        kind: "text",
        text: detailLine
      },
      width,
      presentation
    );
  }
  for (const attributeLine2 of row.attributeLines || []) {
    renderTextRow(
      buffer,
      {
        kind: "text",
        text: attributeLine2
      },
      width,
      presentation
    );
  }
  if (row.quantityOnlyLabel || !row.unitPriceLabel || !row.totalLabel) {
    renderTextRow(
      buffer,
      {
        kind: "text",
        text: row.quantityOnlyLabel || row.quantityLabel
      },
      width,
      presentation
    );
    return;
  }
  renderKeyValueRow(
    buffer,
    {
      kind: "keyValue",
      label: `${row.quantityLabel} x ${row.unitPriceLabel}`,
      value: row.totalLabel
    },
    width,
    presentation
  );
}
function renderRowsSection(buffer, rows, width, presentation) {
  if (!rows?.length) return false;
  for (const row of rows) {
    renderRow(buffer, row, width, presentation);
  }
  return true;
}
function validateRenderableImage(image, maxWidth, label) {
  if (image.width > maxWidth) {
    throw new Error(
      `${label} width ${image.width}px exceeds the max width ${maxWidth}px for this paper layout`
    );
  }
}
function renderEscPosTicket(params) {
  const paperWidth = params.paperWidth ?? 58;
  const layout = getEscPosPaperLayout(paperWidth);
  const presentation = {
    orderFolioSize: params.viewModel.presentation?.orderFolioSize,
    orderFolioEmphasis: params.viewModel.presentation?.orderFolioEmphasis,
    customerNameSize: params.viewModel.presentation?.customerNameSize ?? "medium",
    blockSpacingPreset: params.viewModel.presentation?.blockSpacingPreset,
    blockDensity: params.viewModel.presentation?.blockDensity,
    blockSpacingPxHint: params.viewModel.presentation?.blockSpacingPxHint,
    ...params.presentation
  };
  const preferNativeQr = params.preferNativeQr !== false;
  const buffer = new EscPosBuffer();
  buffer.pushBytes(ESC, 64);
  if (params.viewModel.logo) {
    validateRenderableImage(params.viewModel.logo, layout.imageMaxWidth, "Logo");
    buffer.align("center");
    buffer.image(params.viewModel.logo);
    buffer.resetStyle();
  }
  const hasHeader = renderRowsSection(
    buffer,
    params.viewModel.header,
    layout.charactersPerLine,
    presentation
  );
  if (hasHeader) {
    buffer.separator(layout.charactersPerLine);
  }
  const hasOrderInfo = renderRowsSection(
    buffer,
    params.viewModel.orderInfo,
    layout.charactersPerLine,
    presentation
  );
  if (hasOrderInfo) {
    buffer.separator(layout.charactersPerLine);
  }
  for (const section of params.viewModel.products || []) {
    if (section.title) {
      renderTextRow(
        buffer,
        {
          kind: "text",
          text: section.title,
          role: "sectionTitle"
        },
        layout.charactersPerLine,
        presentation
      );
    }
    for (const product of section.items) {
      renderProductRow(
        buffer,
        product,
        layout.charactersPerLine,
        presentation
      );
    }
  }
  if ((params.viewModel.products || []).length > 0) {
    buffer.separator(layout.charactersPerLine);
  }
  renderRowsSection(
    buffer,
    params.viewModel.totals,
    layout.charactersPerLine,
    presentation
  );
  renderRowsSection(
    buffer,
    params.viewModel.payments,
    layout.charactersPerLine,
    presentation
  );
  if (params.viewModel.totals && params.viewModel.totals.length > 0 || params.viewModel.payments && params.viewModel.payments.length > 0) {
    buffer.separator(layout.charactersPerLine);
  }
  if (params.viewModel.code) {
    buffer.align("center");
    if (params.viewModel.code.type === "barcode") {
      buffer.barcode(params.viewModel.code.value, layout.barcodeHeight);
    } else if (preferNativeQr) {
      buffer.nativeQr(params.viewModel.code.value, layout.qrModuleSize);
    } else if (params.viewModel.code.fallbackAsset || params.viewModel.code.fallbackImage) {
      const fallbackAsset = params.viewModel.code.fallbackAsset || params.viewModel.code.fallbackImage;
      if (fallbackAsset) {
        validateRenderableImage(fallbackAsset, layout.imageMaxWidth, "QR fallback image");
        buffer.image(fallbackAsset);
      }
    } else {
      buffer.line(params.viewModel.code.value);
    }
    buffer.resetStyle();
  }
  renderRowsSection(
    buffer,
    params.viewModel.footer,
    layout.charactersPerLine,
    presentation
  );
  if (params.includeCashDrawerPulse) {
    buffer.cashDrawerPulse();
  }
  if (params.includeCut) {
    const feedLinesBeforeCut = Math.max(
      0,
      Math.min(10, Math.round(params.feedLinesBeforeCut ?? 0))
    );
    for (let index = 0; index < feedLinesBeforeCut; index += 1) {
      buffer.line();
    }
    buffer.cut();
  }
  return buffer.toUint8Array();
}
async function generateTicketESCPOS(params) {
  const viewModel = buildTicketViewModel(params);
  return renderEscPosTicket({
    viewModel,
    paperWidth: params.paperWidth,
    includeCut: params.includeCut,
    feedLinesBeforeCut: params.feedLinesBeforeCut,
    includeCashDrawerPulse: params.includeCashDrawerPulse,
    preferNativeQr: params.preferNativeQr,
    presentation: viewModel.presentation
  });
}

// src/ticket/escposFallback.ts
var ESC2 = 27;
var GS2 = 29;
var LF2 = 10;
var CR = 13;
var HT = 9;
var textDecoder = new TextDecoder();
function clampIndex(bytes, index) {
  return Math.min(bytes.length, Math.max(index, 0));
}
function skipRasterImage(bytes, index) {
  const xL = bytes[index + 4] ?? 0;
  const xH = bytes[index + 5] ?? 0;
  const yL = bytes[index + 6] ?? 0;
  const yH = bytes[index + 7] ?? 0;
  const dataLength = (xL + xH * 256) * (yL + yH * 256);
  return clampIndex(bytes, index + 8 + dataLength);
}
function skipGsParenthesizedCommand(bytes, index) {
  const pL = bytes[index + 3] ?? 0;
  const pH = bytes[index + 4] ?? 0;
  return clampIndex(bytes, index + 5 + pL + pH * 256);
}
function skipBarcodeCommand(bytes, index) {
  const mode = bytes[index + 2];
  if (mode === 73) {
    const payloadLength = bytes[index + 3] ?? 0;
    return clampIndex(bytes, index + 4 + payloadLength);
  }
  let nextIndex = index + 3;
  while (nextIndex < bytes.length && bytes[nextIndex] !== 0) {
    nextIndex += 1;
  }
  return clampIndex(bytes, nextIndex + 1);
}
function nextEscPosCommandIndex(bytes, index) {
  const marker = bytes[index];
  const command = bytes[index + 1];
  if (marker === ESC2) {
    if (command === 64 || command === 50) return clampIndex(bytes, index + 2);
    if (command === 51 || command === 69 || command === 97) {
      return clampIndex(bytes, index + 3);
    }
    if (command === 112) return clampIndex(bytes, index + 5);
    return clampIndex(bytes, index + 2);
  }
  if (marker === GS2) {
    if (command === 33 || command === 72 || command === 86 || command === 104 || command === 119) {
      return clampIndex(bytes, index + 3);
    }
    if (command === 40) return skipGsParenthesizedCommand(bytes, index);
    if (command === 107) return skipBarcodeCommand(bytes, index);
    if (command === 118 && bytes[index + 2] === 48) return skipRasterImage(bytes, index);
    return clampIndex(bytes, index + 2);
  }
  return clampIndex(bytes, index + 1);
}
function decodeEscPosBytesToText(bytes) {
  let readableText = "";
  let textBytes = [];
  const flushTextBytes = () => {
    if (!textBytes.length) return;
    readableText += textDecoder.decode(new Uint8Array(textBytes));
    textBytes = [];
  };
  for (let index = 0; index < bytes.length; ) {
    const byte = bytes[index];
    if (byte === ESC2 || byte === GS2) {
      flushTextBytes();
      index = nextEscPosCommandIndex(bytes, index);
      continue;
    }
    if (byte === LF2) {
      flushTextBytes();
      readableText += "\n";
      index += 1;
      continue;
    }
    if (byte === CR) {
      index += 1;
      continue;
    }
    if (byte === HT || byte >= 32) {
      textBytes.push(byte);
    }
    index += 1;
  }
  flushTextBytes();
  return readableText.replace(/[^\S\n]+$/gm, "").replace(/\n{3,}/g, "\n\n").trim();
}
function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function renderEscPosFallbackHtml({
  bytes,
  text: text3,
  title = "Ticket"
}) {
  const fallbackText = text3 ?? (bytes ? decodeEscPosBytesToText(bytes) : "");
  const escapedTitle = escapeHtml(title);
  const escapedText = escapeHtml(fallbackText);
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapedTitle}</title>
    <style>
      body {
        margin: 0;
        background: #f5f5f5;
        color: #111827;
        font-family: Arial, sans-serif;
      }
      main {
        box-sizing: border-box;
        max-width: 420px;
        min-height: 100vh;
        margin: 0 auto;
        padding: 24px 16px;
        background: #ffffff;
      }
      pre {
        margin: 0;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: "Courier New", Courier, monospace;
        font-size: 13px;
        line-height: 1.35;
      }
    </style>
  </head>
  <body>
    <main>
      <pre>${escapedText}</pre>
    </main>
  </body>
</html>`;
}

// src/ticket/preview.ts
function rowAlignment2(row) {
  if (row.kind === "text" && row.align) {
    return row.align;
  }
  if (row.kind === "text" && row.role === "folio") {
    return "center";
  }
  return "left";
}
function padAligned(value, width, align) {
  if (value.length >= width) {
    return value;
  }
  if (align === "right") {
    return value.padStart(width);
  }
  if (align === "center") {
    const totalPadding = width - value.length;
    const leftPadding = Math.floor(totalPadding / 2);
    return `${" ".repeat(leftPadding)}${value}`.padEnd(width);
  }
  return value;
}
var TicketPreviewBuffer = class {
  constructor() {
    this.lines = [];
  }
  pushLine(value = "") {
    this.lines.push(value);
  }
  blankLines(count) {
    for (let index = 0; index < count; index += 1) {
      this.pushLine("");
    }
  }
  separator(width, character = "-") {
    this.pushLine(character.repeat(width));
  }
  toString() {
    const output = [...this.lines];
    while (output[0] === "") {
      output.shift();
    }
    while (output[output.length - 1] === "") {
      output.pop();
    }
    return output.join("\n");
  }
};
function renderTextRow2(buffer, row, width, presentation) {
  const spacing = resolveEscPosSpacing(presentation);
  const scale = row.role === "folio" ? resolveOrderFolioScale(presentation) : row.role === "customerName" ? mapCustomerNameFontSize(presentation.customerNameSize) : { widthMultiplier: 1, heightMultiplier: 1 };
  const wrapWidth = scale.widthMultiplier === 2 ? Math.max(8, Math.floor((width + 1) / 2)) : width;
  const lines = wrapText(row.text, wrapWidth);
  const align = rowAlignment2(row);
  buffer.blankLines(spacing.blankLinesBefore);
  for (const line of lines) {
    const emphasizedLine = scale.heightMultiplier > 1 ? line.toUpperCase() : line;
    const widenedLine = scale.widthMultiplier > 1 ? Array.from(emphasizedLine).join(" ") : emphasizedLine;
    for (let repeat = 0; repeat < scale.heightMultiplier; repeat += 1) {
      buffer.pushLine(padAligned(widenedLine, width, align));
    }
  }
  buffer.blankLines(spacing.blankLinesAfter);
}
function renderKeyValueRow2(buffer, row, width, presentation) {
  const spacing = resolveEscPosSpacing(presentation);
  const lines = alignValue(`${row.label}:`, row.value, width);
  buffer.blankLines(spacing.blankLinesBefore);
  for (const line of lines) {
    buffer.pushLine(line);
  }
  buffer.blankLines(spacing.blankLinesAfter);
}
function renderRow2(buffer, row, width, presentation) {
  if (row.kind === "divider") {
    buffer.separator(width);
    return;
  }
  if (row.kind === "text") {
    renderTextRow2(buffer, row, width, presentation);
    return;
  }
  renderKeyValueRow2(buffer, row, width, presentation);
}
function renderRowsSection2(buffer, rows, width, presentation) {
  if (!rows?.length) {
    return false;
  }
  for (const row of rows) {
    renderRow2(buffer, row, width, presentation);
  }
  return true;
}
function renderIndentedText(buffer, value, width, indent) {
  const availableWidth = Math.max(8, width - indent.length);
  const wrapped = wrapText(value, availableWidth);
  wrapped.forEach((line, index) => {
    buffer.pushLine(index === 0 ? `${indent}${line}` : `${" ".repeat(indent.length)}${line}`);
  });
}
function renderProductRow2(buffer, row, width, presentation) {
  renderTextRow2(
    buffer,
    {
      kind: "text",
      text: row.name,
      role: "sectionTitle"
    },
    width,
    presentation
  );
  for (const detailLine of row.detailLines || []) {
    renderIndentedText(buffer, sanitizeEscPosText(detailLine), width, "  ");
    buffer.blankLines(1);
  }
  for (const attributeLine2 of row.attributeLines || []) {
    renderIndentedText(buffer, sanitizeEscPosText(attributeLine2), width, "  ");
    buffer.blankLines(1);
  }
  if (row.quantityOnlyLabel || !row.unitPriceLabel || !row.totalLabel) {
    renderTextRow2(
      buffer,
      {
        kind: "text",
        text: row.quantityOnlyLabel || row.quantityLabel
      },
      width,
      presentation
    );
    return;
  }
  renderKeyValueRow2(
    buffer,
    {
      label: `${row.quantityLabel} x ${row.unitPriceLabel}`,
      value: row.totalLabel
    },
    width,
    presentation
  );
}
function renderCodePlaceholder(buffer, code, width, showDebugGuides) {
  const placeholder = code.type === "barcode" ? "[BARCODE]" : "[QR]";
  buffer.pushLine(padAligned(placeholder, width, "center"));
  if (showDebugGuides) {
    for (const line of wrapText(`value: ${code.value}`, width)) {
      buffer.pushLine(line);
    }
  }
}
function buildPreviewRuler(width) {
  return Array.from({ length: width }, (_, index) => `${(index + 1) % 10}`).join("");
}
function resolvePresentation(viewModel) {
  return {
    orderFolioSize: viewModel.presentation?.orderFolioSize,
    orderFolioEmphasis: viewModel.presentation?.orderFolioEmphasis,
    customerNameSize: viewModel.presentation?.customerNameSize ?? "medium",
    blockSpacingPreset: viewModel.presentation?.blockSpacingPreset,
    blockDensity: viewModel.presentation?.blockDensity,
    blockSpacingPxHint: viewModel.presentation?.blockSpacingPxHint
  };
}
function renderTicketPreviewText(viewModel, options = {}) {
  const paperWidth = options.paperWidth ?? 58;
  const layout = getEscPosPaperLayout(paperWidth);
  const presentation = resolvePresentation(viewModel);
  const buffer = new TicketPreviewBuffer();
  if (options.showDebugGuides) {
    buffer.pushLine(
      `[preview ${paperWidth}mm | ${layout.charactersPerLine} cols]`.padEnd(
        layout.charactersPerLine
      )
    );
    buffer.pushLine(buildPreviewRuler(layout.charactersPerLine));
    buffer.separator(layout.charactersPerLine);
  }
  if (viewModel.logo) {
    buffer.pushLine(padAligned("[LOGO]", layout.charactersPerLine, "center"));
    buffer.blankLines(1);
  }
  const hasHeader = renderRowsSection2(
    buffer,
    viewModel.header,
    layout.charactersPerLine,
    presentation
  );
  if (hasHeader) {
    buffer.separator(layout.charactersPerLine);
  }
  const hasOrderInfo = renderRowsSection2(
    buffer,
    viewModel.orderInfo,
    layout.charactersPerLine,
    presentation
  );
  if (hasOrderInfo) {
    buffer.separator(layout.charactersPerLine);
  }
  for (const [index, section] of (viewModel.products || []).entries()) {
    if (index > 0) {
      buffer.blankLines(1);
    }
    if (section.title) {
      renderTextRow2(
        buffer,
        {
          kind: "text",
          text: section.title,
          role: "sectionTitle"
        },
        layout.charactersPerLine,
        presentation
      );
    }
    for (const product of section.items) {
      renderProductRow2(buffer, product, layout.charactersPerLine, presentation);
    }
  }
  if ((viewModel.products || []).length > 0) {
    buffer.separator(layout.charactersPerLine, "=");
  }
  renderRowsSection2(buffer, viewModel.totals, layout.charactersPerLine, presentation);
  renderRowsSection2(buffer, viewModel.payments, layout.charactersPerLine, presentation);
  if (viewModel.totals && viewModel.totals.length > 0 || viewModel.payments && viewModel.payments.length > 0) {
    buffer.separator(layout.charactersPerLine, "=");
  }
  if (viewModel.code) {
    renderCodePlaceholder(
      buffer,
      viewModel.code,
      layout.charactersPerLine,
      Boolean(options.showDebugGuides)
    );
    buffer.blankLines(1);
  }
  renderRowsSection2(buffer, viewModel.footer, layout.charactersPerLine, presentation);
  return buffer.toString();
}
function generateTicketPreviewText(params, options = {}) {
  const viewModel = buildTicketViewModel(params);
  return renderTicketPreviewText(viewModel, {
    ...options,
    paperWidth: options.paperWidth ?? params.paperWidth
  });
}

// src/ticket/outsourcedOrderViewModel.ts
var DEFAULT_OUTSOURCED_ORDER_TICKET_LABELS = {
  title: "TICKET MAQUILADOR",
  orderLabel: "Pedido",
  outsourcedOrderLabel: "Maquilaci\xF3n",
  sentDateLabel: "Fecha env\xEDo",
  receivedDateLabel: "Fecha recibido",
  statusLabel: "Estatus",
  partnerLabel: "Maquilador",
  partnerPhoneLabel: "Tel maquilador",
  externalOrderIdLabel: "Folio externo",
  customerLabel: "Cliente",
  customerPhoneLabel: "Tel cliente",
  conceptsLabel: "Conceptos",
  productLabel: "Producto",
  quantityLabel: "Cantidad",
  piecesLabel: "Piezas",
  colorLabel: "Color",
  damageLabel: "Da\xF1o",
  stainLabel: "Mancha",
  brandLabel: "Marca",
  notesLabel: "Notas",
  partnerNotesLabel: "Notas para maquilador",
  internalNotesLabel: "Notas internas",
  missingProductLabel: "Producto no encontrado en la orden",
  noPricesLegend: "Sin informaci\xF3n de precios",
  statuses: {
    pending: "Pendiente",
    in_process: "En proceso",
    returned: "Regresado"
  }
};
var OUTSOURCED_FINANCIAL_GUARD = {
  showEmisionDate: false,
  showEmisionTime: false,
  showSubtotal: false,
  showCustomerPoints: false,
  showTaxes: false,
  showDiscounts: false,
  showCredit: false,
  showPaymentLines: false,
  showCustomerPrivateNotes: false,
  showCustomerName: false,
  showCustomerPhone: false,
  showCreatedByName: false,
  showDeliveryDate: false,
  showDeliveryTime: false,
  showPickupInfo: false,
  showServiceType: false,
  showIssuerTaxInfo: false,
  orderFolioFontSizePx: void 0,
  ticketLineMarginBlockPx: void 0,
  customerNameFontSize: "small",
  showOutsourcedExternalOrderId: false,
  showPartnerNotes: false,
  tax_id: "",
  tax_id_label: ""
};
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function cleanString(value) {
  if (value === void 0 || value === null) return "";
  return String(value).trim();
}
function textRow2(text3, options = {}) {
  const cleanText = cleanString(text3);
  if (!cleanText) return null;
  return {
    kind: "text",
    text: cleanText,
    ...options
  };
}
function keyValueRow2(label, value) {
  const cleanValue = cleanString(value);
  if (!cleanValue) return null;
  return {
    kind: "keyValue",
    label,
    value: cleanValue
  };
}
function compactRows(rows) {
  return rows.filter((row) => Boolean(row));
}
function resolveLabels2(labels) {
  return {
    ...DEFAULT_OUTSOURCED_ORDER_TICKET_LABELS,
    ...labels,
    statuses: {
      ...DEFAULT_OUTSOURCED_ORDER_TICKET_LABELS.statuses,
      ...labels?.statuses
    }
  };
}
function formatDateValue2(value, timeZone) {
  if (!value) return "";
  if (typeof value === "string") {
    const literalIsoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (literalIsoDate) {
      const [, year, month, day] = literalIsoDate;
      return `${day}/${month}/${year}`;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...timeZone ? { timeZone } : {}
  }).format(date);
}
function resolveOrderFolio(order) {
  if (!order) return "";
  if (typeof order === "string") return cleanString(order);
  if (order.sequence !== void 0 && order.sequence !== null && cleanString(order.sequence)) {
    return buildOrderFolio(order.folioPrefix, order.sequence);
  }
  return cleanString(order._id);
}
function resolveOutsourcedOrderFolio(outsourcedOrder) {
  if (outsourcedOrder.outsourcedOrderSequence !== void 0 && outsourcedOrder.outsourcedOrderSequence !== null) {
    return `PM-${outsourcedOrder.outsourcedOrderSequence}`;
  }
  return cleanString(outsourcedOrder._id);
}
function resolvePartnerName(partner) {
  if (!partner) return "";
  if (typeof partner === "string") return cleanString(partner);
  return cleanString(partner.name || partner._id);
}
function resolvePartnerPhone(partner) {
  if (!partner || typeof partner === "string") return "";
  return cleanString(partner.contactPhone || partner.phone);
}
function normalizeId2(value) {
  if (value === void 0 || value === null) return "";
  if (typeof value === "string") return value;
  if (isRecord(value) && typeof value.toString === "function") return value.toString();
  return String(value);
}
function productMatchesOrderProduct(product, orderProductId) {
  const targetId = normalizeId2(orderProductId);
  if (!targetId) return false;
  return [product._id, product.lineId].map(normalizeId2).some((candidateId) => candidateId === targetId);
}
function resolveProduct(outsourcedOrder) {
  if (outsourcedOrder.relatedProduct) return outsourcedOrder.relatedProduct;
  const order = outsourcedOrder.order;
  if (!order || typeof order === "string") return null;
  const products = [
    ...order.products || [],
    ...order.buyAndGetProducts || []
  ];
  return products.find(
    (product) => productMatchesOrderProduct(product, outsourcedOrder.orderProduct)
  ) || null;
}
function formatQuantity(product) {
  if (product.quantity === void 0 || product.quantity === null) return 0;
  const quantityValue = Number(product.quantity);
  return Number.isFinite(quantityValue) ? quantityValue : 0;
}
function formatPieces(product) {
  if (product.pieceCount !== void 0 && product.pieceCount !== null) {
    return formatMeasuredQuantity(product.pieceCount);
  }
  if (product.pieces === void 0 || product.pieces === null || product.pieces === "") {
    return "";
  }
  const numericPieces = Number(product.pieces);
  const numericQuantity = Number(product.quantity);
  if (Number.isFinite(numericPieces) && Number.isFinite(numericQuantity) && product.type !== "weight" && product.type !== "length") {
    return formatMeasuredQuantity(numericPieces * numericQuantity);
  }
  if (Number.isFinite(numericPieces)) {
    return formatMeasuredQuantity(numericPieces);
  }
  return cleanString(product.pieces);
}
function attributeLine(label, value) {
  const cleanValue = cleanString(value);
  if (!cleanValue) return null;
  return `${label}: ${cleanValue}`;
}
function buildAttributeLines(product, labels) {
  const rows = [];
  const pieces = formatPieces(product);
  if (pieces) {
    rows.push(`${labels.piecesLabel}: ${pieces}`);
  }
  for (const attribute of product.attributes || []) {
    rows.push(
      ...[
        attributeLine(labels.colorLabel, attribute.color),
        attributeLine(labels.damageLabel, attribute.damage),
        attributeLine(labels.stainLabel, attribute.stain),
        attributeLine(labels.brandLabel, attribute.brand),
        attributeLine(labels.notesLabel, attribute.notes)
      ].filter((line) => Boolean(line))
    );
  }
  return rows;
}
function buildProductDetailLines(outsourcedOrder, labels, ticketStructure, options = {}) {
  if (!outsourcedOrder) return [];
  const { timeZone, includeSentDate = true, includeReceivedDate = true } = options;
  const statusLabel = outsourcedOrder.outsourcedStatus ? labels.statuses[outsourcedOrder.outsourcedStatus] || outsourcedOrder.outsourcedStatus : "";
  return [
    ticketStructure.showOutsourcedOrderFolio === false ? null : attributeLine(labels.outsourcedOrderLabel, resolveOutsourcedOrderFolio(outsourcedOrder)),
    ticketStructure.showOutsourcedStatus === false ? null : attributeLine(labels.statusLabel, statusLabel),
    ticketStructure.showOutsourcedSentDate === false || !includeSentDate ? null : attributeLine(labels.sentDateLabel, formatDateValue2(outsourcedOrder.sentDate, timeZone)),
    ticketStructure.showOutsourcedReceivedDate === false || !includeReceivedDate ? null : attributeLine(labels.receivedDateLabel, formatDateValue2(outsourcedOrder.receivedDate, timeZone)),
    ticketStructure.showOutsourcedExternalOrderId === false ? null : attributeLine(labels.externalOrderIdLabel, outsourcedOrder.externalOrderId),
    ticketStructure.showPartnerNotes === false ? null : attributeLine(labels.partnerNotesLabel, outsourcedOrder.partnerNotes)
  ].filter((line) => Boolean(line));
}
function buildTicketProduct(outsourcedOrder, labels, includeOutsourcedReference, ticketStructure, options = {}) {
  const product = resolveProduct(outsourcedOrder);
  const detailLines = buildProductDetailLines(
    includeOutsourcedReference ? outsourcedOrder : void 0,
    labels,
    ticketStructure,
    options
  );
  if (!product) {
    return {
      name: labels.missingProductLabel,
      quantity: 0,
      detailLines,
      preformattedAttributeLines: buildAttributeLines(
        { name: labels.missingProductLabel, quantity: 0 },
        labels
      )
    };
  }
  return {
    name: cleanString(product.name) || labels.missingProductLabel,
    quantity: formatQuantity(product),
    type: product.type,
    pieces: typeof product.pieces === "number" ? product.pieces : void 0,
    pieceCount: product.pieceCount,
    section: product.section,
    detailLines,
    preformattedAttributeLines: buildAttributeLines(
      product,
      labels
    )
  };
}
function maybeObjectOrder(order) {
  return order && typeof order !== "string" ? order : null;
}
function resolveCustomerName(customer) {
  if (!customer) return "";
  if (typeof customer === "string") return cleanString(customer);
  return [customer.name, customer.lastName].map(cleanString).filter(Boolean).join(" ").trim();
}
function toTicketCustomer(customer) {
  if (!customer || typeof customer === "string") return void 0;
  return {
    _id: customer._id,
    id: customer.id,
    name: resolveCustomerName(customer),
    phone: customer.phone
  };
}
function normalizeStructure(ticketStructure, options, labels) {
  return {
    showLegend: true,
    ticketLegendText: labels.noPricesLegend,
    showOrderNotes: Boolean(options?.includeOrderNotes),
    showProductAttributes: true,
    orderFolioFontSizePreset: "large",
    ticketLineSpacingPreset: "medium",
    ...ticketStructure,
    ...OUTSOURCED_FINANCIAL_GUARD,
    showCustomerName: Boolean(options?.includeCustomerName)
  };
}
function buildGenericLabels(labels) {
  return {
    orderLabel: labels.orderLabel,
    customerPhoneLabel: labels.customerPhoneLabel,
    quantityLabel: labels.quantityLabel
  };
}
function buildCommonOrderInfoRows(params) {
  const {
    labels,
    ticketStructure,
    partner,
    sentDate,
    receivedDate,
    externalOrderId,
    status,
    partnerNotes,
    includePartnerPhone,
    timeZone
  } = params;
  const statusLabel = status ? labels.statuses[status] || status : "";
  const showPartnerPhone = ticketStructure.showPartnerPhone === false ? false : includePartnerPhone ?? true;
  return compactRows([
    ticketStructure.showOutsourcedStatus === false ? null : keyValueRow2(labels.statusLabel, statusLabel),
    ticketStructure.showOutsourcedSentDate === false ? null : keyValueRow2(labels.sentDateLabel, formatDateValue2(sentDate, timeZone)),
    ticketStructure.showOutsourcedReceivedDate === false ? null : keyValueRow2(labels.receivedDateLabel, formatDateValue2(receivedDate, timeZone)),
    ticketStructure.showOutsourcedExternalOrderId === false ? null : keyValueRow2(labels.externalOrderIdLabel, externalOrderId),
    keyValueRow2(labels.partnerLabel, resolvePartnerName(partner)),
    showPartnerPhone ? keyValueRow2(labels.partnerPhoneLabel, resolvePartnerPhone(partner)) : null,
    ticketStructure.showPartnerNotes === false ? null : keyValueRow2(labels.partnerNotesLabel, partnerNotes)
  ]);
}
function sortOutsourcedOrdersBySequence(outsourcedOrders) {
  return outsourcedOrders.map((outsourcedOrder, index) => ({ outsourcedOrder, index })).sort((left, right) => {
    const leftSequence = left.outsourcedOrder.outsourcedOrderSequence;
    const rightSequence = right.outsourcedOrder.outsourcedOrderSequence;
    const leftHasSequence = typeof leftSequence === "number";
    const rightHasSequence = typeof rightSequence === "number";
    if (leftHasSequence && rightHasSequence && leftSequence !== rightSequence) {
      return leftSequence - rightSequence;
    }
    if (leftHasSequence && !rightHasSequence) return -1;
    if (!leftHasSequence && rightHasSequence) return 1;
    return left.index - right.index;
  }).map(({ outsourcedOrder }) => outsourcedOrder);
}
function resolveConsolidatedOrder(consolidatedOutsourcedOrder) {
  if (consolidatedOutsourcedOrder.order) {
    return consolidatedOutsourcedOrder.order;
  }
  return consolidatedOutsourcedOrder.outsourcedOrders.find(
    (outsourcedOrder) => Boolean(outsourcedOrder.order)
  )?.order;
}
function resolveConsolidatedPartner(consolidatedOutsourcedOrder) {
  if (consolidatedOutsourcedOrder.partner !== void 0) {
    return consolidatedOutsourcedOrder.partner;
  }
  return consolidatedOutsourcedOrder.outsourcedOrders.find(
    (outsourcedOrder) => outsourcedOrder.partner !== void 0
  )?.partner;
}
function resolveSharedDate(explicitDate, outsourcedOrders, field, timeZone) {
  const datedOrders = outsourcedOrders.filter(
    (outsourcedOrder) => Boolean(outsourcedOrder[field])
  );
  if (!datedOrders.length) return explicitDate;
  const firstFormattedDate = formatDateValue2(datedOrders[0][field], timeZone);
  const allSameDate = datedOrders.length === outsourcedOrders.length && datedOrders.every(
    (outsourcedOrder) => formatDateValue2(outsourcedOrder[field], timeZone) === firstFormattedDate
  );
  return allSameDate ? datedOrders[0][field] : void 0;
}
function buildTicket(params, title) {
  const viewModel = buildTicketViewModel(params);
  viewModel.header = [
    ...viewModel.header,
    ...compactRows([
      textRow2(title, {
        align: "center",
        role: "sectionTitle"
      })
    ])
  ];
  return viewModel;
}
function buildOutsourcedOrderTicketViewModel(input) {
  const labels = resolveLabels2(input.labels);
  const { outsourcedOrder } = input;
  const options = input.options || {};
  const sourceOrder = maybeObjectOrder(outsourcedOrder.order);
  const customer = sourceOrder?.customer;
  const ticketStructure = normalizeStructure(input.ticketStructure, options, labels);
  const product = buildTicketProduct(
    outsourcedOrder,
    labels,
    false,
    ticketStructure,
    {
      timeZone: options.timeZone
    }
  );
  const orderInfoRows = compactRows([
    ticketStructure.showOutsourcedOrderFolio === false ? null : keyValueRow2(labels.outsourcedOrderLabel, resolveOutsourcedOrderFolio(outsourcedOrder)),
    ...buildCommonOrderInfoRows({
      labels,
      ticketStructure,
      partner: outsourcedOrder.partner,
      sentDate: outsourcedOrder.sentDate,
      receivedDate: outsourcedOrder.receivedDate,
      externalOrderId: outsourcedOrder.externalOrderId,
      status: outsourcedOrder.outsourcedStatus,
      partnerNotes: outsourcedOrder.partnerNotes,
      includePartnerPhone: options.includePartnerPhone,
      timeZone: options.timeZone
    })
  ]);
  const viewModel = buildTicket(
    {
      order: {
        ...sourceOrder || {},
        folioPrefix: sourceOrder?.folioPrefix,
        sequence: sourceOrder?.sequence ?? resolveOrderFolio(outsourcedOrder.order),
        customer: toTicketCustomer(customer),
        products: [product],
        buyAndGetProducts: []
      },
      store: input.store,
      ticketStructure,
      labels: buildGenericLabels(labels),
      companyTaxInfo: input.companyTaxInfo,
      logo: input.logo,
      codeFallbackImage: input.codeFallbackImage,
      timeZone: options.timeZone,
      financialDataMode: "hidden"
    },
    labels.title
  );
  viewModel.orderInfo = [
    ...viewModel.orderInfo,
    ...orderInfoRows
  ];
  return viewModel;
}
function buildConsolidatedOutsourcedOrderTicketViewModel(input) {
  const labels = resolveLabels2(input.labels);
  const { consolidatedOutsourcedOrder } = input;
  const options = input.options || {};
  const sortedOutsourcedOrders = sortOutsourcedOrdersBySequence(
    consolidatedOutsourcedOrder.outsourcedOrders || []
  );
  const resolvedOrder = resolveConsolidatedOrder(consolidatedOutsourcedOrder);
  const sourceOrder = maybeObjectOrder(resolvedOrder);
  const partner = resolveConsolidatedPartner(consolidatedOutsourcedOrder);
  const ticketStructure = normalizeStructure(input.ticketStructure, options, labels);
  const sharedSentDate = resolveSharedDate(
    consolidatedOutsourcedOrder.sentDate,
    sortedOutsourcedOrders,
    "sentDate",
    options.timeZone
  );
  const sharedReceivedDate = resolveSharedDate(
    consolidatedOutsourcedOrder.receivedDate,
    sortedOutsourcedOrders,
    "receivedDate",
    options.timeZone
  );
  const products = sortedOutsourcedOrders.map(
    (outsourcedOrder) => buildTicketProduct(
      outsourcedOrder,
      labels,
      true,
      ticketStructure,
      {
        timeZone: options.timeZone,
        includeSentDate: !sharedSentDate,
        includeReceivedDate: !sharedReceivedDate
      }
    )
  );
  const viewModel = buildTicket(
    {
      order: {
        ...sourceOrder || {},
        folioPrefix: sourceOrder?.folioPrefix,
        sequence: sourceOrder?.sequence ?? resolveOrderFolio(resolvedOrder),
        customer: toTicketCustomer(sourceOrder?.customer),
        products,
        buyAndGetProducts: []
      },
      store: input.store,
      ticketStructure,
      labels: buildGenericLabels(labels),
      companyTaxInfo: input.companyTaxInfo,
      logo: input.logo,
      codeFallbackImage: input.codeFallbackImage,
      timeZone: options.timeZone,
      financialDataMode: "hidden"
    },
    labels.title
  );
  viewModel.orderInfo = [
    ...viewModel.orderInfo,
    ...buildCommonOrderInfoRows({
      labels,
      ticketStructure,
      partner,
      sentDate: sharedSentDate,
      receivedDate: sharedReceivedDate,
      includePartnerPhone: options.includePartnerPhone,
      timeZone: options.timeZone
    })
  ];
  return viewModel;
}

// src/ticket/outsourcedOrderEscpos.ts
function buildOutsourcedOrderEscPosTicket(viewModel, options = {}) {
  return renderEscPosTicket({
    viewModel,
    paperWidth: options.paperWidth,
    includeCut: options.includeCut,
    feedLinesBeforeCut: options.feedLinesBeforeCut
  });
}

// src/ticket/cashierBoxMovementReceiptViewModel.ts
var DEFAULT_CASHIER_BOX_MOVEMENT_RECEIPT_LABELS = {
  title: "COMPROBANTE DE MOVIMIENTO DE CAJA",
  typeLabel: "Tipo",
  dateLabel: "Fecha",
  cashierBoxLabel: "Caja",
  amountLabel: "MONTO",
  conceptLabel: "Concepto",
  deliveredByLabel: "ENTREGA",
  receivedByLabel: "RECIBE",
  nameLabel: "Nombre",
  signatureLabel: "Firma",
  noConceptLabel: "Sin concepto",
  movementTypes: {
    in: "Entrada",
    out: "Salida"
  }
};
function cleanString2(value) {
  if (value === void 0 || value === null) return "";
  return String(value).trim();
}
function resolveLabels3(labels) {
  return {
    ...DEFAULT_CASHIER_BOX_MOVEMENT_RECEIPT_LABELS,
    ...labels,
    movementTypes: {
      ...DEFAULT_CASHIER_BOX_MOVEMENT_RECEIPT_LABELS.movementTypes,
      ...labels?.movementTypes
    }
  };
}
function resolveCashierBoxName(cashierBox) {
  if (!cashierBox) return "--";
  if (typeof cashierBox === "string") return cleanString2(cashierBox) || "--";
  return cleanString2(cashierBox.name) || "--";
}
function formatDateTime(value, timeZone) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const options = timeZone ? { timeZone } : void 0;
  const dateLabel = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...options
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...options
  }).format(date);
  return `${dateLabel} ${timeLabel}`;
}
function movementTypeLabel(type, labels) {
  if (type === "in" || type === "out") {
    return labels.movementTypes[type];
  }
  return cleanString2(type) || "--";
}
function keyValueRow3(label, value) {
  return {
    kind: "keyValue",
    label,
    value: cleanString2(value) || "--"
  };
}
function signatureRows(title, labels) {
  return [
    { kind: "divider" },
    { kind: "text", text: title, role: "sectionTitle", align: "center" },
    { kind: "text", text: `${labels.nameLabel}: ________________________` },
    { kind: "text", text: `${labels.signatureLabel}:  ________________________` }
  ];
}
function buildCashierBoxMovementReceiptViewModel({
  movement,
  cashierBox,
  store,
  labels: inputLabels
}) {
  const labels = resolveLabels3(inputLabels);
  const concept = cleanString2(movement.notes) || labels.noConceptLabel;
  const currencySymbol = store?.currencySymbol || "$";
  return {
    header: [
      ...cleanString2(store?.name) ? [{ kind: "text", text: cleanString2(store?.name), align: "center" }] : [],
      { kind: "text", text: labels.title, role: "sectionTitle", align: "center" }
    ],
    orderInfo: [
      keyValueRow3(labels.typeLabel, movementTypeLabel(movement.type, labels)),
      keyValueRow3(labels.dateLabel, formatDateTime(movement.date, store?.timezone)),
      keyValueRow3(labels.cashierBoxLabel, resolveCashierBoxName(cashierBox)),
      {
        kind: "text",
        text: `${labels.conceptLabel}: ${concept}`
      }
    ],
    products: [],
    totals: [
      {
        kind: "text",
        text: `${labels.amountLabel}: ${formatMoneyString(movement.amount, 2, ".", ",", currencySymbol)}`,
        role: "total",
        align: "center"
      }
    ],
    footer: [
      ...signatureRows(labels.deliveredByLabel, labels),
      ...signatureRows(labels.receivedByLabel, labels)
    ],
    presentation: {
      blockSpacingPreset: "small",
      customerNameSize: "small"
    }
  };
}

// src/ticket/cashierBoxMovementReceiptEscpos.ts
function buildCashierBoxMovementReceiptEscPos(viewModel, options = {}) {
  return renderEscPosTicket({
    viewModel,
    paperWidth: options.paperWidth,
    includeCut: options.includeCut,
    feedLinesBeforeCut: options.feedLinesBeforeCut
  });
}

// src/ticket/consolidatedOrdersViewModel.ts
var DEFAULT_CONSOLIDATED_ORDERS_TICKET_LABELS = {
  title: "TICKET CONSOLIDADO",
  ordersLabel: "Pedidos",
  storeLabel: "Sucursal",
  quantityLabel: "Cantidad",
  minimumChargeLabel: "Cargo m\xEDnimo",
  subtotalLabel: "Subtotal",
  totalLabel: "TOTAL",
  paidLabel: "PAGADO",
  dueLabel: "DEBE"
};
function finiteNumber2(value) {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
}
function cleanString3(value) {
  return value == null ? "" : String(value).trim();
}
function resolveLabels4(labels) {
  return {
    ...DEFAULT_CONSOLIDATED_ORDERS_TICKET_LABELS,
    ...labels
  };
}
function resolveOrderId(order) {
  return cleanString3(order._id || order.id);
}
function resolveFolio(order) {
  if (order.sequence !== void 0 && order.sequence !== null) {
    return buildOrderFolio(order.folioPrefix, order.sequence);
  }
  return resolveOrderId(order);
}
function timestamp(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}
function compareOptionalNumber(left, right) {
  if (left !== null && right !== null && left !== right) return left - right;
  if (left !== null && right === null) return -1;
  if (left === null && right !== null) return 1;
  return 0;
}
function compareSequence(left, right) {
  const leftText = cleanString3(left);
  const rightText = cleanString3(right);
  if (!leftText && rightText) return 1;
  if (leftText && !rightText) return -1;
  if (!leftText && !rightText) return 0;
  const leftNumber = Number(leftText);
  const rightNumber = Number(rightText);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber !== rightNumber) {
    return leftNumber - rightNumber;
  }
  return leftText.localeCompare(rightText, void 0, {
    numeric: true,
    sensitivity: "base"
  });
}
function sortConsolidatedTicketOrders(orders) {
  return orders.map((order, index) => ({ order, index })).sort((left, right) => {
    const dateComparison = compareOptionalNumber(
      timestamp(left.order.createdDate),
      timestamp(right.order.createdDate)
    );
    if (dateComparison !== 0) return dateComparison;
    const sequenceComparison = compareSequence(
      left.order.sequence,
      right.order.sequence
    );
    if (sequenceComparison !== 0) return sequenceComparison;
    const idComparison = resolveOrderId(left.order).localeCompare(
      resolveOrderId(right.order)
    );
    if (idComparison !== 0) return idComparison;
    return left.index - right.index;
  }).map(({ order }) => order);
}
function objectProducts(products) {
  return (products || []).filter(
    (product) => typeof product === "object" && product !== null
  );
}
function toTicketOrder(order) {
  return {
    _id: resolveOrderId(order),
    folioPrefix: order.folioPrefix,
    sequence: order.sequence,
    createdDate: order.createdDate,
    express: order.express,
    orderType: order.orderType,
    returnItemsChargeMode: order.returnItemsChargeMode,
    total: finiteNumber2(order.total),
    paymentLines: (order.paymentLines || []).filter(
      (line) => Boolean(line) && isApprovedPaymentLine(line)
    ),
    products: objectProducts(order.products),
    buyAndGetProducts: objectProducts(order.buyAndGetProducts)
  };
}
function textRow3(text3, options = {}) {
  const normalizedText = cleanString3(text3);
  if (!normalizedText) return null;
  return {
    kind: "text",
    text: normalizedText,
    ...options
  };
}
function moneyRow(label, value, currencySymbol, role) {
  return {
    kind: "keyValue",
    label,
    value: formatMoneyString(value, 2, ".", ",", currencySymbol),
    ...role ? { role } : {}
  };
}
function compactRows2(rows) {
  return rows.filter((row) => Boolean(row));
}
function buildConsolidatedOrdersTicketViewModel(input) {
  const labels = resolveLabels4(input.labels);
  const sortedOrders = sortConsolidatedTicketOrders(input.orders || []);
  const currencySymbol = input.store?.currencySymbol || "$";
  const folios = sortedOrders.map(resolveFolio).filter(Boolean);
  let totalCents = 0;
  let paidCents = 0;
  let dueCents = 0;
  const orderBlocks = sortedOrders.map((order) => {
    const total = finiteNumber2(order.total);
    const paymentLines = (order.paymentLines || []).filter(
      (line) => Boolean(line) && isApprovedPaymentLine(line)
    );
    const paid = paymentLines.reduce(
      (sum, paymentLine) => sum + finiteNumber2(paymentLine.amountPaid),
      0
    );
    const due = getOrderAmountDue({ total, paymentLines });
    const orderViewModel = buildTicketViewModel({
      order: toTicketOrder(order),
      store: input.store,
      labels: {
        quantityLabel: labels.quantityLabel,
        minimumChargeLabel: labels.minimumChargeLabel
      },
      ticketStructure: {
        groupProductsBySection: false,
        showProductAttributes: false
      },
      timeZone: input.timeZone,
      financialDataMode: "full"
    });
    const folio = resolveFolio(order);
    totalCents += toCents(total);
    paidCents += toCents(paid);
    dueCents += toCents(due);
    return {
      orderId: resolveOrderId(order),
      folio,
      orderInfo: compactRows2([
        textRow3(folio, {
          align: "center",
          role: "folio"
        }),
        order.storeName ? {
          kind: "keyValue",
          label: labels.storeLabel,
          value: cleanString3(order.storeName)
        } : null
      ]),
      products: orderViewModel.products,
      subtotalRows: [
        moneyRow(labels.subtotalLabel, total, currencySymbol, "total")
      ]
    };
  });
  const summary = {
    total: fromCents(totalCents),
    paid: fromCents(paidCents),
    due: fromCents(dueCents)
  };
  return {
    header: compactRows2([
      textRow3(input.store?.name || "", {
        align: "center",
        role: "sectionTitle"
      }),
      textRow3(labels.title, {
        align: "center",
        role: "sectionTitle"
      }),
      textRow3(input.customerName || "", {
        align: "center",
        role: "customerName"
      }),
      textRow3(`${labels.ordersLabel}: ${folios.join(", ")}`)
    ]),
    orderBlocks,
    totals: [
      moneyRow(labels.totalLabel, summary.total, currencySymbol, "total"),
      moneyRow(labels.paidLabel, summary.paid, currencySymbol),
      moneyRow(labels.dueLabel, summary.due, currencySymbol, "total")
    ],
    summary,
    logo: input.logo,
    presentation: {
      orderFolioSize: "medium",
      customerNameSize: "medium",
      blockSpacingPreset: "small"
    }
  };
}

// src/ticket/consolidatedOrdersEscpos.ts
var ESC_POS_INITIALIZE_LENGTH = 2;
function emptyViewModel() {
  return {
    header: [],
    orderInfo: [],
    products: [],
    totals: []
  };
}
function withoutRepeatedInitialize(bytes) {
  return bytes.slice(ESC_POS_INITIALIZE_LENGTH);
}
function concatenateBytes(chunks) {
  const totalLength = chunks.reduce((length, chunk) => length + chunk.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}
function buildConsolidatedOrdersEscPosTicket(viewModel, options = {}) {
  const paperWidth = options.paperWidth ?? 58;
  const presentation = viewModel.presentation;
  const headerViewModel = {
    ...emptyViewModel(),
    header: viewModel.header,
    logo: viewModel.logo,
    presentation
  };
  const chunks = [
    renderEscPosTicket({
      viewModel: headerViewModel,
      paperWidth,
      presentation
    })
  ];
  for (const block of viewModel.orderBlocks) {
    const orderViewModel = {
      ...emptyViewModel(),
      orderInfo: block.orderInfo,
      products: block.products,
      totals: block.subtotalRows,
      presentation
    };
    const orderBytes = renderEscPosTicket({
      viewModel: orderViewModel,
      paperWidth,
      presentation
    });
    chunks.push(withoutRepeatedInitialize(orderBytes));
  }
  const totalsViewModel = {
    ...emptyViewModel(),
    totals: viewModel.totals,
    presentation
  };
  const totalsBytes = renderEscPosTicket({
    viewModel: totalsViewModel,
    paperWidth,
    presentation,
    includeCut: options.includeCut,
    feedLinesBeforeCut: options.feedLinesBeforeCut
  });
  chunks.push(withoutRepeatedInitialize(totalsBytes));
  return concatenateBytes(chunks);
}

// src/ticket/cashupReportViewModel.ts
function asNumber2(value) {
  return Number(value ?? 0);
}
function getEntityName(entity) {
  return entity && typeof entity === "object" && "name" in entity ? String(entity.name || "") : "";
}
function getCustomerFullName2(name, lastName) {
  return `${name || ""} ${lastName || ""}`.trim();
}
function ticketValue(value) {
  return value === void 0 || value === "" ? "--" : String(value);
}
function formatCashupFieldValue(value, formatMoney) {
  return typeof value === "number" ? formatMoney(value) : value;
}
function textRow4(text3, options = {}) {
  return {
    kind: "text",
    text: text3,
    ...options
  };
}
function keyValueRow4(label, value, options = {}) {
  return {
    kind: "keyValue",
    label: ticketValue(label),
    value: ticketValue(value),
    ...options
  };
}
function dividerRow() {
  return { kind: "divider" };
}
function appendSection(rows, title, sectionRows) {
  rows.push(
    textRow4(title.toUpperCase(), {
      role: "sectionTitle",
      align: "center"
    })
  );
  rows.push(...sectionRows);
  rows.push(dividerRow());
}
function buildBasicInfoFields({
  reportData,
  storeCanUseMP,
  t,
  formatMoney,
  formatDate
}) {
  const reportSummary = buildCashUpReportDisplaySummary(reportData);
  const fields = [
    {
      label: t("CashupsPage:cashUpReport.date"),
      value: formatDate(reportData.createdDate, "date")
    },
    {
      label: t("CashupsPage:cashUpReport.startingCash"),
      value: formatMoney(reportData.startingCash)
    },
    {
      label: t("CashupsPage:cashUpReport.hour"),
      value: formatDate(reportData.createdDate, "time")
    },
    {
      label: t("CashupsPage:cashUpReport.user"),
      value: getEntityName(reportData.createdBy)
    },
    {
      label: t("CashupsPage:cashUpReport.calculated"),
      value: formatMoney(reportSummary.calculatedTotal)
    },
    {
      label: t("CashupsPage:cashUpReport.cashierBox"),
      value: getEntityName(reportData.cashierBox)
    },
    {
      label: t("CashupsPage:cashUpReport.difference"),
      value: formatMoney(reportSummary.difference)
    },
    {
      label: t("CashupsPage:cashUpReport.reportNumber"),
      value: reportData.reportNumber
    },
    {
      label: t("CashupsPage:cashUpReport.withdrawal"),
      value: formatMoney(reportData.cashToBank)
    },
    {
      label: t("CashupsPage:cashUpReport.notes"),
      value: reportData.notes || ""
    },
    {
      label: t("FieldName:leftInDrawer"),
      value: formatMoney(reportData.leftInDrawer)
    }
  ];
  if (storeCanUseMP) {
    fields.push({
      label: t("CashupsPage:cashUpReport.counted"),
      value: formatMoney(reportSummary.declaredTotal)
    });
  }
  return fields;
}
function buildSalesDataFields({
  reportData,
  storeCanUseMP,
  t,
  formatMoney
}) {
  const fields = [
    {
      label: t("CashupsPage:cashUpReport.paidCash"),
      value: formatMoney(reportData.cashFromOrders)
    },
    {
      label: t("CashupsPage:cashUpReport.paidCard"),
      value: formatMoney(reportData.cardFromOrders)
    },
    {
      label: t("CashupsPage:cashUpReport.paidTransfer"),
      value: formatMoney(asNumber2(reportData.transferFromOrders))
    },
    {
      label: t("CashupsPage:cashUpReport.paidTotal"),
      value: formatMoney(
        reportData.cashFromOrders + reportData.cardFromOrders + asNumber2(reportData.transferFromOrders) + asNumber2(reportData.mercadoPagoFromOrders)
      )
    }
  ];
  if (storeCanUseMP) {
    fields.splice(3, 0, {
      label: t("CashupsPage:cashUpReport.paidMercadoPago"),
      value: formatMoney(asNumber2(reportData.mercadoPagoFromOrders))
    });
  }
  return fields;
}
function buildCashierBoxMovementFields({
  reportData,
  t,
  formatMoney
}) {
  return [
    {
      label: t("CashupsPage:cashUpReport.ins"),
      value: formatMoney(
        reportData.cashPayIns + reportData.cardPayIns + asNumber2(reportData.transferPayIns) + asNumber2(reportData.mercadoPagoPayIns)
      )
    },
    {
      label: t("CashupsPage:cashUpReport.outs"),
      value: formatMoney(
        reportData.cashPayOuts + reportData.cardPayOuts + asNumber2(reportData.transferPayOuts) + asNumber2(reportData.mercadoPagoPayOuts)
      )
    }
  ];
}
function buildGeneralMovementFields({
  reportData,
  t,
  formatMoney
}) {
  const totalInOrders = asNumber2(reportData.cardFromOrders) + +asNumber2(reportData.cashFromOrders).toFixed(2) + +asNumber2(reportData.transferFromOrders).toFixed(2) + +asNumber2(reportData.mercadoPagoFromOrders).toFixed(2);
  return [
    {
      label: t("CashupsPage:cashUpReport.ordersIns"),
      value: formatMoney(totalInOrders)
    },
    {
      label: t("CashupsPage:cashUpReport.cashierBoxOuts"),
      value: formatMoney(
        reportData.cashPayOuts + reportData.cardPayOuts + asNumber2(reportData.transferPayOuts) + asNumber2(reportData.mercadoPagoPayOuts)
      )
    },
    {
      label: t("CashupsPage:cashUpReport.cashierBoxIns"),
      value: formatMoney(
        reportData.cashPayIns + reportData.cardPayIns + asNumber2(reportData.transferPayIns) + asNumber2(reportData.mercadoPagoPayIns)
      )
    },
    {
      label: t("CashupsPage:cashUpReport.total"),
      value: formatMoney(
        totalInOrders + reportData.cashPayIns + reportData.cardPayIns + asNumber2(reportData.transferPayIns) + asNumber2(reportData.mercadoPagoPayIns)
      )
    },
    {
      label: t("CashupsPage:cashUpReport.total"),
      value: formatMoney(
        reportData.cashPayOuts + reportData.cardPayOuts + asNumber2(reportData.transferPayOuts) + asNumber2(reportData.mercadoPagoPayOuts)
      )
    }
  ];
}
function buildPaymentTypeRows({
  reportData,
  storeCanUseMP,
  t,
  formatMoney
}) {
  const reportSummary = buildCashUpReportDisplaySummary(reportData);
  return reportSummary.paymentTypeRows.filter((row) => row.key !== "mercadopago" || storeCanUseMP).flatMap((row) => [
    textRow4(t(`CashupsPage:cashUpReport.${row.key}`), {
      role: "sectionTitle"
    }),
    keyValueRow4(
      t("CashupsPage:cashUpReport.calculated"),
      formatMoney(row.calculated)
    ),
    keyValueRow4(
      t("CashupsPage:cashUpReport.counted"),
      formatCashupFieldValue(row.counted, formatMoney)
    ),
    keyValueRow4(
      t("CashupsPage:cashUpReport.difference"),
      formatCashupFieldValue(row.difference, formatMoney)
    )
  ]);
}
function resolveOrder(line) {
  return line.order && typeof line.order === "object" ? line.order : null;
}
function resolveMovement(line) {
  return line.movement && typeof line.movement === "object" ? line.movement : null;
}
function buildDetailedRows({
  reportData,
  t,
  formatMoney,
  formatDate
}) {
  const rows = [];
  (reportData.lines || []).forEach((line, index) => {
    const order = resolveOrder(line);
    const movement = resolveMovement(line);
    const paymentLine = order?.paymentLines?.find(
      (item) => String(item._id) === String(line.orderPaymentLineId)
    );
    const type = order ? buildOrderFolio(order.folioPrefix, order.sequence ?? "") : movement ? t("CashupsPage:cashUpReport.movement") : "";
    const date = order ? formatDate(paymentLine?.paymentDate, "date") : formatDate(movement?.date, "date");
    const notes = movement?.notes || "";
    const customer = order ? getCustomerFullName2(order.customerName, order.customerLastName) : "";
    rows.push(
      textRow4(`${index + 1}) ${ticketValue(type)}`, {
        role: "sectionTitle"
      }),
      keyValueRow4(t("CashupsPage:cashUpReport.customer"), customer),
      keyValueRow4(
        t("CashupsPage:cashUpReport.method"),
        t(`CashupsPage:cashUpReport.paymentMethods.${line.paymentMethod}`)
      ),
      keyValueRow4(
        t("CashupsPage:cashUpReport.in"),
        formatMoney(line.type === "in" ? line.amount : 0)
      ),
      keyValueRow4(
        t("CashupsPage:cashUpReport.out"),
        formatMoney(line.type === "out" ? line.amount : 0)
      ),
      keyValueRow4(t("CashupsPage:cashUpReport.date"), date),
      keyValueRow4(t("CashupsPage:cashUpReport.notes"), notes)
    );
  });
  rows.push(
    keyValueRow4(
      t("CashupsPage:cashUpReport.total"),
      formatMoney(
        reportData.cardFromOrders + reportData.cashFromOrders + asNumber2(reportData.transferFromOrders) + reportData.cashPayIns + asNumber2(reportData.transferPayIns) + reportData.cardPayIns + asNumber2(reportData.mercadoPagoPayIns) + asNumber2(reportData.mercadoPagoFromOrders)
      ),
      { role: "total" }
    ),
    keyValueRow4(
      t("CashupsPage:cashUpReport.outs"),
      formatMoney(
        reportData.cashPayOuts + reportData.cardPayOuts + asNumber2(reportData.transferPayOuts) + asNumber2(reportData.mercadoPagoPayOuts)
      ),
      { role: "total" }
    )
  );
  return rows;
}
function buildCollectedOrdersRows(reportData, formatMoney, formatDate) {
  const summaries = reportData.collectedOrdersSummary || [];
  const rows = [];
  const periodFromLabel = formatDate(reportData.periodFrom, "dateTime");
  const periodToLabel = formatDate(reportData.periodTo, "dateTime");
  if (periodFromLabel && periodToLabel) {
    rows.push(textRow4(`Periodo: ${periodFromLabel} - ${periodToLabel}`));
  }
  if (!summaries.length) {
    rows.push(textRow4("Sin servicios concluidos en el periodo"));
  }
  const totals = summaries.reduce(
    (acc, summary) => {
      const balance = Math.max(
        asNumber2(summary.total) - asNumber2(summary.amountPaid),
        0
      );
      return {
        total: acc.total + asNumber2(summary.total),
        paidInPeriod: acc.paidInPeriod + asNumber2(summary.paidInPeriod),
        balance: acc.balance + balance
      };
    },
    { total: 0, paidInPeriod: 0, balance: 0 }
  );
  summaries.forEach((summary, index) => {
    const balance = Math.max(
      asNumber2(summary.total) - asNumber2(summary.amountPaid),
      0
    );
    const folio = buildOrderFolio(
      summary.folioPrefix,
      summary.sequence ?? ""
    );
    const customer = getCustomerFullName2(
      summary.customerName,
      summary.customerLastName
    );
    rows.push(
      textRow4(`${index + 1}) ${ticketValue(folio)}`, {
        role: "sectionTitle"
      }),
      keyValueRow4("Folio", folio),
      keyValueRow4("Cliente", customer),
      keyValueRow4(
        "Entrega",
        formatDate(summary.collectedDateTime, "dateTime")
      ),
      keyValueRow4("Total", formatMoney(asNumber2(summary.total))),
      keyValueRow4("Pagado", formatMoney(asNumber2(summary.amountPaid))),
      keyValueRow4("Saldo", formatMoney(balance)),
      keyValueRow4(
        "Pagado en periodo",
        formatMoney(asNumber2(summary.paidInPeriod))
      )
    );
  });
  rows.push(
    textRow4(
      `Total servicios: ${summaries.length}${summaries.length > 0 ? ` | Total: ${formatMoney(totals.total)} | Pagado en periodo: ${formatMoney(
        totals.paidInPeriod
      )} | Saldo total: ${formatMoney(totals.balance)}` : ""}`,
      { role: "sectionTitle" }
    )
  );
  return rows;
}
function fieldsToRows(fields) {
  return fields.map((field) => keyValueRow4(field.label, field.value));
}
function buildCashupReportTicketViewModel(params) {
  const { reportData, store, t, formatMoney, formatDate } = params;
  const rows = [];
  appendSection(
    rows,
    t("CashupsPage:cashUpReport.print.basicInfoTitle"),
    fieldsToRows(buildBasicInfoFields(params))
  );
  appendSection(
    rows,
    t("CashupsPage:cashUpReport.salesDataTitle"),
    fieldsToRows(buildSalesDataFields(params))
  );
  appendSection(
    rows,
    t("CashupsPage:cashUpReport.cashierBoxMovementTitle"),
    fieldsToRows(buildCashierBoxMovementFields(params))
  );
  appendSection(
    rows,
    t("CashupsPage:cashUpReport.generalMovementsTitle"),
    fieldsToRows(buildGeneralMovementFields(params))
  );
  appendSection(
    rows,
    t("CashupsPage:cashUpReport.movementPaymentTypeTitle"),
    buildPaymentTypeRows(params)
  );
  if (params.includeDetailed) {
    appendSection(
      rows,
      t("CashupsPage:cashUpReport.detailedTitle"),
      buildDetailedRows(params)
    );
  }
  if (store?.cashUpReportConfig?.showCollectedOrdersSection) {
    appendSection(
      rows,
      "SERVICIOS CONCLUIDOS (ENTREGADOS)",
      buildCollectedOrdersRows(reportData, formatMoney, formatDate)
    );
  }
  return {
    header: [
      textRow4(t("CashupsPage:cashUpReport.title").toUpperCase(), {
        role: "sectionTitle",
        align: "center"
      })
    ],
    orderInfo: rows,
    products: [],
    totals: [],
    payments: [],
    footer: [textRow4(""), textRow4("")],
    presentation: {
      blockSpacingPreset: "small",
      customerNameSize: "medium"
    }
  };
}

// src/ticket/cashupReportEscpos.ts
function generateCashupReportEscPos(params) {
  return renderEscPosTicket({
    viewModel: buildCashupReportTicketViewModel(params),
    paperWidth: params.paperWidth,
    includeCut: params.includeCut ?? true,
    preferNativeQr: true
  });
}

// src/websiteBuilder/accessPolicy.ts
var isTrue = (value) => value === true || value instanceof Boolean && Boolean(value);
function canAccessWebsiteBuilderAdmin(input) {
  const companyCanUseWebsiteBuilder = isTrue(
    input.company?.companyServicesPermissions?.canUseWebsiteBuilder
  );
  return input.globalEnabled === true && companyCanUseWebsiteBuilder;
}
function canRenderPublicWebsite(input) {
  const result = evaluatePublicWebsiteAccessPolicy(input);
  if (result.canRender) {
    return { canRender: true, mode: result.mode };
  }
  return { canRender: false, mode: "not_found" };
}
function evaluatePublicWebsiteAccessPolicy(input) {
  if (input.globalEnabled !== true) {
    return { canRender: false, mode: "not_found", reason: "feature_disabled" };
  }
  if (!input.company) {
    return { canRender: false, mode: "not_found", reason: "company_not_found" };
  }
  const companyCanUseWebsiteBuilder = isTrue(
    input.company?.companyServicesPermissions?.canUseWebsiteBuilder
  );
  const websiteEnabled = isTrue(input.company?.websiteBuilderSettings?.websiteEnabled);
  const maintenanceModeEnabled = isTrue(
    input.company?.websiteBuilderSettings?.maintenanceModeEnabled
  );
  if (!companyCanUseWebsiteBuilder) {
    return { canRender: false, mode: "not_found", reason: "company_not_entitled" };
  }
  if (!websiteEnabled) {
    return { canRender: false, mode: "not_found", reason: "website_disabled" };
  }
  if (maintenanceModeEnabled) {
    return { canRender: true, mode: "maintenance" };
  }
  return { canRender: true, mode: "live" };
}

// src/customerDisplay/contract.ts
var CUSTOMER_DISPLAY_PROTOCOL = "washday.customer-display.v1";
var CUSTOMER_DISPLAY_NAMESPACE = "/customer-display";
var CUSTOMER_DISPLAY_EVENTS = {
  claim: "cd:claim",
  subscribe: "cd:subscribe",
  request: "cd:request",
  snapshot: "cd:snapshot",
  release: "cd:release",
  status: "cd:status",
  heartbeat: "cd:heartbeat"
};
function isCustomerDisplayId(value) {
  return typeof value === "string" && value.length === 64 && /^[a-f0-9]+$/.test(value);
}
function isCustomerDisplayPublisherKey(value) {
  return isCustomerDisplayId(value);
}
function isCustomerDisplayPublisherInstanceId(value) {
  return typeof value === "string" && value.length >= 8 && value.length <= 128 && /^[A-Za-z0-9_-]+$/.test(value);
}

// src/customerDisplay/brand.ts
var maximumNameLength = 160;
var maximumLogoUrlLength = 2048;
function isLogoUrl(value) {
  if (typeof value !== "string" || !value.length || value.length > maximumLogoUrlLength || !/^https?:\/\/[^\s]+$/.test(value)) return false;
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && Boolean(url.hostname);
  } catch {
    return false;
  }
}
function parseCustomerDisplayBrand(input) {
  try {
    if (typeof input !== "object" || input === null || Array.isArray(input)) return null;
    const brand = input;
    const { name, logoUrl, storeName } = brand;
    if (typeof name !== "string" || name.length > maximumNameLength || storeName !== void 0 && (typeof storeName !== "string" || !storeName.trim() || storeName.length > maximumNameLength) || logoUrl !== void 0 && !isLogoUrl(logoUrl)) return null;
    return {
      name: name.trim(),
      ...logoUrl === void 0 ? {} : { logoUrl },
      ...storeName === void 0 ? {} : { storeName: storeName.trim() }
    };
  } catch {
    return null;
  }
}
function createCustomerDisplayBrand(company, store) {
  const name = typeof company?.name === "string" ? company.name.trim().slice(0, maximumNameLength) : "";
  const storeName = typeof store?.name === "string" ? store.name.trim().slice(0, maximumNameLength) : "";
  const logoUrl = typeof company?.logoUrl === "string" ? company.logoUrl.trim() : void 0;
  return {
    name,
    ...isLogoUrl(logoUrl) ? { logoUrl } : {},
    ...storeName ? { storeName } : {}
  };
}
function getCustomerDisplayInitials(name) {
  const words = name.normalize("NFC").match(/[\p{L}\p{N}][\p{L}\p{M}\p{N}]*/gu) ?? [];
  return Array.from(words.slice(0, 2).map((word) => Array.from(word)[0].toUpperCase()).join("")).slice(0, 2).join("");
}

// src/customerDisplay/linePricing.ts
var quantityLabel = (value) => {
  if (typeof value !== "string" || value.length > 32 || value.match(/^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/)?.[0] !== value) return false;
  const numeric = Number(value.replace(/,/g, ""));
  return Number.isFinite(numeric) && numeric > 0 && numeric <= 1e12;
};
var price = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1e12;
function parseCustomerDisplayLinePricing(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const input = value;
  if (!quantityLabel(input.quantityLabel) || !price(input.unitPrice) || input.unit !== void 0 && !["kg", "lb", "m", "ft"].includes(input.unit) || input.minimumQuantityLabel !== void 0 && (input.unit === void 0 || !quantityLabel(input.minimumQuantityLabel)) || input.unitPriceBeforeDiscount !== void 0 && !price(input.unitPriceBeforeDiscount)) return null;
  return {
    quantityLabel: input.quantityLabel,
    unitPrice: input.unitPrice,
    ...input.unit === void 0 ? {} : { unit: input.unit },
    ...input.minimumQuantityLabel === void 0 ? {} : { minimumQuantityLabel: input.minimumQuantityLabel },
    ...input.unitPriceBeforeDiscount === void 0 ? {} : { unitPriceBeforeDiscount: input.unitPriceBeforeDiscount }
  };
}

// src/customerDisplay/snapshot.ts
var states = ["idle", "editing", "order_error", "payment_pending", "completed", "disconnected"];
var paymentStatuses = [
  "not_started",
  "processing",
  "pending",
  "paid",
  "partially_paid",
  "failed",
  "cancelled"
];
var record2 = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var text = (value, maximum) => typeof value === "string" && value.length > 0 && value.length <= maximum;
var amount = (value) => typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= 1e12;
function sanitizeSnapshot(input, requireProtocol) {
  if (!record2(input) || requireProtocol && input.protocol !== CUSTOMER_DISPLAY_PROTOCOL) return null;
  if (!isCustomerDisplayPublisherInstanceId(input.publisherInstanceId) || !Number.isSafeInteger(input.version) || input.version <= 0 || !Number.isSafeInteger(input.timestamp) || input.timestamp < 0 || typeof input.state !== "string" || !states.includes(input.state) || typeof input.currency !== "string" || !/^[A-Z]{3}$/.test(input.currency)) return null;
  const brand = parseCustomerDisplayBrand(input.brand);
  if (!brand) return null;
  if (!Array.isArray(input.lines) || input.lines.length > 250) return null;
  const lines = [];
  for (const line of input.lines) {
    if (!record2(line) || !text(line.id, 160) || !text(line.name, 240) || line.variant !== void 0 && !text(line.variant, 240) || typeof line.quantity !== "number" || !Number.isFinite(line.quantity) || line.quantity < 0 || line.quantity > 1e6 || !amount(line.amount) || typeof line.kind !== "string" || !["product", "bonus", "return"].includes(line.kind)) return null;
    const pricing = line.pricing === void 0 ? null : parseCustomerDisplayLinePricing(line.pricing);
    if (line.pricing !== void 0 && !pricing) return null;
    lines.push({
      id: line.id,
      name: line.name,
      ...line.variant === void 0 ? {} : { variant: line.variant },
      quantity: line.quantity,
      amount: line.amount,
      kind: line.kind,
      ...pricing ? { pricing } : {}
    });
  }
  const totals = input.totals;
  if (!record2(totals) || !amount(totals.subtotal) || !amount(totals.discount) || !amount(totals.taxes) || !amount(totals.shipping) || !amount(totals.total) || totals.credit !== void 0 && (!amount(totals.credit) || totals.credit < 0) || totals.loyalty !== void 0 && (!amount(totals.loyalty) || totals.loyalty < 0) || typeof totals.taxesIncluded !== "boolean") return null;
  const payment = input.payment;
  if (!record2(payment) || typeof payment.status !== "string" || !paymentStatuses.includes(payment.status) || payment.method !== void 0 && !text(payment.method, 80) || payment.amountPaid !== void 0 && (!amount(payment.amountPaid) || payment.amountPaid < 0) || payment.amountDue !== void 0 && (!amount(payment.amountDue) || payment.amountDue < 0)) return null;
  const safePayment = {
    status: payment.status,
    ...payment.method === void 0 ? {} : { method: payment.method },
    ...payment.amountPaid === void 0 ? {} : { amountPaid: payment.amountPaid },
    ...payment.amountDue === void 0 ? {} : { amountDue: payment.amountDue }
  };
  const cleared = input.state === "idle" || input.state === "disconnected";
  return {
    protocol: CUSTOMER_DISPLAY_PROTOCOL,
    publisherInstanceId: input.publisherInstanceId,
    version: input.version,
    timestamp: input.timestamp,
    state: input.state,
    brand,
    lines: cleared ? [] : lines,
    totals: cleared ? { subtotal: 0, discount: 0, taxes: 0, shipping: 0, total: 0, taxesIncluded: false } : {
      subtotal: totals.subtotal,
      discount: totals.discount,
      taxes: totals.taxes,
      shipping: totals.shipping,
      total: totals.total,
      taxesIncluded: totals.taxesIncluded,
      ...totals.credit === void 0 ? {} : { credit: totals.credit },
      ...totals.loyalty === void 0 ? {} : { loyalty: totals.loyalty }
    },
    currency: input.currency,
    payment: cleared ? { status: "not_started" } : safePayment
  };
}
function createCustomerDisplaySnapshot(input) {
  try {
    return sanitizeSnapshot(input, false);
  } catch {
    return null;
  }
}
function parseCustomerDisplaySnapshot(input) {
  try {
    return sanitizeSnapshot(input, true);
  } catch {
    return null;
  }
}

// src/customerDisplay/ordering.ts
function createCustomerDisplayOrderState() {
  return {
    snapshot: null,
    authorizedPublisherInstanceId: null,
    lastPublisherInstanceId: null,
    lastVersion: 0,
    retiredPublisherInstanceIds: []
  };
}
function authorizeCustomerDisplayPublisher(state, publisherInstanceId) {
  if (publisherInstanceId !== null && (!isCustomerDisplayPublisherInstanceId(publisherInstanceId) || state.retiredPublisherInstanceIds.includes(publisherInstanceId))) return state;
  if (state.authorizedPublisherInstanceId === publisherInstanceId) return state;
  if (publisherInstanceId === null) return { ...state, snapshot: null, authorizedPublisherInstanceId: null };
  const changed = state.lastPublisherInstanceId !== publisherInstanceId;
  return {
    ...state,
    snapshot: null,
    authorizedPublisherInstanceId: publisherInstanceId,
    lastPublisherInstanceId: publisherInstanceId,
    lastVersion: changed ? 0 : state.lastVersion,
    retiredPublisherInstanceIds: changed && state.lastPublisherInstanceId ? [...state.retiredPublisherInstanceIds, state.lastPublisherInstanceId] : state.retiredPublisherInstanceIds
  };
}
function acceptCustomerDisplaySnapshot(state, input) {
  const snapshot = parseCustomerDisplaySnapshot(input);
  if (!snapshot || snapshot.publisherInstanceId !== state.authorizedPublisherInstanceId || snapshot.version < state.lastVersion || snapshot.version === state.lastVersion && state.snapshot !== null) return state;
  return { ...state, snapshot, lastVersion: snapshot.version };
}

// src/customerDisplay/order.ts
function resolveCustomerDisplayPayment(input) {
  const approved = (input.paymentLines ?? []).filter((line) => typeof line === "object" && line !== null && isApprovedPaymentLine(line) && typeof line.amountPaid === "number" && Number.isFinite(line.amountPaid) && line.amountPaid >= 0);
  const paidCents = approved.reduce((sum, line) => sum + toCents(line.amountPaid ?? 0), 0);
  const amountPaid = fromCents(paidCents);
  const amountDue = fromCents(Math.max(0, toCents(input.total) - paidCents));
  const methods = Array.from(new Set(approved.filter((line) => (line.amountPaid ?? 0) > 0).map((line) => line.paymentMethod).filter((method) => Boolean(method))));
  return {
    status: input.status ?? (amountPaid > 0 ? amountDue === 0 ? "paid" : "partially_paid" : "not_started"),
    ...methods.length === 1 ? { method: methods[0] } : {},
    amountPaid,
    amountDue
  };
}
function createCustomerDisplayOrderContent(input) {
  const order = input.order;
  const credit = order.displayTotals?.creditApplied ?? order.creditApplied ?? 0;
  const loyalty = order.displayTotals?.redeemPointsApplied ?? order.redeemPointsApplied ?? order.loyaltyDiscountAmount ?? 0;
  return {
    lines: input.lines.map((line) => {
      const pricing = parseCustomerDisplayLinePricing(line.pricing);
      return {
        id: line.id,
        name: line.name,
        ...line.variant === void 0 ? {} : { variant: line.variant },
        quantity: line.quantity,
        amount: line.amount,
        kind: line.kind,
        ...pricing ? { pricing } : {}
      };
    }),
    totals: {
      subtotal: order.displayTotals?.subtotal ?? order.productTotalWithoutDiscount ?? order.subtotal ?? 0,
      discount: order.displayTotals?.discount ?? order.totalDiscountAmount ?? 0,
      ...credit > 0 ? { credit } : {},
      ...loyalty > 0 ? { loyalty } : {},
      taxes: order.taxesTotal ?? 0,
      shipping: order.shippingServiceTotal ?? 0,
      total: order.total,
      taxesIncluded: input.taxesIncluded
    },
    payment: resolveCustomerDisplayPayment({ total: order.total, paymentLines: order.paymentLines, status: input.paymentStatus })
  };
}

// src/customerDisplay/paymentResult.ts
function resolveCustomerDisplayAttemptState(status) {
  if (status === "approved") return "confirmed";
  if (status === "cancelled") return "cancelled";
  if (status === "rejected" || status === "refunded" || status === "charged_back") return "failed";
  return status === "processing" ? "processing" : "pending";
}

// src/productSurveys.ts
function getProductSurveyOptions(survey, otherLabel) {
  const options = survey.options.filter((option) => option.key !== "other");
  return survey.allowOther ? [...options, { key: "other", label: otherLabel }] : options;
}
function validateProductSurveyAnswer(survey, answer) {
  const keys = new Set(
    getProductSurveyOptions(survey, "Other").map((option) => option.key)
  );
  if (answer.selections.some((key) => !keys.has(key)))
    return "invalidSelection";
  if (answer.selections.includes("other")) {
    if (!answer.otherText?.trim()) return "otherRequired";
    if (answer.otherText.trim().length > 1e3) return "otherTooLong";
  }
  if ((answer.comment?.trim().length ?? 0) > 2e3) return "commentTooLong";
  if (!answer.selections.length && !answer.comment?.trim()) return "required";
  return null;
}
var PRODUCT_SURVEY_DEFINITION_FIELDS = [
  "title",
  "description",
  "question",
  "questionHint",
  "options",
  "allowOther",
  "otherLabel",
  "otherTextPrompt",
  "otherTextPlaceholder",
  "freeTextPrompt",
  "freeTextPlaceholder",
  "audience",
  "startsAt",
  "endsAt"
];
function parseProductSurveyDefinition(input) {
  const fail = (field, code = "invalid") => ({ ok: false, error: { field, code } });
  if (!input || typeof input !== "object" || Array.isArray(input))
    return fail("definition");
  const value = input;
  const textLimits = {
    title: 200,
    description: 500,
    question: 500,
    questionHint: 500,
    otherLabel: 300,
    otherTextPrompt: 500,
    otherTextPlaceholder: 500,
    freeTextPrompt: 500,
    freeTextPlaceholder: 500
  };
  const allowed = [
    ...Object.keys(textLimits),
    "options",
    "allowOther",
    "audience",
    "startsAt",
    "endsAt"
  ];
  for (const key of Object.keys(value))
    if (!allowed.includes(key)) return fail(key, "unknown");
  const normalized = {};
  for (const [key, max] of Object.entries(textLimits)) {
    const required = key === "title" || key === "question";
    if (value[key] === void 0 && !required) continue;
    if (typeof value[key] !== "string")
      return fail(
        key,
        required && value[key] === void 0 ? "required" : "invalid"
      );
    const text3 = value[key].trim();
    if (!text3 && required) return fail(key, "required");
    if (text3.length > max) return fail(key, "tooLong");
    if (text3) normalized[key] = text3;
  }
  if (typeof value.allowOther !== "boolean") return fail("allowOther");
  if (value.audience !== "company_admins" && value.audience !== "all_users")
    return fail("audience");
  if (!Array.isArray(value.options) || value.options.length < 1 || value.options.length > 30)
    return fail("options");
  const options = [];
  const keys = /* @__PURE__ */ new Set();
  for (let index = 0; index < value.options.length; index++) {
    const option = value.options[index];
    const field = `options.${index}`;
    if (!option || typeof option !== "object" || Array.isArray(option))
      return fail(field);
    for (const key2 of Object.keys(option))
      if (key2 !== "key" && key2 !== "label")
        return fail(`${field}.${key2}`, "unknown");
    if (typeof option.key !== "string" || typeof option.label !== "string")
      return fail(field);
    const key = option.key.trim();
    const label = option.label.trim();
    if (!/^[a-z0-9][a-z0-9_-]*$/i.test(key) || key.length > 100 || key.toLowerCase() === "other")
      return fail(`${field}.key`);
    if (keys.has(key.toLowerCase())) return fail(`${field}.key`, "duplicate");
    if (!label || label.length > 300)
      return fail(`${field}.label`, !label ? "required" : "tooLong");
    keys.add(key.toLowerCase());
    options.push({ key, label });
  }
  const dates = {};
  for (const key of ["startsAt", "endsAt"]) {
    const raw = value[key];
    if (raw === void 0 || raw === null) {
      dates[key] = null;
      continue;
    }
    if (typeof raw !== "string") return fail(key);
    const text3 = raw.trim();
    const match = /^(\d{4}-\d{2}-\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/.exec(
      text3
    );
    const day = match && /* @__PURE__ */ new Date(`${match[1]}T00:00:00Z`);
    const date = new Date(text3);
    if (!day || !Number.isFinite(day.getTime()) || day.toISOString().slice(0, 10) !== match[1] || !Number.isFinite(date.getTime()))
      return fail(key);
    dates[key] = date.toISOString();
  }
  if (dates.startsAt && dates.endsAt && dates.startsAt > dates.endsAt)
    return fail("endsAt", "range");
  return {
    ok: true,
    value: {
      ...normalized,
      options,
      allowOther: value.allowOther,
      audience: value.audience,
      startsAt: dates.startsAt,
      endsAt: dates.endsAt
    }
  };
}

// src/label.ts
function resolveLabelSettings(input) {
  const explicit = Number(input?.labelSettingsVersion) >= 1;
  const previouslyAlwaysPrinted = (key) => !explicit || input?.[key] !== false;
  const fontSize = Number(input?.orderFolioFontSizePx);
  return {
    labelSettingsVersion: 1,
    printLogoOnTicket: input?.printLogoOnTicket === true,
    showStoreName: previouslyAlwaysPrinted("showStoreName"),
    showCustomerName: previouslyAlwaysPrinted("showCustomerName"),
    showEmisionDate: previouslyAlwaysPrinted("showEmisionDate"),
    showDeliveryDate: previouslyAlwaysPrinted("showDeliveryDate"),
    showDeliveryTime: previouslyAlwaysPrinted("showDeliveryTime"),
    showOrderCodeInTicket: previouslyAlwaysPrinted("showOrderCodeInTicket"),
    // These two fields and logo already worked before the versioned UI.
    showTotalPieces: input?.showTotalPieces === true,
    showPendingBalance: input?.showPendingBalance === true,
    showServiceName: input?.showServiceName !== false,
    showAssignedEmployee: input?.showAssignedEmployee !== false,
    showServiceQuantity: input?.showServiceQuantity === true,
    orderCodeFormat: explicit && input?.orderCodeFormat === "barcode" ? "barcode" : "qr",
    codeSize: ["xs", "s", "m", "l"].find((size) => size === input?.codeSize) ?? "s",
    orderFolioFontSizePx: Number.isFinite(fontSize) && fontSize > 0 ? Math.min(80, Math.max(10, fontSize)) : 25
  };
}
var record3 = (value) => typeof value === "object" && value !== null;
var text2 = (value) => typeof value === "string" ? value.trim() : "";
var employeeId = (value) => typeof value === "string" ? value.trim() : record3(value) ? text2(value._id) || text2(value.id) : "";
var employeeName = (value) => {
  if (!record3(value)) return "";
  return [text2(value.name) || text2(value.firstName), text2(value.lastName)].filter(Boolean).join(" ");
};
function getServiceLabelLines(order, employees = [], labels = {}) {
  const result = [];
  for (const collection of ["products", "buyAndGetProducts"]) {
    (order[collection] ?? []).forEach((value, index) => {
      if (!record3(value)) return;
      const line = value;
      const quantity = Number(line.quantity ?? 1);
      if (!Number.isFinite(quantity) || quantity <= 0 || line.status === "cancelled" || line.isActive === false || line.active === false) return;
      const id = employeeId(line.assignedEmployee);
      const resolvedName = employeeName(line.assignedEmployee) || (id ? employeeName(employees.find((employee) => employeeId(employee) === id)) : "");
      const employeeState = resolvedName ? "assigned" : id ? "unavailable" : "unassigned";
      result.push({
        key: `${collection}:${line.lineId || line.orderProductLineID || `index-${index}`}`,
        collection,
        index,
        line,
        quantity,
        name: text2(line.name) || labels.service || "Servicio",
        employeeName: resolvedName || (id ? labels.unavailable || "Empleado no disponible" : labels.unassigned || "Sin asignar"),
        employeeState
      });
    });
  }
  return result;
}
async function sendLabelBatch(items, send) {
  const sent = [];
  const failed = [];
  for (const item of items) {
    try {
      await send(item);
      sent.push(item);
    } catch (error) {
      failed.push({ item, error });
    }
  }
  return { sent, failed };
}

// src/labelSelection.ts
var record4 = (value) => typeof value === "object" && value !== null;
var sourceId = (value) => record4(value) ? value._id || value.id : value;
var canonical = (value) => {
  if (Array.isArray(value)) return value.map(canonical);
  if (!record4(value)) return value;
  return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
};
var structuralFingerprint = (lines) => JSON.stringify(lines.map(({ key, name, quantity, line }) => {
  const source = line;
  return {
    key,
    name,
    quantity,
    storeProductId: sourceId(source.storeProductId),
    productId: sourceId(source.productId),
    product: sourceId(source.product),
    sourceLineId: sourceId(source._id),
    type: source.type,
    pieces: source.pieces,
    pieceCount: source.pieceCount,
    selectedOptionId: source.selectedOptionId,
    selectedOption: canonical(source.selectedOption),
    returnSourceOrderLineId: source.returnSourceOrderLineId,
    attributes: canonical(source.attributes),
    properties: canonical(source.properties)
  };
}));
function hasServiceLabelSelectionChanged(previous, current, selectedKeys) {
  if (selectedKeys.some((key) => !current.some((line) => line.key === key))) return true;
  for (const collection of ["products", "buyAndGetProducts"]) {
    if (!selectedKeys.some((key) => key.startsWith(`${collection}:index-`))) continue;
    const before = previous.filter((line) => line.collection === collection);
    const after = current.filter((line) => line.collection === collection);
    if (structuralFingerprint(before) !== structuralFingerprint(after)) return true;
  }
  return false;
}

// src/billing/subscriptionBilling.ts
function resolveSubscriptionBillingMode(subscription, defaultPaymentMethodType) {
  if (!subscription) return "none";
  const settings = subscription.payment_settings;
  const methods = settings?.payment_method_types;
  if (subscription.collection_method === "charge_automatically") {
    const onlyCard = methods?.length === 1 && methods[0] === "card";
    const inheritedCard = !methods?.length && defaultPaymentMethodType === "card";
    return onlyCard || inheritedCard ? "automatic_card" : "automatic_other";
  }
  if (subscription.collection_method === "send_invoice") {
    const balance = settings?.payment_method_options?.customer_balance;
    return methods?.length === 1 && methods[0] === "customer_balance" && balance?.funding_type === "bank_transfer" && balance.bank_transfer?.type === "mx_bank_transfer" ? "spei" : "manual_other";
  }
  return "unknown";
}
function getSubscriptionSelfServicePolicy(subscription) {
  const billingMode = resolveSubscriptionBillingMode(subscription);
  const allowed = subscription?.collection_method !== "send_invoice";
  return {
    billingMode,
    allowed,
    reasonCode: allowed ? null : billingMode === "spei" ? "SPEI_REQUIRES_SUPPORT" : "MANUAL_BILLING_REQUIRES_SUPPORT",
    action: allowed ? null : "contact_support"
  };
}
function getSpeiMigrationEligibility(input) {
  const reasons = [];
  if (!input.billingCountryCode) reasons.push("billing_country_unknown");
  else if (input.billingCountryCode !== "MX") reasons.push("billing_country_not_mx");
  if (input.priceBookRegion !== "MX") reasons.push("price_book_not_mx");
  if (!input.hasSubscription) reasons.push("no_subscription");
  else {
    if (!input.planPriceMatches) reasons.push("plan_price_mismatch");
    if (input.subscriptionItemsSupported !== true) reasons.push("subscription_items_unsupported");
    if (input.priceBookCurrency !== "mxn" || !input.currencies.length || input.currencies.some((currency) => currency !== "mxn")) reasons.push("currency_not_mxn");
    if (input.merchantCountryCode !== "MX") reasons.push("merchant_country_not_mx");
    if (input.customerCountryCode !== "MX") reasons.push("customer_country_not_mx");
    if (input.subscriptionStatus !== "active") reasons.push("subscription_status_unsupported");
    if (input.mode === "spei") reasons.push("already_spei");
    else if (input.mode !== "automatic_card") reasons.push("collection_mode_unsupported");
    if (input.hasSchedule) reasons.push("subscription_schedule_present");
    if (input.hasLegacyCfdi) reasons.push("legacy_cfdi_subscription_present");
    if (input.openInvoiceCount > 0) reasons.push("open_invoice_present");
    if (input.hasMoreOpenInvoices) reasons.push("invoice_scan_incomplete");
    if (input.paidInvoiceVerified !== true) reasons.push("paid_invoice_not_verified");
    if (input.hasScheduledCancellation) reasons.push("cancellation_scheduled");
    if (input.hasCollectionPause) reasons.push("collection_paused");
    if (input.hasPendingUpdate) reasons.push("pending_subscription_update");
  }
  return { eligible: reasons.length === 0, reasonCode: reasons[0] ?? "eligible", reasons };
}

// src/billing/manualSubscriptionLifecycle.ts
function getSubscriptionPlanAccess(plan) {
  const hasMultiStore = plan === "standard" || plan === "premium";
  return {
    canUseDeliveryApp: hasMultiStore,
    canUseCustomersApp: hasMultiStore,
    customStoresLimit: plan === "premium" ? Infinity : plan === "standard" ? 2 : 1,
    hasMultiStore
  };
}
function resolveManualSubscriptionLifecycle(input) {
  const invoices = [...new Map(input.invoices.map((invoice) => [invoice.id, invoice])).values()];
  const alertReasons = [...new Set(invoices.flatMap((invoice) => [
    ...invoice.revoked ? ["payment_reversed"] : [],
    ...invoice.finalizationFailed ? ["invoice_finalization_failed"] : [],
    ...["void", "uncollectible"].includes(invoice.status) ? [`invoice_${invoice.status}`] : [],
    ...invoice.status === "open" && invoice.dueDate === null ? ["missing_due_date"] : []
  ]))].sort();
  const result = (status, reason, paidInvoice = null) => ({ status, reason, paidInvoice, alertReasons });
  if (input.canceled) return result("suspended", "subscription_canceled");
  if (input.evidenceIncomplete) {
    alertReasons.push("invoice_evidence_incomplete");
    return result("grace", "invoice_evidence_incomplete");
  }
  const current = invoices.filter((invoice) => invoice.periodStart <= input.now && invoice.periodEnd > input.now);
  const paid = current.filter((invoice) => invoice.status === "paid" && invoice.amountRemaining === 0 && !invoice.revoked).sort((a, b) => b.periodStart - a.periodStart || b.periodEnd - a.periodEnd || a.id.localeCompare(b.id));
  const latest = paid[0] || null;
  if (latest && paid.some((invoice) => invoice.periodStart === latest.periodStart && invoice.periodEnd === latest.periodEnd && invoice.priceId !== latest.priceId)) {
    alertReasons.push("ambiguous_paid_period");
    return result("grace", "ambiguous_paid_period");
  }
  if (current.some((invoice) => invoice.revoked)) return result("grace", "payment_reversed");
  const overdue = invoices.some((invoice) => invoice.periodStart <= input.now && (invoice.status === "open" && invoice.amountRemaining > 0 && invoice.dueDate !== null && invoice.dueDate <= input.now || invoice.status === "uncollectible"));
  if (overdue) return result("grace", "invoice_overdue", latest);
  return latest ? result("active", "paid_period", latest) : result("grace", "awaiting_payment");
}

// src/billing/subscriptionCfdi.ts
function resolveSubscriptionCfdiPaymentForm(input) {
  const held = (reason) => ({ paymentForm: null, reason });
  if (input.status !== "paid" || !Number.isSafeInteger(input.amountDue) || input.amountDue <= 0 || !Number.isSafeInteger(input.amountPaid) || input.amountPaid < input.amountDue || input.amountRemaining !== 0) return held("invoice_not_fully_paid");
  if (!input.settlements.length || input.settlements.some((payment) => !Number.isSafeInteger(payment.amount) || payment.amount <= 0) || input.settlements.reduce((sum, payment) => sum + payment.amount, 0) !== input.amountPaid) return held("payment_allocation_incomplete");
  const forms = input.settlements.map((payment) => payment.method === "mx_bank_transfer" ? "03" : payment.method === "card" && payment.cardFunding === "credit" ? "04" : payment.method === "card" && payment.cardFunding === "debit" ? "28" : null);
  if (forms.some((form) => !form)) return held("unsupported_settlement");
  if (new Set(forms).size !== 1) return held("mixed_settlement");
  return { paymentForm: forms[0], reason: "verified_settlement" };
}

// src/reports/dateRange.ts
var DEFAULT_REPORT_TIME_ZONE = "America/Mexico_City";
var DAY_MS = 864e5;
function resolveReportTimeZone(timeZone) {
  try {
    if (timeZone?.trim()) {
      new Intl.DateTimeFormat("en", { timeZone: timeZone.trim() }).format();
      return timeZone.trim();
    }
  } catch {
  }
  return DEFAULT_REPORT_TIME_ZONE;
}
function getStoreCalendarDay(timeZone, now = /* @__PURE__ */ new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: resolveReportTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(now);
  const part = (name) => parts.find((entry) => entry.type === name).value;
  return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
}
function getReportCalendarMonth(timeZone, now = /* @__PURE__ */ new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: resolveReportTimeZone(timeZone),
    year: "numeric",
    month: "2-digit"
  }).formatToParts(now);
  const year = parts.find((part) => part.type === "year").value;
  const month = parts.find((part) => part.type === "month").value;
  const lastDay = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
  return { from: `${year}-${month}-01`, to: `${year}-${month}-${lastDay}` };
}
function calendarInstant(day) {
  const value = Date.parse(`${day}T00:00:00.000Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isFinite(value) || new Date(value).toISOString().slice(0, 10) !== day) {
    throw new Error("Invalid report calendar date");
  }
  return value;
}
function startOfCalendarDay(day, timeZone) {
  const utc = calendarInstant(day);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  let low = utc - 2 * DAY_MS;
  let high = utc + 2 * DAY_MS;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    const parts = formatter.formatToParts(middle);
    const part = (name) => parts.find((entry) => entry.type === name)?.value || "";
    const localDay = `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
    if (localDay >= day) high = middle;
    else low = middle + 1;
  }
  return new Date(low).toISOString();
}
function getReportDayRange(fromDay, toDay, timeZone) {
  const timezone = resolveReportTimeZone(timeZone);
  if (!fromDay && !toDay) return { timezone };
  if (!fromDay || !toDay || calendarInstant(fromDay) > calendarInstant(toDay)) {
    throw new Error("Invalid report date range");
  }
  const nextDay = new Date(calendarInstant(toDay) + DAY_MS).toISOString().slice(0, 10);
  const fromDate = startOfCalendarDay(fromDay, timezone);
  const toDate = startOfCalendarDay(nextDay, timezone);
  if (fromDate >= toDate) throw new Error("Empty report date range");
  return { timezone, fromDate, toDate };
}

// src/workBoard.ts
function getWorkBoardDropTarget(source) {
  if (source?.status === "cleaning") return "ready";
  if (source?.status === "ready" && source.delivery === false) return "collected";
  return void 0;
}

// src/customer/frequentCustomerCard.ts
function normalizeFrequentCustomerCardNumber(number) {
  return (number ?? "").trim().toUpperCase();
}
function generateFrequentCustomerCardNumber() {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const suffix = Array.from({ length: 12 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
  return `CF-${suffix}`;
}
function getFrequentCustomerCardStatus(card, now = /* @__PURE__ */ new Date()) {
  if (!normalizeFrequentCustomerCardNumber(card?.number)) return "not_configured";
  const today = now.toISOString().slice(0, 10);
  if (card?.validFrom && new Date(card.validFrom).toISOString().slice(0, 10) > today) return "not_started";
  if (!card?.validThrough) return "valid";
  const validThrough = new Date(card.validThrough).toISOString().slice(0, 10);
  return validThrough < today ? "expired" : "valid";
}

export { CASH_ONLY_PLACEHOLDER, CUSTOMER_DISPLAY_EVENTS, CUSTOMER_DISPLAY_NAMESPACE, CUSTOMER_DISPLAY_PROTOCOL, DEFAULT_CASHIER_BOX_MOVEMENT_RECEIPT_LABELS, DEFAULT_CONSOLIDATED_ORDERS_TICKET_LABELS, DEFAULT_ESC_POS_FEED_LINES_BEFORE_CUT, DEFAULT_OUTSOURCED_ORDER_TICKET_LABELS, DEFAULT_STORE_COUNTRY_DEFAULTS, MEASURED_QUANTITY_MAX, PRODUCT_SURVEY_DEFINITION_FIELDS, STORE_DEFAULTS_BY_COUNTRY, acceptCustomerDisplaySnapshot, aggregateTaxBreakdown, allocateByWeights, allocateCents, allocateFixedDiscountAcrossLines, applyManualUnitPrice, areCFDICustomerFiscalProfilesEqual, authorizeCustomerDisplayPublisher, bpsToPercent, buildCashUpReportDisplaySummary, buildCashierBoxMovementReceiptEscPos, buildCashierBoxMovementReceiptViewModel, buildCashupReportTicketViewModel, buildConsolidatedOrdersEscPosTicket, buildConsolidatedOrdersTicketViewModel, buildConsolidatedOutsourcedOrderTicketViewModel, buildExpensePayload, buildGoogleMapsSearchUrlFromLatLng, buildInitialAssignedEmployeeMap, buildMapLocationFromLatLng, buildOrderFolio, buildOrderLineAssignmentPayload, buildOutsourcedOrderEscPosTicket, buildOutsourcedOrderTicketViewModel, buildQrPayload, buildReportFileName, buildTicketViewModel, calculateHaversineDistanceKm, calculateLineTaxBreakdown, calculateManualPriceLineTotals, calculateOrderTotal, calculateShippingPricing, canAccessWebsiteBuilderAdmin, canAdjustOrderLinePrice, canAssignOrderLineEmployees, canRenderPublicWebsite, clampMin0, clearHistoricalPromotionRoleForPricing, createCustomerDisplayBrand, createCustomerDisplayOrderContent, createCustomerDisplayOrderState, createCustomerDisplaySnapshot, decodeEscPosBytesToText, evaluatePublicWebsiteAccessPolicy, formatExpenseDateForPayload, formatMeasuredQuantity, formatMoneyString, formatPiecesBreakdown, formatProductAttributesLines, fromCents, generateCashupReportEscPos, generateFrequentCustomerCardNumber, generateTicketESCPOS, generateTicketPreviewText, getApplicableTaxesForLine, getAssignedEmployeeId, getAssignedEmployeeLabel, getBuyXGetYFulfillmentCount, getBuyXGetYQuantityForCondition, getCFDICustomerFiscalProfileFromPreview, getConfiguredStoreTaxes, getConfiguredStoreTransferTaxes, getCustomerDisplayInitials, getEmployeeDisplayName, getEscPosPaperLayout, getExpenseCashierBoxId, getExpenseCashierBoxLabel, getExpenseStoreTimeZone, getFrequentCustomerCardStatus, getGoogleMapsUrlFromMapLocation, getLatFromMapLocation, getLatLngFromMapLocation, getLngFromMapLocation, getMeasuredUnitLabel, getMissingCFDICustomerFiscalProfileFields, getOrderAmountDue, getOrderLineAssignmentId, getOrderLineAssignmentKey, getPiecesBreakdown, getProductSurveyOptions, getReferenceId, getReportCalendarMonth, getReportDayRange, getServiceLabelLines, getSpeiMigrationEligibility, getStoreCalendarDay, getStoreTaxBps, getStoreTransferTaxBps, getSubscriptionPlanAccess, getSubscriptionSelfServicePolicy, getTaxRateBpsForLine, getWorkBoardDropTarget, grossToNetCents, hasManualUnitPrice, hasOrderLineAssignmentChanges, hasServiceLabelSelectionChanged, identifyReportFile, isApprovedPaymentLine, isCustomerDisplayId, isCustomerDisplayPublisherInstanceId, isCustomerDisplayPublisherKey, isExpenseMovementLocked, isHistoricalPromotionProvenanceLine, isIso3166Alpha2CountryCode, isNoChargeReturnLine, isOrderLineAssignable, isStoreRetentionTax, isValidMapLocationCoordinates, isValueInExpenseOptionList, mapCashupCalculationDetail, mapCustomerNameFontSize, mapOrderFolioFontPreset, mapOrderFolioFontSize, mapSpacing, mapSpacingDensity, mapTicketLineMarginToEscPosSpacing, mapTicketLineSpacingPreset, matchesBuyXGetYCondition, matchesProduct, mulBps, normalizeAssignedEmployeeFilter, normalizeBuyXGetYSelectedOptionId, normalizeBuyXGetYVariantScope, normalizeCFDICustomerFiscalProfile, normalizeFrequentCustomerCardNumber, normalizeHistoricalPromotionProvenanceForPricing, normalizeMeasuredQuantity, normalizeMinimumBillableQuantity, normalizeShippingTaxPolicy, normalizeTaxBreakdownToTotal, orderDisplayTotalsFromPricingResult, parseCustomerDisplayBrand, parseCustomerDisplayLinePricing, parseCustomerDisplaySnapshot, parseDecimalInput, parseExpenseDateForCalendar, parseLinePriceAdjustment, parseManualUnitPrice, parseProductSurveyDefinition, parseScannedCode, percentToBps, percentToBpsOrZero, pickOrderDisplayTotals, renderEscPosFallbackHtml, renderEscPosTicket, renderTicketPreviewText, resolveAddressLatLng, resolveBillableQuantity, resolveBuyXGetYRewardSnapshot, resolveCustomerDisplayAttemptState, resolveCustomerDisplayCurrency, resolveCustomerDisplayPayment, resolveEffectiveOrderLineCharge, resolveEscPosSpacing, resolveFinancialDataMode, resolveLabelSettings, resolveLineDiscountTotal, resolveManualSubscriptionLifecycle, resolveOrderDisplayTotals, resolveOrderFolioFontPresetFromLegacyPx, resolveOrderFolioScale, resolveOrderLineCharge, resolvePercentageDiscountUnitPrice, resolveReportTimeZone, resolveSectionReferenceName, resolveShippingDecision, resolveStoreCountryDefaultsByIso, resolveSubscriptionBillingMode, resolveSubscriptionCfdiPaymentForm, resolveTicketLineSpacingPresetFromLegacyPx, roundDiv, sendLabelBatch, sortConsolidatedTicketOrders, toCents, validateCFDICustomerFiscalProfile, validateMeasuredQuantity, validateProductSurveyAnswer };
